import React from 'react';
import { createPortal } from 'react-dom'; // REVEAL-002 R3: veil escapes to document.body (over the nav)
import { useAppContext } from '../contexts/AppContext';
import { Sparkle, BookOpenText, PencilSimple, ChatCircle, Brain, Microphone, Shuffle, Camera } from '@phosphor-icons/react';
import { isImageFile, normalizeImageForBook, checkPhotoQuality } from '../utils/imageHelpers';
import { interviewHasSubstance } from '../utils/interviewDraft'; // F-118b: an unsent draft is substance — no destroy without a warn
import { trackEvent } from '../utils/analytics';
import { isGiftWhisperMilestone } from '../utils/giftWhisper';
import { uploadData, getUrl } from 'aws-amplify/storage';
import { createLegacyEntry } from '../graphql/mutations';
import { CELEBRATION_PROMPTS, OCCASION_TYPES } from '../celebrationPrompts';
import { ST_PROMPTS, AI_Q_CATEGORIES } from '../constants/prompts';
import { PROMPT_PACKS } from '../constants/promptPacks';
import { getAuthorVantage, resolveInterviewMode, DONE_BUTTON_LABEL, buildOpenerLine, FINISH_CARD_LINE } from '../utils/interviewEngine';
import { typedShellInsets, applyThreadScroll, autosizeComposer, composerSendDisabled, desktopSidebarPx } from '../utils/interviewChatShell';
import {
  EXIT_GUARD_TITLE, EXIT_GUARD_SUB, EXIT_GUARD_LEAVE,
  CTA_PRIMARY, CTA_SECONDARY, EXIT_GUARD_SCRIM, EXIT_GUARD_CARD,
} from '../constants/interviewExitGuard'; // F-103 universal exit guard (shared with VoiceInterview)
import {
  THIRD_PERSON_THEMES,
  getBranchType,
  getThirdPersonThemeOrder,
  getThirdPersonPrompts,
  resolveThirdPersonPromptText,
  getOccasionOpeningQuestion,
  getThemeDisplayName,
} from '../constants/thirdPersonThemes'; // TB-108 wiring
import { cleanPagesForSave } from '../utils/bookHelpers';
import { PATH_RETURN_KEY, resolvePathRoomMount, isPathVoiceCapable } from '../utils/pathHome';
import { getReviewMode } from '../utils/reviewState'; // PIPE-SAVE-1 F-3: saved-state review decision
import { formatStreamPreview } from '../utils/streamPreview'; // F-415-STREAM: shape the streaming story text

import { promptToChapterTitle } from '../utils/contentHelpers';
import { callAi, logInterviewStart } from '../utils/aiClient';
import { isRapidFireContent, parseRapidFireContent, chapterWord } from '../RemnantBookPDF'; // TB-104 Hot Seat Q&A; chapterWord (REVEAL-001 C4)
import VoiceInterview from '../components/VoiceInterview'; // TB-104 Phase 2: voice interview surface
import RevealPages from '../components/RevealPages'; // REVEAL-001 C2: the chapter through the real PDF path
import CoverThumbnail from '../components/CoverThumbnail'; // REVEAL-001 C4: real chosen-cover thumbnail for State 3
import pipHoldingBook from '../assets/pip/pip-holding-book.svg'; // glasses + book pose for the topic picker
import pipPoised from '../assets/pip/pip-poised.svg'; // standing pose for the "writing your story" generating screen

/**
 * F-415-STREAM: the streamed story card — WHITE like the printed page (founder walk feedback
 * 2026-08-10 round 2: cream-on-cream was hard to read). Shared between the generating screen and
 * the reveal's loading fallback, so the SAME card carries the teller across the step change and
 * the "Preparing your pages" flash never shows on a streamed arrival.
 */
const PipChatBubble = ({ children, extraClass = '', latest = false, thinking = false }) => (
  <div className="iv-ai-bubble" data-kind={extraClass || undefined} data-iv-latest-pip={latest ? 'true' : undefined} aria-live={thinking ? 'polite' : undefined} aria-label={thinking ? 'Pip is thinking' : undefined}>
    <img className="iv-bubble-avatar" src="/pip/pip-body.png" alt="" />
    <div className="iv-bubble-name">Pip</div>
    {children}
  </div>
);

const StreamCard = ({ preview }) => (
  <div data-testid="stream-preview" aria-live="off" style={{ maxWidth: 560, margin: '18px auto 0', background: '#fff', border: '1px solid #e4dccb', borderRadius: 4, padding: '26px 22px 30px', boxShadow: '0 2px 0 #efe6d2, 0 16px 38px rgba(60,47,30,.12)' }}>
    {preview.title && (
      <h3 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: '1.25rem', color: '#2a2520', margin: '0 0 14px', textAlign: 'center' }}>{preview.title}</h3>
    )}
    {preview.paragraphs.map((p, i) => (
      <p key={i} style={{ fontFamily: "'Lora', serif", fontSize: 16, lineHeight: 1.7, color: '#3c3428', margin: '0 0 12px', textAlign: 'left' }}>{p}</p>
    ))}
  </div>
);

const KID_QUESTIONS = [
  "What's your favorite thing to do with Mom?",
  "What does Mom do that makes you laugh?",
  "What does Mom always say?",
  "What's Mom's superpower?",
  "What's something Mom makes that you love?",
  "What does Mom do when you're sad?",
  "What do you want to tell Mom?",
  "If you could give Mom anything in the whole world, what would it be?",
];

// REVEAL-001 C5 — "Tell Pip more" feature flag. OFF for the reveal (State-0/v11) launch package: the
// reopen MECHANISM is built, Fable-approved, and founder-walk-verified, but the append QUALITY rewrites
// the whole saved chapter on each reopen (F-42: ~half the sentences reworded, +89% length, first-person
// voice drift). The door stays HIDDEN until the append-mode PIPELINE fix (PIPE-APPEND: preserve existing
// sentences verbatim, integrate only new material) lands in its own future session. Flip to true there.
// Pipeline work never gates the reveal publish; this flag is how the reveal ships without the door.
const TELL_PIP_MORE_ENABLED = false;

// Kid mode feature flag. HIDDEN 2026-07-11 — pending legal review (children's data: a child's
// name + optional audio/video recordings = COPPA-class personal information) + product review.
// The entry button is the ONLY setKidMode(true) call site, so flagging it off makes the whole
// kid flow unreachable. Existing saved kid stories still render (render-side checks the per-page
// isKidStory data field, never this flag). See BACKLOG.
const KID_MODE_ENABLED = false;

const StRecordPage = () => {
  const {
    // Navigation & mode
    setActiveTab, setActiveTabRaw, showToast, isGiftBook, activeBook, activeBookId, ensureBookExists,
    chapterQrCodes, // REVEAL-001 C2: per-story QR data URL for the in-page "scan to play" affordance
    // Stories
    stories, refreshStories, userId, client, user,
    // Book state
    stBookPages, setStBookPages, stBookTitle,
    stBookStep, setStBookStep, stBookOpen, setStBookOpen,
    stBookSubtitle, stBookAuthor, stBookDedication,
    stBookCover, stBookCoverPhotoPath,
    saveBookData,
    // Gateway & interview mode
    stShowGateway, setStShowGateway,
    stInterviewMode, setStInterviewMode,
    // Recording state
    storyRecording, storyAudioBlob, setStoryAudioBlob,
    storyAudioUrl, setStoryAudioUrl,
    storyVideoMode, setStoryVideoMode,
    storyVideoBlob, setStoryVideoBlob, storyVideoUrl, setStoryVideoUrl,
    storyLiveVideoRef,
    startStoryRecording, stopStoryRecording,
    stPostRecordStep, setStPostRecordStep,
    stAutoTranscribeTriggered, setStAutoTranscribeTriggered,
    handleStPostRecord,
    // Processed story
    stProcessed, setStProcessed,
    handleStPolishStory, handleStSaveRecorded,
    // Prompts & AI question
    stSelectedPrompt, setStSelectedPrompt,
    stStartMode, setStStartMode,
    stShowPromptPicker, setStShowPromptPicker,
    activePackId, setActivePackId,
    forcePackPicker, setForcePackPicker,
    stShuffleIdx, setStShuffleIdx,
    stAiQuestion, setStAiQuestion, stAiQuestionLoading,
    mothersAudience, setMothersAudience,
    generateAiQuestion,
    // Interview (Q&A) mode
    stInterviewStep, setStInterviewStep,
    stGenerationFailure, setStGenerationFailure, // F-334 persistent failure banner
    stStreamPreview, // F-415-STREAM: the Whole's text so far, rendered on the generating screen
    stDraftWords, // F-415-STREAM: the assembler draft's live word count (strip, phase 1)
    stInterviewInput, setStInterviewInput,
    stInterviewMessages, setStInterviewMessages,
    stInterviewLoading,
    stInterviewPolishing,
    startStInterview,
    generateStInterviewStory, handleInterviewPolish, retryPolishedGeneration,
    saveStInterviewStory,
    removeRevealedStory, // PIPE-SAVE-1 F-3: remove from the saved review/reveal
    updateSavedInterviewStory, // PIPE-SAVE-1 F-7: persist post-save edits on the reveal
    discardUnsavedDraft, // REVEAL-002 R1: State-0 Discard — delete the draft, back to New Story home
    updateStoryDraftEdits, // REVEAL-002 R1: State-0 Edit — persist edits to the draft row
    markStoryLandingPlayed, // REVEAL-002 R3: veil marks landingPlayedAt on mount
    beatName, // REVEAL-002 R3/B1: resolved first name for the beat (profile→given→none; NEVER the handle)
    reopenStoryForMore, // REVEAL-001 C5: "Tell Pip more" — forward-only reopen of a saved chapter
    stReopenStoryId, // REVEAL-001 C5 Guard 1: truthy while a reopened session is active (Finish guard)
    stSaving, // TB-118: save-in-flight — disable save buttons, show "Saving..."
    // TB-104 Stage 2a: adaptive engine
    submitFirstAnswer, sendStInterviewMsg,
    stInterviewEndRef,
    interviewFollowUpCount, interviewConversation,
    // TB-104 Stage 3 (FIX 6): engine reset setters for Back-button state cleanup
    setInterviewFollowUpCount, setInterviewAskedMoves, setInterviewConversation,
    setInterviewLastClassification, setInterviewEmptyCount,
    // TB-104 Stage B: explicit wrap boolean
    interviewIsWrapped, setInterviewIsWrapped,
    // TB-104 keep-going: budget bonus, wrap reason, keep-going handler
    interviewWrapReason, handleInterviewKeepGoing,
    interviewBudgetBonus, setInterviewBudgetBonus, // TB-134 fix 2: extend budget on typed continuation past a wrap
    // TB-104 steering: one "ask me something else" control + rephrase + write-up-so-far (Back save path)
    writeUpInterviewSoFar,
    // TB-104 Stage 2b: Hot Seat rapid-fire
    generateHotSeatStory,
    rapidAnswers, setRapidAnswers,
    rapidCurrentIdx, setRapidCurrentIdx,
    // Transcription
    transcribing, transcribeAudio,
    // Write state (reset on "Write my own story" entry — fresh start intent)
    setStWriteTitle, setStWriteBody, setStWritePhotoFile, setStWritePhotoPreview,
    // Photo quality
    photoQualityWarning, setPhotoQualityWarning,
    // Enhancement nudge
    accountSettings,
    stPhotoEnhanceSkipped, setStPhotoEnhanceSkipped,
    stEnhanceAfterSave, setStEnhanceAfterSave,
    // Memory crosslinks
    setMemoryDementiaMode, setMemoryDayQuality, setMemoryTopic,
    setMemoryTab, setMemoryStep,
    // Auto-link QR
    autoLinkRecordingToQr,
    // F-13: in-app gift sheet opener (milestone whisper)
    openGiftSheet,
    // F-24: total saved stories, for the opener graduation (<2 = full opener, >=2 = brief greeting)
    stStoryList,
    // F-80: doorway seedless "Start talking" handshake
    seedlessStart, setSeedlessStart,
    pathOpenPackId, setPathOpenPackId,
  } = useAppContext();

  // Enhancement nudge helper
  const freeEnhanceRemaining = Math.max(0, 3 - (accountSettings?.enhancementsUsed || 0));
  const showEnhanceNudge = stProcessed.pendingPhotoPreview && !stEnhanceAfterSave && !stPhotoEnhanceSkipped && freeEnhanceRemaining > 0;
  const EnhanceNudge = () => showEnhanceNudge ? (
    <div style={{
      marginTop: '12px', padding: '14px 16px', borderRadius: '12px',
      background: 'rgba(201, 168, 76, 0.08)', border: '1px solid rgba(201, 168, 76, 0.3)',
      display: 'flex', alignItems: 'flex-start', gap: '12px',
    }}>
      <span style={{ fontSize: '1.4rem' }}></span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#0f172a', marginBottom: '4px' }}>
          Enhance this photo before printing?
        </div>
        <div style={{ fontSize: '0.82rem', color: 'rgba(15,23,42,0.6)', marginBottom: '10px' }}>
          AI sharpens faces, restores faded colors, and upscales to 300 DPI for print. Takes ~30 seconds.
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setStEnhanceAfterSave(true)} style={{
            padding: '7px 16px', borderRadius: '20px', background: '#7c2d12', color: 'white',
            border: 'none', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600,
          }}>
            Enhance for Free
          </button>
          <button onClick={() => setStPhotoEnhanceSkipped(true)} style={{
            padding: '7px 16px', borderRadius: '20px', background: 'transparent',
            color: 'rgba(15,23,42,0.5)', border: '1px solid rgba(15,23,42,0.15)',
            fontSize: '0.82rem', cursor: 'pointer',
          }}>
            Keep as-is
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // ── TB-104 Phase 2: Voice interview mode ──
  // Activated when user taps "Talk it out" on the question card.
  // Renders VoiceInterview in place of the standard question view.
  // On done, the transcript's user turns feed into generateStInterviewStory.
  const [stVoiceInterviewMode, setStVoiceInterviewMode] = React.useState(false);
  // Typed "Talk to Pip instead" arms a voice turn instead of replaying Ready.
  const [voiceAutoStart, setVoiceAutoStart] = React.useState(false);

  // ── Kid Mode state ──
  const [stShowPackPicker, setStShowPackPicker] = React.useState(false);
  // Returning users who chose a pack before skip the picker and go straight to a
  // question (the "Switch packs" chip still gives on-demand access). First-timers see
  // it once. Without this the picker fired on every visit, breaking 2-tap-to-Pip.
  const [stPackChosen, setStPackChosen] = React.useState(() => {
    if (pathOpenPackId) return true;
    try { return !!localStorage.getItem('tb_packChosen'); } catch (e) { return false; }
  });
  const [returnToPath, setReturnToPath] = React.useState(() => {
    try { return sessionStorage.getItem(PATH_RETURN_KEY) === '1'; } catch (e) { return false; }
  });
  const backFromPathQuestions = () => {
    if (!returnToPath) return false;
    setReturnToPath(false);
    try { sessionStorage.removeItem(PATH_RETURN_KEY); } catch (e) { /* private mode */ }
    setActiveTab('talk');
    return true;
  };
  // "Create your story" from the Pip home forces the pack chooser, even for returning
  // writers, so a new story always starts by picking a topic pack.
  React.useEffect(() => {
    if (forcePackPicker) {
      setStShowPackPicker(true);
      if (setForcePackPicker) setForcePackPicker(false);
    }
  }, [forcePackPicker]); // eslint-disable-line react-hooks/exhaustive-deps

  // F-80: the doorway's "Start talking" -> SEEDLESS voice interview. StTalkHome set activePackId =
  // 'easy-start' + stSelectedPrompt = null via context; here we flip this page's LOCAL state to enter
  // the voice interview directly (skip the pack picker), with no chosen prompt so the VoiceInterview
  // opens on the COLD_START_OPENER and sends coldStart:true (F-62 warm-up ramp + F-54 fire). Consume
  // the flag once, same handshake as forcePackPicker. On a non-voice device VoiceInterview degrades to
  // "I'd rather type" -> seedless text (handleSwitchToText -> startStInterview(null)).
  React.useEffect(() => {
    if (seedlessStart) {
      // A Path room arms auto-start and the return flag. Just start talking does not.
      setVoiceAutoStart(false);
      setReturnToPath(false);
      try { sessionStorage.removeItem(PATH_RETURN_KEY); } catch (e) { /* private mode */ }
      setStPackChosen(true);
      setStShowPackPicker(false);
      setStVoiceInterviewMode(true);
      if (setSeedlessStart) setSeedlessStart(false);
    }
  }, [seedlessStart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Path tapped a stop. Skip the pack list and the prompt cards. Voice auto-starts
  // when the device can; otherwise the typed interview opens on the same silent opener.
  // Hot Seat still opens rapid-fire; this effect only remembers Back for that door.
  React.useEffect(() => {
    if (!pathOpenPackId) return;
    setStPackChosen(true);
    setStShowPackPicker(false);
    setReturnToPath(true);
    try { localStorage.setItem('tb_packChosen', '1'); } catch (e) { /* private mode */ }
    try { sessionStorage.setItem(PATH_RETURN_KEY, '1'); } catch (e) { /* private mode */ }
    if (pathOpenPackId !== 'hot-seat') {
      const mount = resolvePathRoomMount({
        packId: pathOpenPackId,
        selectedPrompt: stSelectedPrompt,
        voiceCapable: isPathVoiceCapable(),
      });
      if (mount.prompt && setStSelectedPrompt) setStSelectedPrompt(mount.prompt);
      if (mount.enterVoice) {
        setVoiceAutoStart(true);
        setStVoiceInterviewMode(true);
      } else if (mount.enterText) {
        startStInterview(mount.prompt);
      }
    }
    if (setPathOpenPackId) setPathOpenPackId(null);
  }, [pathOpenPackId]); // eslint-disable-line react-hooks/exhaustive-deps

  // For celebration/gift books that use PROMPT_PACKS (self-vantage only): default the active
  // pack to 'occasion' when the active book has a themed occasion pack.
  // This runs whenever the active book changes so switching between books works correctly.
  // Third-person gift books do NOT use occasion packs -- they use THIRD_PERSON_THEMES, and
  // the theme id flows through activePackId separately. Skip this effect for those books.
  React.useEffect(() => {
    if (!isGiftBook) return;
    const occ = activeBook?.occasion || '';
    if (!occ || occ === 'OTHER') return;
    if (!CELEBRATION_PROMPTS[occ === 'MOTHERS_DAY' ? 'MOTHERS_DAY' : occ]) return;
    // Skip for third-person gift books (partner, observer, etc.) -- they use theme ids, not 'occasion'.
    const rawRel = activeBook?.authorRelationship || '';
    if (getAuthorVantage(rawRel) !== 'self') return;
    // If the current pack is a standard PROMPT_PACK (not already 'occasion'), switch to 'occasion'.
    if (activePackId !== 'occasion') {
      setActivePackId('occasion');
      setStShuffleIdx(0);
    }
  }, [activeBookId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [kidMode, setKidMode] = React.useState(false);
  const [kidName, setKidName] = React.useState('');
  const [kidStep, setKidStep] = React.useState('name'); // 'name' | 'question' | 'review'
  const [kidQuestionIdx, setKidQuestionIdx] = React.useState(0);
  const [kidAnswers, setKidAnswers] = React.useState([]); // array of { question, answer }
  const [kidCurrentAnswer, setKidCurrentAnswer] = React.useState('');
  const [kidRecording, setKidRecording] = React.useState(false);
  const [kidTranscribing, setKidTranscribing] = React.useState(false);
  const [kidPolishing, setKidPolishing] = React.useState(false);
  const [kidSaving, setKidSaving] = React.useState(false);
  const [kidAudioBlobs, setKidAudioBlobs] = React.useState([]); // per-question audio blobs
  const [kidVideoBlobs, setKidVideoBlobs] = React.useState([]); // per-question video blobs
  const [kidVideoMode, setKidVideoMode] = React.useState(false);
  const [kidIncludeQr, setKidIncludeQr] = React.useState(false);
  const kidRecorderRef = React.useRef(null);
  const kidAudioChunksRef = React.useRef([]);
  const kidAudioStreamRef = React.useRef(null);
  const kidVideoRecorderRef = React.useRef(null);
  const kidVideoChunksRef = React.useRef([]);
  const kidLiveVideoRef = React.useRef(null);

  // ── TB-104 Stage B: in-conversation recording state ──
  // Separate from the global storyRecording / storyAudioBlob to avoid interfering
  // with the existing answer-step and simple-record flows.
  const [ivRecording, setIvRecording] = React.useState(false);      // is recording in-thread right now
  const [ivVideoMode, setIvVideoMode] = React.useState(false);       // voice vs video in capture bar
  const [ivTranscribing, setIvTranscribing] = React.useState(false); // waiting for Whisper
  // Keepsake: the FIRST recording per session (spec §7a-2 + red-team P2-4)
  // eslint-disable-next-line no-unused-vars
  const [ivKeepsakeAudioBlob, setIvKeepsakeAudioBlob] = React.useState(null);
  // eslint-disable-next-line no-unused-vars
  const [ivKeepsakeVideoBlob, setIvKeepsakeVideoBlob] = React.useState(null);
  const [ivKeepsakeCaptured, setIvKeepsakeCaptured] = React.useState(false); // first recording landed
  const [ivIncludeQr, setIvIncludeQr] = React.useState(true);  // default ON (spec §8, P2-4)
  const ivRecorderRef = React.useRef(null);
  const ivAudioChunksRef = React.useRef([]);
  const ivAudioStreamRef = React.useRef(null);
  const ivVideoRecorderRef = React.useRef(null);
  const ivVideoChunksRef = React.useRef([]);
  const ivLiveVideoRef = React.useRef(null);
  // Ref for conversation scroll anchor (separate from stInterviewEndRef for clarity)
  const ivConvEndRef = React.useRef(null);
  // TYPED-CHAT-SHELL-1: scroll the THREAD PANE so the newest Pip question is fully
  // readable just above the composer. Never scroll the window (that clipped the
  // first line of the question and sometimes lost the input).
  React.useEffect(() => {
    if (stInterviewStep !== 'conversation') return undefined;
    const id = setTimeout(() => {
      const thread = ivThreadRef.current;
      const latest = thread && thread.querySelector('[data-iv-latest-pip="true"]');
      applyThreadScroll(thread, latest);
    }, 50);
    return () => clearTimeout(id);
  }, [interviewConversation, stInterviewLoading, stInterviewStep, stInterviewMessages]);
  // Keep the cursor in the answer field. The textarea is disabled while Pip is
  // thinking (stInterviewLoading), which drops focus; when it re-enables nothing
  // restores it, so the user had to click back in every turn. Refocus the moment
  // Pip finishes (and on entry) so they never leave the text field.
  const ivInputRef = React.useRef(null);
  React.useEffect(() => {
    if (stInterviewStep !== 'conversation') return undefined;
    if (stInterviewLoading || ivRecording || ivTranscribing) return undefined;
    const id = setTimeout(() => ivInputRef.current?.focus(), 60);
    return () => clearTimeout(id);
  }, [stInterviewLoading, stInterviewStep, ivRecording, ivTranscribing]);
  // TB-104: timestamp when in-conversation recording started, to reject silent/accidental taps
  // (a near-empty recording makes Whisper hallucinate a phrase that then gets answered as if real).
  const ivRecordStartRef = React.useRef(0);
  // TB-104 steering: confirm before leaving an in-progress interview that has unsaved substance
  const [ivExitConfirm, setIvExitConfirm] = React.useState(false);
  const ivShellRef = React.useRef(null);
  const ivThreadRef = React.useRef(null);
  const ivDockRef = React.useRef(null);
  React.useEffect(() => {
    if (stInterviewStep !== 'conversation') return undefined;
    const root = document.documentElement;
    const body = document.body;
    root.classList.add('iv-chat-lock');
    body.classList.add('iv-chat-lock');
    return () => {
      root.classList.remove('iv-chat-lock');
      body.classList.remove('iv-chat-lock');
    };
  }, [stInterviewStep]);
  React.useEffect(() => {
    if (stInterviewStep !== 'conversation') return undefined;
    const apply = () => {
      const shell = ivShellRef.current;
      if (!shell || typeof window === 'undefined') return;
      const vv = window.visualViewport;
      const mobile = window.innerWidth <= 640;
      const insets = typedShellInsets({
        innerHeight: window.innerHeight,
        visualViewportHeight: vv ? vv.height : window.innerHeight,
        visualViewportOffsetTop: vv ? vv.offsetTop : 0,
        mobile,
        innerWidth: window.innerWidth,
      });
      if (mobile) {
        shell.style.top = `${(vv ? vv.offsetTop : 0) + insets.top}px`;
        shell.style.left = '0px';
        shell.style.right = '0px';
        shell.style.bottom = `${insets.bottom}px`;
        shell.style.height = 'auto';
      } else {
        shell.style.top = '0px';
        shell.style.left = `${insets.left || desktopSidebarPx(window.innerWidth)}px`;
        shell.style.right = '0px';
        shell.style.bottom = '0px';
        shell.style.height = 'auto';
      }
    };
    apply();
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (vv) {
      vv.addEventListener('resize', apply);
      vv.addEventListener('scroll', apply);
    }
    window.addEventListener('resize', apply);
    return () => {
      if (vv) {
        vv.removeEventListener('resize', apply);
        vv.removeEventListener('scroll', apply);
      }
      window.removeEventListener('resize', apply);
    };
  }, [stInterviewStep]);
  React.useEffect(() => {
    if (stInterviewStep !== 'conversation') return;
    autosizeComposer(ivInputRef.current);
  }, [stInterviewInput, stInterviewStep]);
  // Review-screen back-guard: replaces window.confirm with a clear three-option in-app dialog
  const [stReviewBackConfirm, setStReviewBackConfirm] = React.useState(false);
  // REVEAL-001 C2 (State 1). revealEditing: the reveal's "Edit" door → the edit
  // fields (C3 turns this into the amber-ribbon State 2). revealQrOn: the in-page
  // "scan to play" toggle — default ON for a voice story (a recording exists),
  // absent for a typed story (no audio). revealViewedRef: fire chapter_reveal_viewed
  // exactly once per saved story (ref-guarded).
  const [revealEditing, setRevealEditing] = React.useState(false);
  // REVEAL-002 §6b: the QR toggle is removed from the reveal (photos + scan-to-play live in Book
  // Builder). revealQrOn stays true so a SAVED voice story's revisit preview (State 1, R2 scope) still
  // shows its own already-saved QR; typed R1 stories have no audio so no QR renders regardless.
  const [revealQrOn] = React.useState(true);
  // REVEAL-002 R1 State 0: confirm dialog for Discard-this-unsaved-story (the ONLY non-save exit).
  const [revealDiscardConfirm, setRevealDiscardConfirm] = React.useState(false);
  // F-415-STREAM: follow the stream like a chat — keep the newest words in view while the teller
  // is at (or near) the bottom; never fight a reader who has scrolled up to reread.
  React.useEffect(() => {
    if (stInterviewStep !== 'generating' || !stStreamPreview) return;
    const nearBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 220;
    if (nearBottom) window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }, [stStreamPreview, stInterviewStep]);

  // REVEAL-002 R3: the landing veil. veilPhase: null (undecided) | 'veil' (holding) | 'arriving' (page
  // settling) | 'done' (chrome live). Plays ONCE per finish-arrival — gated on landingPlayedAt (the draft
  // row), never on resume/State-1/State-3, never under prefers-reduced-motion.
  const [veilPhase, setVeilPhase] = React.useState(null);
  // Walk round 2, item 7: the reveal's paging state, reported up by RevealPages so the pinned dock
  // owns the controls. { page, total, go } | null.
  const [revealPager, setRevealPager] = React.useState(null);

  // ── THE PAGED READER'S STAGE (founder ruling 2026-08-10, option A) ─────────────────────────
  // The page's height is MEASURED, from the stage's own top to the top of the pinned dock, so it
  // adapts to whatever chrome each breakpoint puts around it (mobile header + bottom nav, desktop
  // sidebar) without this file hard-coding any of their sizes — the hard-coding is what produced
  // the desktop banding bug. Below MIN_PAGED_STAGE a real page would be a few lines, so the reader
  // falls back to the scrolling card instead of pretending to paginate.
  const MIN_PAGED_STAGE = 260;
  const stageRef = React.useRef(null);
  const dockRef = React.useRef(null);
  const [stageHeight, setStageHeight] = React.useState(0);
  const paged = stageHeight >= MIN_PAGED_STAGE;
  React.useLayoutEffect(() => {
    const measure = () => {
      const stage = stageRef.current;
      if (!stage) { setStageHeight(0); return; }
      const top = stage.getBoundingClientRect().top;
      const dockTop = dockRef.current
        ? dockRef.current.getBoundingClientRect().top
        : window.innerHeight - 150; // first paint, before the dock exists
      const h = Math.floor(dockTop - top - 20); // 20, not 12: measured, the composition landed 4px over the fold at 12 and produced a scrollbar for nothing
      // Only accept a real change: a 1px jitter must not re-paginate the chapter under the reader.
      setStageHeight((prev) => (Math.abs(prev - h) > 4 ? Math.max(0, h) : prev));
    };
    measure();
    // The dock mounts through a portal, so the first layout pass can run before it exists. Re-measure
    // across the next few frames rather than trusting the estimate the first pass had to use — that
    // stale first number is what left the page overlapping the dock by 36px in the first build.
    let frames = 0;
    let raf = 0;
    const settle = () => { measure(); if (++frames < 12) raf = requestAnimationFrame(settle); };
    raf = requestAnimationFrame(settle);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) {
      if (stageRef.current && stageRef.current.parentElement) ro.observe(stageRef.current.parentElement);
      if (dockRef.current) ro.observe(dockRef.current);
    }
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, [stInterviewStep, stProcessed.draftId, stProcessed.saved, veilPhase]); // eslint-disable-line react-hooks/exhaustive-deps
  const [pagesReady, setPagesReady] = React.useState(false); // B2a: RevealPages has PAINTED (onReady)
  const [veilMinHeld, setVeilMinHeld] = React.useState(false); // the ~1.6s minimum dwell has elapsed
  const veilStartedRef = React.useRef(null); // the draftId whose landing has already been handled
  const veilRef = React.useRef(null);
  const skipVeil = () => setVeilPhase('done'); // INSTANT; effect (2), keyed on veilPhase, clears the timers

  // (1) Decide ONCE per fresh finish-arrival whether the veil plays. Marks landingPlayedAt at veil-START
  // (a close mid-veil never replays). NO timers here — F-61: arming them in this guarded effect meant a
  // StrictMode mount→cleanup→mount cleared them, then the veilStartedRef guard skipped re-arming, so the
  // veil hung forever. Timers now live in (2), keyed on veilPhase, which always re-arms.
  React.useEffect(() => {
    const dId = stProcessed.draftId;
    const inReadyState = stInterviewStep === 'review' && !stProcessed.saved && !!dId;
    if (!inReadyState || veilStartedRef.current === dId) return;
    veilStartedRef.current = dId;
    const reduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // F-415-STREAM (founder walk feedback 2026-08-10): a teller who WATCHED the story stream in has
    // already had the arrival — playing the veil after it reads as a screen flash, not a ceremony.
    const shouldVeil = !stProcessed.resumed && !stProcessed.streamed && !stProcessed.landingPlayedAt && !reduced;
    if (!shouldVeil) { setVeilPhase('done'); return; } // reduced-motion / resume / already-played → instant
    setVeilMinHeld(false); setPagesReady(false); // reset the lift gates for this arrival
    setVeilPhase('veil');
    if (markStoryLandingPlayed) markStoryLandingPlayed(dId); // mark at veil-start (never replays)
  }, [stProcessed.draftId, stProcessed.saved, stInterviewStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // (2) While the veil holds, arm the minimum dwell + the safety cap. KEYED ON veilPhase so it always
  // re-arms (StrictMode-safe), and cleans up the instant the veil lifts/skips.
  React.useEffect(() => {
    if (veilPhase !== 'veil') return undefined;
    const minT = setTimeout(() => setVeilMinHeld(true), 1600); // minimum dwell
    const maxT = setTimeout(() => { setVeilMinHeld(true); setPagesReady(true); }, 6000); // never trap on a hung render
    return () => { clearTimeout(minT); clearTimeout(maxT); };
  }, [veilPhase]);

  // (3) LIFT only when the minimum dwell AND the page render are both done — it lifts INTO the settled
  // page (or the honest error state), never onto "Preparing…". Then the page settles (~1.5s), chrome follows.
  React.useEffect(() => {
    if (veilPhase !== 'veil' || !veilMinHeld || !pagesReady) return undefined;
    setVeilPhase('arriving');
    const t = setTimeout(() => setVeilPhase('done'), 1500);
    return () => clearTimeout(t);
  }, [veilPhase, veilMinHeld, pagesReady]);

  // (4) A11y: move focus to the veil so a screen reader lands on the live region; listen at the window so
  // ANY key dismisses it even if focus was stolen (skip is instant).
  React.useEffect(() => {
    if (veilPhase !== 'veil') return undefined;
    const id = setTimeout(() => { if (veilRef.current) veilRef.current.focus(); }, 30);
    const onKey = () => setVeilPhase('done');
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(id); window.removeEventListener('keydown', onKey); };
  }, [veilPhase]);

  const revealViewedRef = React.useRef(null);
  // C3 State 2: baseline snapshot for Discard (the saved copy at edit-entry); and a flag when the
  // current edit session was RESTORED from a pagehide draft (shows the "we kept your edits" note).
  const [revealEditBaseline, setRevealEditBaseline] = React.useState(null);
  const [revealDraftRestored, setRevealDraftRestored] = React.useState(false);
  const revealDraftCheckedRef = React.useRef(null);
  const REVEAL_DRAFT_KEY = (sid) => `reveal_draft_${sid}`;
  // C4 State 3 (kept): "Keep it" → the completion beat. revealKept is per saved story; firstPromptFiredRef
  // fires first_prompt_shown once when the next-question tease surfaces on State 3.
  const [revealKept, setRevealKept] = React.useState(false);
  const firstPromptFiredRef = React.useRef(null);

  // REVEAL-001 §6: chapter_reveal_viewed fires ONCE on arrival at State 1 (the
  // funnel edge this screen owns: entry_saved → reveal engagement). Ref-guarded
  // per saved story so a re-render (e.g. version switch) never re-fires it.
  React.useEffect(() => {
    const saved = stInterviewStep === 'review' && stProcessed.saved && stProcessed.savedStoryId && !revealEditing;
    if (saved && revealViewedRef.current !== stProcessed.savedStoryId) {
      revealViewedRef.current = stProcessed.savedStoryId;
      trackEvent('chapter_reveal_viewed', {
        channel: stProcessed.audioBlob ? 'voice' : 'typed',
        book_type: isGiftBook ? 'celebration' : 'personal',
      });
    }
  }, [stInterviewStep, stProcessed.saved, stProcessed.savedStoryId, stProcessed.audioBlob, revealEditing, isGiftBook]);

  // C4: a fresh saved story resets the per-story reveal sub-states so a new reveal never inherits the
  // previous story's "kept"/"editing" screen.
  const revealStoryRef = React.useRef(null);
  // F-72 (flash b): a fresh Save-from-reveal is heading to State 3, so this reset must NOT zero revealKept
  // for it (that reset, firing when the new savedStoryId appears, is what flashed the State-1 saved-revisit
  // screen between Save and the celebration). handleSaveToBook sets this before the save; the effect
  // consumes it (clears it) so a LATER genuine revisit still resets correctly.
  const pendingKeepRef = React.useRef(false);
  React.useEffect(() => {
    if (stProcessed.savedStoryId && revealStoryRef.current !== stProcessed.savedStoryId) {
      revealStoryRef.current = stProcessed.savedStoryId;
      if (pendingKeepRef.current) { pendingKeepRef.current = false; } // this fresh keep stays on State 3
      else { setRevealKept(false); setRevealEditing(false); }
    }
  }, [stProcessed.savedStoryId]);

  // C4 §6: first_prompt_shown fires ONCE when the State-3 next-question tease surfaces (ref-guarded per story).
  React.useEffect(() => {
    if (stInterviewStep === 'review' && stProcessed.saved && stProcessed.savedStoryId && revealKept
        && firstPromptFiredRef.current !== stProcessed.savedStoryId) {
      firstPromptFiredRef.current = stProcessed.savedStoryId;
      trackEvent('first_prompt_shown', { context: 'reveal_kept', book_type: isGiftBook ? 'celebration' : 'personal' });
    }
  }, [stInterviewStep, stProcessed.saved, stProcessed.savedStoryId, revealKept, isGiftBook]);

  // C3 State 2, Correction 1 — DRAFT autosave on pagehide/visibilitychange while editing + dirty.
  // A DRAFT never changes what prints (unlike updateSavedInterviewStory) — it's saved to localStorage
  // and restored into State 2 next visit, so an iOS-Safari backgrounding (beforeunload never fires)
  // loses nothing while the amber "your original is still safe" promise + Discard stay honest. Only an
  // explicit "Save changes" mutates the book. Registered once; reads latest edit state via a ref.
  const revealEditStateRef = React.useRef({});
  revealEditStateRef.current = {
    editing: revealEditing, sid: stProcessed.savedStoryId, dirty: stProcessed.dirty,
    title: stProcessed.title, content: stProcessed.content, activeVersion: stProcessed.activeVersion,
  };
  React.useEffect(() => {
    const saveDraft = () => {
      const s = revealEditStateRef.current;
      if (!(s.editing && s.sid && s.dirty)) return;
      try { localStorage.setItem(REVEAL_DRAFT_KEY(s.sid), JSON.stringify({ title: s.title, content: s.content, activeVersion: s.activeVersion, at: Date.now() })); } catch { /* quota / private mode */ }
    };
    const onHide = () => { if (document.visibilityState === 'hidden') saveDraft(); };
    window.addEventListener('pagehide', saveDraft);
    document.addEventListener('visibilitychange', onHide);
    return () => { window.removeEventListener('pagehide', saveDraft); document.removeEventListener('visibilitychange', onHide); };
  }, []);

  // C3 State 2, Correction 1 — restore a pagehide DRAFT on the next arrival at the reveal for that
  // story: land directly in State 2 (dirty) with the "we kept your unsaved edits" note. The baseline
  // captured here is the SAVED (book) copy, so Discard still returns to what actually prints.
  React.useEffect(() => {
    if (!(stInterviewStep === 'review' && stProcessed.saved && stProcessed.savedStoryId) || revealEditing) return;
    const sid = stProcessed.savedStoryId;
    if (revealDraftCheckedRef.current === sid) return;
    revealDraftCheckedRef.current = sid;
    try {
      const raw = localStorage.getItem(REVEAL_DRAFT_KEY(sid));
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft || typeof draft.content !== 'string') return;
      setRevealEditBaseline({ title: stProcessed.title, content: stProcessed.content, activeVersion: stProcessed.activeVersion });
      setStProcessed(p => ({ ...p, title: draft.title ?? p.title, content: draft.content, activeVersion: draft.activeVersion ?? p.activeVersion, dirty: true }));
      setRevealDraftRestored(true);
      setRevealEditing(true);
    } catch { /* ignore malformed draft */ }
  }, [stInterviewStep, stProcessed.saved, stProcessed.savedStoryId, revealEditing]); // eslint-disable-line react-hooks/exhaustive-deps
  // TB-104: expandable "tips for a great interview" panel. Auto-opens the first time someone
  // reaches the interview (so they learn they can steer), collapsed on every visit after.
  const [ivTipsOpen, setIvTipsOpen] = React.useState(() => {
    try { return localStorage.getItem('tb_interview_tips_seen') !== '1'; } catch (e) { return true; }
  });
  const toggleIvTips = () => {
    setIvTipsOpen(prev => {
      if (prev) { try { localStorage.setItem('tb_interview_tips_seen', '1'); } catch (e) {} }
      return !prev;
    });
  };

  const startKidRecording = async () => {
    try {
      if (kidVideoMode) {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        kidAudioStreamRef.current = stream;
        if (kidLiveVideoRef.current) { kidLiveVideoRef.current.srcObject = stream; kidLiveVideoRef.current.play(); }
        // Video recorder
        kidVideoChunksRef.current = [];
        const videoRec = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus', audioBitsPerSecond: 48000 });
        videoRec.ondataavailable = (e) => { if (e.data.size > 0) kidVideoChunksRef.current.push(e.data); };
        kidVideoRecorderRef.current = videoRec;
        // Audio-only recorder for transcription
        const audioStream = new MediaStream(stream.getAudioTracks());
        kidAudioChunksRef.current = [];
        const audioRec = new MediaRecorder(audioStream, { mimeType: 'audio/webm', audioBitsPerSecond: 48000 });
        audioRec.ondataavailable = (e) => { if (e.data.size > 0) kidAudioChunksRef.current.push(e.data); };
        audioRec.onstop = async () => {
          const audioBlob = new Blob(kidAudioChunksRef.current, { type: 'audio/webm' });
          const videoBlob = new Blob(kidVideoChunksRef.current, { type: 'video/webm' });
          stream.getTracks().forEach(t => t.stop());
          setKidRecording(false);
          setKidTranscribing(true);
          try {
            const text = await transcribeAudio(audioBlob);
            if (text?.trim()) {
              setKidCurrentAnswer(prev => prev ? prev + ' ' + text.trim() : text.trim());
              setKidAudioBlobs(prev => [...prev, audioBlob]);
              setKidVideoBlobs(prev => [...prev, videoBlob]);
            }
          } catch (err) { showToast('Could not transcribe. Try typing instead.', 'error'); }
          setKidTranscribing(false);
        };
        kidRecorderRef.current = audioRec;
        videoRec.start(100);
        audioRec.start(250);
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        kidAudioStreamRef.current = stream;
        kidAudioChunksRef.current = [];
        const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm', audioBitsPerSecond: 48000 });
        recorder.ondataavailable = (e) => { if (e.data.size > 0) kidAudioChunksRef.current.push(e.data); };
        recorder.onstop = async () => {
          const blob = new Blob(kidAudioChunksRef.current, { type: 'audio/webm' });
          stream.getTracks().forEach(t => t.stop());
          setKidRecording(false);
          setKidTranscribing(true);
          try {
            const text = await transcribeAudio(blob);
            if (text?.trim()) {
              setKidCurrentAnswer(prev => prev ? prev + ' ' + text.trim() : text.trim());
              setKidAudioBlobs(prev => [...prev, blob]);
            }
          } catch (err) { showToast('Could not transcribe. Try typing instead.', 'error'); }
          setKidTranscribing(false);
        };
        kidRecorderRef.current = recorder;
        recorder.start(250);
      }
      setKidRecording(true);
    } catch (err) {
      showToast(kidVideoMode
        ? 'Could not access camera. Please allow camera access and try again.'
        : 'Could not access microphone. Please allow microphone access.', 'error');
    }
  };

  const stopKidRecording = () => {
    if (kidVideoRecorderRef.current) {
      kidVideoRecorderRef.current.stop();
      kidVideoRecorderRef.current = null;
    }
    if (kidRecorderRef.current && kidRecording) {
      kidRecorderRef.current.stop();
    }
  };

  const kidSubmitAnswer = () => {
    if (!kidCurrentAnswer.trim()) return;
    const updated = [...kidAnswers, { question: KID_QUESTIONS[kidQuestionIdx], answer: kidCurrentAnswer.trim() }];
    setKidAnswers(updated);
    setKidCurrentAnswer('');
    if (kidQuestionIdx < KID_QUESTIONS.length - 1) {
      setKidQuestionIdx(kidQuestionIdx + 1);
    } else {
      setKidStep('review');
    }
  };

  const kidSkipQuestion = () => {
    if (kidQuestionIdx < KID_QUESTIONS.length - 1) {
      setKidQuestionIdx(kidQuestionIdx + 1);
      setKidCurrentAnswer('');
    } else {
      setKidStep('review');
    }
  };

  const buildKidStoryContent = (answers) => {
    return answers.map(a => `${a.question}\n${a.answer}`).join('\n\n');
  };

  const handleKidPolish = async () => {
    setKidPolishing(true);
    try {
      const raw = buildKidStoryContent(kidAnswers);
      const { getStoryCleanupPrompt } = await import('../utils/aiPrompts');
      const prompt = getStoryCleanupPrompt(raw, 'grammar', { title: `${kidName}'s Words for Mom` });
      const d = await callAi({ action: 'expand', messages: [{ role: 'user', content: raw }], roughDraft: prompt });
      const polished = d.text || raw;
      // Remove any AI-generated title line — we keep our own
      const lines = polished.split('\n').filter(l => l.trim());
      let content = polished;
      if (lines.length > 1 && lines[0].length < 80 && !lines[0].includes('?')) {
        content = lines.slice(1).join('\n').trim();
      }
      // Re-parse into Q&A pairs if possible, otherwise use as-is
      setKidAnswers(prev => {
        // Try to split polished text back into Q&A
        const pairs = content.split('\n\n').filter(b => b.trim());
        if (pairs.length === prev.length) {
          return prev.map((a, i) => {
            const block = pairs[i] || '';
            const blockLines = block.split('\n');
            // First line is question if it contains '?', otherwise use original question
            const answer = blockLines[blockLines.length > 1 && blockLines[0].includes('?') ? 1 : 0] ?
              (blockLines.length > 1 && blockLines[0].includes('?') ? blockLines.slice(1).join('\n').trim() : block.trim()) : a.answer;
            return { ...a, answer: answer || a.answer };
          });
        }
        return prev;
      });
    } catch (err) {
      console.error('Kid polish error:', err);
      showToast('Could not clean up text. You can still save as-is.', 'error');
    }
    setKidPolishing(false);
  };

  const saveKidStory = async () => {
    if (kidAnswers.length === 0) return;
    ensureBookExists();
    setKidSaving(true);
    try {
      // Combine media blobs — prefer video if kid recorded in video mode
      const hasVideo = kidVideoBlobs.length > 0;
      let mediaPath = null;
      if (hasVideo || kidAudioBlobs.length > 0) {
        try {
          const blobs = hasVideo ? kidVideoBlobs : kidAudioBlobs;
          const mimeType = hasVideo ? 'video/webm' : 'audio/webm';
          const combinedBlob = new Blob(blobs, { type: mimeType });
          const safeName = `${kidName.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '_')}_Kid_Story`;
          const fileName = `${safeName}_${Date.now()}.webm`;
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const identityId = session.identityId;
          const folder = hasVideo ? 'video' : 'audio';
          mediaPath = `private/${identityId}/${folder}/${fileName}`;
          await uploadData({ path: mediaPath, data: combinedBlob, options: { contentType: mimeType, metadata: { title: `${kidName}'s Words for Mom` } } });
        } catch (mediaErr) { console.warn('[Kid Media Upload] Failed:', mediaErr.message); mediaPath = null; }
      }

      let contentToSave = buildKidStoryContent(kidAnswers);
      if (mediaPath) { contentToSave += `\n\n[${hasVideo ? 'video' : 'audio'}:${mediaPath}]`; }
      const title = `${kidName}'s Words for Mom`;
      const result = await client.graphql({ query: createLegacyEntry, variables: { input: { userId: user?.userId || user?.username, title, content: contentToSave, type: 'story', createdAt: new Date().toISOString() } } });
      const newStoryId = result.data.createLegacyEntry.id;
      if (newStoryId) {
        const newPage = { storyId: newStoryId, contributorName: kidName, isKidStory: true, caption: '', layout: 'classic' };
        const updatedPages = [...stBookPages, newPage];
        setStBookPages(updatedPages);
        const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverPhotoPath: stBookCoverPhotoPath, pages: cleanPagesForSave(updatedPages) };
        saveBookData(bookData);
        // Auto-link QR code if user opted in and we have media
        if (mediaPath && kidIncludeQr) {
          await autoLinkRecordingToQr(newStoryId, mediaPath, title, updatedPages, hasVideo ? 'VIDEO' : 'AUDIO');
        }
      }
      showToast(`${kidName}'s story saved to the book!`);
      await refreshStories();
      // Reset kid mode and return to book
      setKidMode(false); setKidName(''); setKidStep('name'); setKidQuestionIdx(0); setKidAnswers([]); setKidCurrentAnswer(''); setKidAudioBlobs([]); setKidVideoBlobs([]); setKidVideoMode(false); setKidIncludeQr(false);
      setStBookStep(0); setStBookOpen(false); setActiveTabRaw('book'); window.scrollTo(0, 0);
    } catch (e) { showToast('Save failed.', 'error'); }
    setKidSaving(false);
  };

  const exitKidMode = () => {
    setKidMode(false); setKidName(''); setKidStep('name'); setKidQuestionIdx(0); setKidAnswers([]); setKidCurrentAnswer(''); setKidAudioBlobs([]); setKidVideoBlobs([]); setKidVideoMode(false); setKidIncludeQr(false);
    if (kidVideoRecorderRef.current) { kidVideoRecorderRef.current.stop(); }
    if (kidRecorderRef.current && kidRecording) { kidRecorderRef.current.stop(); }
  };

  // TB-104: Whisper hallucinates short canned phrases when given silence or noise.
  // If a recording was too brief to contain real speech, or the transcript is a known
  // silence-artifact, treat it as "nothing said" rather than feeding it to the interview.
  const IV_MIN_RECORD_MS = 1200; // anything shorter is an accidental tap, not an answer
  const isLikelyWhisperNoise = (text) => {
    const t = (text || '').trim().toLowerCase().replace(/[.!?,]/g, '').trim();
    if (!t) return true;
    // Common Whisper silence/noise hallucinations
    const NOISE = new Set([
      'you', 'thank you', 'thanks', 'thanks for watching', 'thank you for watching',
      'please subscribe', 'subscribe', 'bye', 'okay', 'ok', 'yeah', 'hmm', 'mhm',
      'the', 'so', 'um', 'uh', 'silence', 'music', 'applause', 'plain scramble',
    ]);
    if (NOISE.has(t)) return true;
    // A single very short token (1-2 chars) is not a real answer
    if (t.length <= 2) return true;
    return false;
  };

  // ── TB-104 Stage B: in-conversation recording (reuses existing transcribeAudio from App.js) ──
  // startIvRecording: starts mic or camera recording inline in the capture bar.
  // On stop, transcribes the audio and delivers the text into the conversation thread.
  //
  // 🔴 UNREACHABLE since the founder removed the dictation mic (2026-08-10). Its only caller was
  // `handleMicToggle`. It is left in place rather than swept out in the same change for two reasons:
  // its video branch was ALREADY dead before this (`setIvVideoMode(true)` exists nowhere), so the
  // sweep is bigger than this ruling and belongs on its own; and deleting it orphans the whole
  // keepsake-blob chain (`setIvKeepsakeAudioBlob`/`VideoBlob`/`Captured`) on a screen about to be
  // walked. Filed as `IV-DICTATION-DEAD-CODE` in docs/BACKLOG.md. Note-don't-fix, rule 7.
  // eslint-disable-next-line no-unused-vars
  const startIvRecording = async (onTranscribed) => {
    try {
      if (ivVideoMode) {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        ivAudioStreamRef.current = stream;
        if (ivLiveVideoRef.current) { ivLiveVideoRef.current.srcObject = stream; ivLiveVideoRef.current.play(); }
        ivVideoChunksRef.current = [];
        const videoRec = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus', audioBitsPerSecond: 48000 });
        videoRec.ondataavailable = (e) => { if (e.data.size > 0) ivVideoChunksRef.current.push(e.data); };
        ivVideoRecorderRef.current = videoRec;
        ivAudioChunksRef.current = [];
        const audioStream = new MediaStream(stream.getAudioTracks());
        const audioRec = new MediaRecorder(audioStream, { mimeType: 'audio/webm', audioBitsPerSecond: 48000 });
        audioRec.ondataavailable = (e) => { if (e.data.size > 0) ivAudioChunksRef.current.push(e.data); };
        audioRec.onstop = async () => {
          const audioBlob = new Blob(ivAudioChunksRef.current, { type: 'audio/webm' });
          const videoBlob = new Blob(ivVideoChunksRef.current, { type: 'video/webm' });
          stream.getTracks().forEach(t => t.stop());
          setIvRecording(false);
          // Reject accidental/silent taps before transcribing (Whisper hallucinates on silence)
          if (Date.now() - ivRecordStartRef.current < IV_MIN_RECORD_MS) {
            showToast("I didn't catch anything. Tap record and start whenever you're ready.", 'info');
            return;
          }
          setIvTranscribing(true);
          try {
            const text = await transcribeAudio(audioBlob);
            if (text?.trim() && !isLikelyWhisperNoise(text)) {
              // Keepsake: retain only the FIRST recording (spec §7a-2)
              if (!ivKeepsakeCaptured) {
                setIvKeepsakeAudioBlob(audioBlob);
                setIvKeepsakeVideoBlob(videoBlob);
                setIvKeepsakeCaptured(true);
                setStProcessed(p => ({ ...p, audioBlob, videoBlob, includeQr: true }));
              }
              onTranscribed(text.trim());
            } else {
              showToast("I didn't quite catch that. Try again, or type your answer instead.", 'info');
            }
          } catch (err) {
            showToast("Couldn't hear that clearly. You can type your answer instead.", 'error');
          }
          setIvTranscribing(false);
        };
        ivRecorderRef.current = audioRec;
        videoRec.start(100);
        audioRec.start(250);
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        ivAudioStreamRef.current = stream;
        ivAudioChunksRef.current = [];
        const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm', audioBitsPerSecond: 48000 });
        recorder.ondataavailable = (e) => { if (e.data.size > 0) ivAudioChunksRef.current.push(e.data); };
        recorder.onstop = async () => {
          const audioBlob = new Blob(ivAudioChunksRef.current, { type: 'audio/webm' });
          stream.getTracks().forEach(t => t.stop());
          setIvRecording(false);
          // Reject accidental/silent taps before transcribing (Whisper hallucinates on silence)
          if (Date.now() - ivRecordStartRef.current < IV_MIN_RECORD_MS) {
            showToast("I didn't catch anything. Tap record and start whenever you're ready.", 'info');
            return;
          }
          setIvTranscribing(true);
          try {
            const text = await transcribeAudio(audioBlob);
            if (text?.trim() && !isLikelyWhisperNoise(text)) {
              // Keepsake: retain only the FIRST recording (spec §7a-2)
              if (!ivKeepsakeCaptured) {
                setIvKeepsakeAudioBlob(audioBlob);
                setIvKeepsakeCaptured(true);
                setStProcessed(p => ({ ...p, audioBlob, videoBlob: null, includeQr: true }));
              }
              onTranscribed(text.trim());
            } else {
              showToast("I didn't quite catch that. Try again, or type your answer instead.", 'info');
            }
          } catch (err) {
            showToast("Couldn't hear that clearly. You can type your answer instead.", 'error');
          }
          setIvTranscribing(false);
        };
        ivRecorderRef.current = recorder;
        recorder.start(250);
      }
      ivRecordStartRef.current = Date.now();
      setIvRecording(true);
    } catch (err) {
      showToast(ivVideoMode
        ? 'Could not access camera. Please allow camera access and try again.'
        : 'Could not access microphone. Please allow microphone access.', 'error');
    }
  };

  const stopIvRecording = () => {
    if (ivVideoRecorderRef.current) { ivVideoRecorderRef.current.stop(); ivVideoRecorderRef.current = null; }
    if (ivRecorderRef.current && ivRecording) { ivRecorderRef.current.stop(); }
  };

  // Reset IV recording state (called on Back / new interview)
  const resetIvState = () => {
    setIvRecording(false);
    setIvTranscribing(false);
    setIvKeepsakeAudioBlob(null);
    setIvKeepsakeVideoBlob(null);
    setIvKeepsakeCaptured(false);
    setIvIncludeQr(true);
    setIvVideoMode(false);
  };

  // ── Skip gateway for gift books (only one capture option) ──
  React.useEffect(() => {
    if (stShowGateway && isGiftBook) {
      setStShowGateway(false);
    }
  }, [stShowGateway, isGiftBook, setStShowGateway]);

  // ── Unsaved recording warning ──
  React.useEffect(() => {
    // TB-117: the interview review screen (voice AND text) holds a written-up but UNSAVED story
    // whose only save is a manual "Save to My Book" tap. The old condition only checked recorded
    // media blobs, so voice/text interviews got zero leave-warning and users lost stories here.
    // F-7: warn only when there is genuinely unsaved review work — an unsaved chapter, OR a saved
    // chapter with unpersisted post-save edits (dirty). A clean saved reveal must NOT trigger the
    // "Leave site?" prompt (that was the F-7 bug: saved state still warned and exited to a blank tab).
    const hasUnsavedInterview = stInterviewStep === 'review' && !!(stProcessed && stProcessed.content)
      && (!stProcessed.saved || !!stProcessed.dirty);
    // F-103: the PRE-generation interview window was unguarded (Person C's disconnect lost everything). Guard
    // a live 'conversation' that carries unwritten user turns (>=5 words of substance, mirroring ivHasSubstance)
    // so a tab close / reload no longer silently drops the telling. The reveal/State-0 draft is separately
    // durable; this covers the window BEFORE a write-up exists.
    const hasUnwrittenConversation = stInterviewStep === 'conversation'
      && interviewHasSubstance({ draft: stInterviewInput, conversation: interviewConversation }); // F-118b: an unsent draft counts too
    const hasUnsavedMedia = !!(storyAudioBlob || storyVideoBlob || stProcessed.audioBlob || stProcessed.videoBlob || hasUnsavedInterview || hasUnwrittenConversation || (kidMode && (kidAudioBlobs.length > 0 || kidVideoBlobs.length > 0)));
    const handleBeforeUnload = (e) => {
      if (hasUnsavedMedia) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [storyAudioBlob, storyVideoBlob, stProcessed.audioBlob, stProcessed.videoBlob, stInterviewStep, stProcessed.content, interviewConversation, stInterviewInput, kidMode, kidAudioBlobs.length, kidVideoBlobs.length]); // eslint-disable-line react-hooks/exhaustive-deps


    // ── KID MODE FLOW ──
    if (kidMode) {
      // Step 1: Ask for child's name
      if (kidStep === 'name') {
        return (
          <div className="storyteller-content" style={{ textAlign: 'center' }}>
            <button onClick={exitKidMode} className="st-back" style={{ textAlign: 'left', width: '100%' }}>← Back</button>
            <div style={{ paddingTop: 40, maxWidth: 520, margin: '0 auto' }}>
              <div style={{ fontSize: 64, marginBottom: 20 }}>🧒</div>
              <h2 className="st-h2" style={{ fontSize: '2rem', marginBottom: 12 }}>Add a Kid's Story</h2>
              <p className="st-sub" style={{ fontSize: '1.15rem', marginBottom: 40, lineHeight: 1.6 }}>We'll ask them a few fun questions about Mom. They can talk or type — we'll handle the rest.</p>
              <div style={{ marginBottom: 32 }}>
                <label style={{ display: 'block', fontSize: '1.1rem', color: '#0f172a', fontFamily: "'Lora', serif", fontWeight: 600, marginBottom: 10 }}>What's the child's name?</label>
                <input
                  type="text" value={kidName} onChange={e => setKidName(e.target.value)}
                  placeholder="e.g. Emma"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && kidName.trim()) setKidStep('question'); }}
                  style={{ width: '100%', maxWidth: 360, padding: '16px 20px', fontSize: '1.3rem', border: '2px solid rgba(15,23,42,0.15)', borderRadius: 12, fontFamily: "'Lora', serif", textAlign: 'center', outline: 'none', background: '#fffdf8' }}
                />
              </div>
              <button onClick={() => setKidStep('question')} disabled={!kidName.trim()}
                style={{ padding: '16px 48px', fontSize: '1.2rem', fontFamily: "'Lora', serif", fontWeight: 600, letterSpacing: 1, background: kidName.trim() ? 'rgba(15,23,42,0.85)' : 'rgba(15,23,42,0.2)', color: '#fff', border: 'none', borderRadius: 30, cursor: kidName.trim() ? 'pointer' : 'default' }}>
                Let's go →
              </button>
            </div>
          </div>
        );
      }

      // Step 2: Walk through questions one at a time
      if (kidStep === 'question') {
        const q = KID_QUESTIONS[kidQuestionIdx];
        return (
          <div className="storyteller-content" style={{ textAlign: 'center' }}>
            <button onClick={() => { if (kidQuestionIdx > 0) { setKidQuestionIdx(prev => prev - 1); } else { setKidStep('name'); } }} className="st-back" style={{ textAlign: 'left', width: '100%' }}>← Back</button>
            <div style={{ paddingTop: 20, maxWidth: 580, margin: '0 auto' }}>
              {/* Progress indicator */}
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 28 }}>
                {KID_QUESTIONS.map((_, i) => (
                  <div key={i} style={{ width: 32, height: 6, borderRadius: 3, background: i < kidQuestionIdx ? '#27ae60' : i === kidQuestionIdx ? 'rgba(15,23,42,0.7)' : 'rgba(15,23,42,0.12)', transition: 'background 0.3s' }} />
                ))}
              </div>
              <p style={{ fontSize: '1rem', color: '#3c4653', marginBottom: 8, fontFamily: "'Lora', serif" }}>Question {kidQuestionIdx + 1} of {KID_QUESTIONS.length}</p>

              {/* The question */}
              <div style={{ background: 'rgba(15,23,42,0.03)', border: '2px solid rgba(15,23,42,0.1)', borderRadius: 16, padding: '32px 24px', marginBottom: 28 }}>
                <div style={{ fontSize: '1.6rem', color: '#0f172a', lineHeight: 1.5, fontFamily: "'Lora', serif", fontWeight: 500 }}>{q}</div>
              </div>

              {/* Audio / Video toggle for kid recording */}
              {!kidRecording && !kidTranscribing && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
                  <button onClick={() => setKidVideoMode(false)}
                    style={{ padding: '8px 20px', fontSize: '0.95rem', fontFamily: "'Lora', serif", fontWeight: 600, background: !kidVideoMode ? 'rgba(15,23,42,0.85)' : 'rgba(15,23,42,0.06)', color: !kidVideoMode ? '#fff' : '#3c4653', border: '1px solid rgba(15,23,42,0.15)', borderRadius: 20, cursor: 'pointer' }}>
                     Voice
                  </button>
                  <button onClick={() => setKidVideoMode(true)}
                    style={{ padding: '8px 20px', fontSize: '0.95rem', fontFamily: "'Lora', serif", fontWeight: 600, background: kidVideoMode ? 'rgba(15,23,42,0.85)' : 'rgba(15,23,42,0.06)', color: kidVideoMode ? '#fff' : '#3c4653', border: '1px solid rgba(15,23,42,0.15)', borderRadius: 20, cursor: 'pointer' }}>
                     Video
                  </button>
                </div>
              )}

              {/* Persistent kid video element — always in DOM when video mode is on */}
              {kidVideoMode && (
                <video ref={kidLiveVideoRef} muted playsInline
                  style={{ width: '100%', maxWidth: 320, borderRadius: 16, marginBottom: 16, background: '#1a1612', display: kidRecording ? 'block' : 'none', border: '3px solid rgba(220,80,60,0.4)', margin: '0 auto 16px' }} />
              )}

              {/* Recording UI */}
              {kidRecording ? (
                <div style={{ marginBottom: 24 }}>
                  {!kidVideoMode && (
                    <div style={{ fontSize: 48, marginBottom: 12 }}></div>
                  )}
                  <p style={{ fontSize: '1.1rem', color: 'rgba(220,80,60,0.9)', fontWeight: 600, marginBottom: 16 }}>{kidVideoMode ? 'Recording...' : 'Listening...'}</p>
                  <button onClick={stopKidRecording}
                    style={{ padding: '14px 36px', fontSize: '1.1rem', fontFamily: "'Lora', serif", background: 'rgba(220,80,60,0.15)', border: '2px solid rgba(220,80,60,0.3)', borderRadius: 30, cursor: 'pointer', color: '#c0392b', fontWeight: 600 }}>
                    ⏹ Done talking
                  </button>
                </div>
              ) : kidTranscribing ? (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ width: 40, height: 40, border: '3px solid rgba(15,23,42,0.2)', borderTopColor: 'rgba(15,23,42,0.5)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
                  <p style={{ fontSize: '1.05rem', color: '#3c4653' }}>Turning words into text...</p>
                </div>
              ) : (
                <div style={{ marginBottom: 24 }}>
                  <button onClick={startKidRecording}
                    style={{ padding: '16px 40px', fontSize: '1.15rem', fontFamily: "'Lora', serif", fontWeight: 600, background: 'rgba(15,23,42,0.85)', color: '#fff', border: 'none', borderRadius: 30, cursor: 'pointer', marginBottom: 16, letterSpacing: 1 }}>
                    {kidVideoMode ? '' : ''} Tap to {kidVideoMode ? 'record' : 'talk'}
                  </button>
                  <p style={{ fontSize: '0.95rem', color: '#3c4653', marginBottom: 16 }}>or type below</p>
                </div>
              )}

              {/* Text input */}
              <textarea
                value={kidCurrentAnswer}
                onChange={e => setKidCurrentAnswer(e.target.value)}
                placeholder={`${kidName}'s answer...`}
                rows={4}
                style={{ width: '100%', padding: '16px 18px', fontSize: '1.15rem', border: '2px solid rgba(15,23,42,0.12)', borderRadius: 12, fontFamily: "'Lora', serif", resize: 'vertical', outline: 'none', background: '#fffdf8', lineHeight: 1.6 }}
              />

              {/* Actions */}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20 }}>
                <button onClick={kidSubmitAnswer} disabled={!kidCurrentAnswer.trim()}
                  style={{ padding: '14px 36px', fontSize: '1.1rem', fontFamily: "'Lora', serif", fontWeight: 600, background: kidCurrentAnswer.trim() ? 'rgba(15,23,42,0.85)' : 'rgba(15,23,42,0.2)', color: '#fff', border: 'none', borderRadius: 30, cursor: kidCurrentAnswer.trim() ? 'pointer' : 'default', letterSpacing: 1 }}>
                  {kidQuestionIdx < KID_QUESTIONS.length - 1 ? 'Next →' : 'Finish →'}
                </button>
                <button onClick={kidSkipQuestion}
                  style={{ padding: '14px 24px', fontSize: '1rem', fontFamily: "'Lora', serif", background: 'none', border: '1.5px solid rgba(15,23,42,0.12)', borderRadius: 30, cursor: 'pointer', color: '#3c4653' }}>
                  Skip this one
                </button>
              </div>
              {kidAnswers.length > 0 && (
                <button onClick={() => setKidStep('review')}
                  style={{ marginTop: 12, padding: '10px 24px', fontSize: '0.95rem', fontFamily: "'Lora', serif", background: 'none', border: 'none', cursor: 'pointer', color: '#3c4653', textDecoration: 'underline' }}>
                  Done — I'm finished ({kidAnswers.length} answered)
                </button>
              )}
            </div>
          </div>
        );
      }

      // Step 3: Review all answers
      if (kidStep === 'review') {
        return (
          <div className="storyteller-content" style={{ textAlign: 'center' }}>
            <button onClick={() => { setKidStep('question'); setKidQuestionIdx(Math.max(0, kidAnswers.length - 1)); }} className="st-back" style={{ textAlign: 'left', width: '100%' }}>← Back</button>
            <div style={{ paddingTop: 20, maxWidth: 600, margin: '0 auto' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
              <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 8 }}>{kidName}'s Words for Mom</h2>
              <p className="st-sub" style={{ fontSize: '1.05rem', marginBottom: 32 }}>{kidAnswers.length} question{kidAnswers.length !== 1 ? 's' : ''} answered</p>

              {/* Preview answers */}
              <div style={{ textAlign: 'left', marginBottom: 32 }}>
                {kidAnswers.map((a, i) => (
                  <div key={i} style={{ background: 'rgba(15,23,42,0.03)', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 12, padding: '18px 20px', marginBottom: 12 }}>
                    <p style={{ fontSize: '0.95rem', color: '#3c4653', fontWeight: 600, marginBottom: 6, fontFamily: "'Lora', serif" }}>{a.question}</p>
                    <p style={{ fontSize: '1.1rem', color: '#0f172a', lineHeight: 1.6, fontFamily: "'Lora', serif" }}>{a.answer}</p>
                  </div>
                ))}
              </div>

              {/* Voice recording callout — warm messaging for kid recordings */}
              {(kidAudioBlobs.length > 0 || kidVideoBlobs.length > 0) && kidAnswers.length > 0 && (
                <div style={{ marginBottom: 24, padding: '20px 24px', background: 'rgba(201,168,76,0.08)', borderRadius: 14, border: '1.5px solid rgba(201,168,76,0.2)', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.15rem', color: '#0f172a', fontFamily: "'Lora', serif", fontWeight: 500, lineHeight: 1.6 }}>
                    {kidVideoBlobs.length > 0 ? '' : ''} Mom will be able to scan a QR code in the book to {kidVideoBlobs.length > 0 ? 'watch' : 'hear'} {kidName}'s {kidVideoBlobs.length > 0 ? 'video' : 'voice'} in their own words.
                  </div>
                  {kidIncludeQr && (
                    <button onClick={() => setKidIncludeQr(false)}
                      style={{ marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: '#3c4653', fontFamily: "'Lora', serif", textDecoration: 'underline' }}>
                      Don't include voice recording
                    </button>
                  )}
                  {!kidIncludeQr && (
                    <button onClick={() => setKidIncludeQr(true)}
                      style={{ marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: '#7a5a10', fontFamily: "'Lora', serif", textDecoration: 'underline' }}>
                      Include voice recording
                    </button>
                  )}
                </div>
              )}

              {kidAnswers.length === 0 ? (
                <div style={{ padding: '24px', background: 'rgba(15,23,42,0.03)', borderRadius: 12, marginBottom: 24 }}>
                  <p style={{ color: '#3c4653', fontSize: '1.05rem' }}>No questions were answered. Go back to answer at least one.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={saveKidStory} disabled={kidSaving || kidPolishing}
                    style={{ padding: '16px 36px', fontSize: '1.1rem', fontFamily: "'Lora', serif", fontWeight: 600, background: 'rgba(15,23,42,0.85)', color: '#fff', border: 'none', borderRadius: 30, cursor: 'pointer', letterSpacing: 1, opacity: kidSaving ? 0.5 : 1 }}>
                    {kidSaving ? ' Saving...' : '💾 Save as-is'}
                  </button>
                  <button onClick={async () => { await handleKidPolish(); }} disabled={kidPolishing || kidSaving}
                    style={{ padding: '16px 36px', fontSize: '1.1rem', fontFamily: "'Lora', serif", fontWeight: 600, background: 'rgba(15,23,42,0.05)', color: '#0f172a', border: '2px solid rgba(15,23,42,0.15)', borderRadius: 30, cursor: 'pointer', letterSpacing: 1, opacity: kidPolishing ? 0.5 : 1 }}>
                    {kidPolishing ? ' Cleaning up...' : '✏️ Clean up spelling & punctuation'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      }
    }

    // ── TB-104 Phase 2: VOICE INTERVIEW MODE ──
    // Renders when user taps "Talk it out" on the question card.
    // VoiceInterview handles the ElevenLabs session internally.
    // On done, we collect the user-role turns and feed them to generateStInterviewStory
    // so the result lands on the existing review/save screen — same payoff as the text interview.
    // F-80: mount gate relaxed — drop `&& stSelectedPrompt` so the SEEDLESS doorway entry (null prompt)
    // mounts the voice interview. VoiceInterview is null-safe (F-62/F-64): a null prompt opens on the
    // COLD_START_OPENER and sends coldStart:true. A topic-picked entry still passes its chosen prompt.
    if (stVoiceInterviewMode) {
      const handleVoiceInterviewDone = async (transcriptEntries, conversationId, opts = {}) => {
        // transcriptEntries is the FULL conversation [{role:'user'|'assistant', content}]
        // pulled from ElevenLabs' server-side record. Collect the storyteller's spoken
        // answers, but pass the whole Q&A so the assembler can stitch a coherent chapter.
        const turns = (transcriptEntries || []).filter(t => t && t.content && t.content.trim());
        const userTurns = turns.filter(t => t.role === 'user');
        const userAnswers = userTurns.map(t => t.content).join('\n\n');

        // Thin-input belt-and-suspenders: VoiceInterview's finishUp already checks content
        // before entering 'writingup', so this path should only be reached with enough text.
        // If it is somehow called under the minimum (e.g. the done phase after a drop with
        // a very short session), reset voice mode cleanly via onCancel rather than leaving
        // the UI in 'writingup' permanently. generateStInterviewStory in App.js is a
        // further backstop for genuinely near-empty input that slips through.
        const voiceUserWordCount = userAnswers.trim().split(/\s+/).filter(Boolean).length;
        if (!userAnswers.trim() || voiceUserWordCount < 8) {
          // Reset voice mode — returns the user to the topic picker without any loading state.
          setVoiceAutoStart(false);
          setStVoiceInterviewMode(false);
          setStSelectedPrompt(null);
          return;
        }

        // Hand off to the shared write-up pipeline. generateStInterviewStory renders its
        // "generating" then "review" screens inside the stInterviewMode branch, so we must
        // switch INTO interview mode (and out of voice mode) before it runs — otherwise the
        // chapter is generated and cached but the UI falls through to the question picker.
        setStInterviewMode(true);
        setStInterviewStep('generating');
        setVoiceAutoStart(false);
        setStVoiceInterviewMode(false);
        // Pass the full conversation + a follow-up count so the assembler path runs
        // (sees the interviewer's questions, ignores noise). Lands on the review/save screen.
        // forceFirstPerson=true: the voice interview is always the storyteller speaking as
        // themselves. The gift-book flag must NOT flip the assembler to third-person here.
        // REVEAL-002 R2 (UNIFIED-FINISH): a voice finish saves NOTHING — it creates a draft and lands
        // State 0, exactly like typed. Pass the conversation id (marks a voice finish; persisted on the
        // draft and, at the later deliberate Save, on the LegacyEntry) and the trigger
        // ('spoken'|'timer'|'button') for story_ready_viewed. autoSave is retired.
        await generateStInterviewStory(userAnswers, null, turns, Math.max(0, userTurns.length - 1), true, false, {
          voiceConversationId: conversationId || null,
          trigger: opts.trigger || (opts.viaDoneSignal ? 'spoken' : 'button'),
        });
      };

      // "I'd rather type" handler: exit voice mode and enter the text conversation
      // with the same prompt, preserving all interview engine state.
      const handleSwitchToText = () => {
        setVoiceAutoStart(false);
        setStVoiceInterviewMode(false);
        // startStInterview seeds the conversation thread with the opener and sets
        // stInterviewMode=true + stInterviewStep='conversation'.
        startStInterview(stSelectedPrompt);
      };

      // onCancel: called by VoiceInterview when the session has nothing to write up
      // (empty or genuinely too thin after a drop), or when the user confirms exit.
      // Resets voice mode and returns to the topic picker with no loading state shown.
      const handleVoiceCancel = () => {
        // F-87 (D2): a SEEDLESS entry (no chosen prompt) has no picker to fall back to — Cancel/leave
        // returns to the DOORWAY, not the easy-start prompt cards the user never chose. A Path room
        // returns to Path. Topic-picked entries still fall through to the questions.
        const wasSeedless = !stSelectedPrompt;
        setVoiceAutoStart(false);
        setStVoiceInterviewMode(false);
        setStSelectedPrompt(null);
        if (backFromPathQuestions()) return;
        if (wasSeedless) setActiveTab('talk');
      };

      return (
        <div className="storyteller-content">
          <VoiceInterview
            selectedPrompt={stSelectedPrompt}
            autoStart={voiceAutoStart}
            // F-83: a SEEDLESS entry (no chosen prompt) has no picker to go "Back to questions" to —
            // its idle Back is relabeled "Back" and returns to the DOORWAY (ratified journey D2).
            seedless={!stSelectedPrompt || returnToPath}
            onDone={handleVoiceInterviewDone}
            onBack={() => {
              const wasSeedless = !stSelectedPrompt;
              setVoiceAutoStart(false);
              setStVoiceInterviewMode(false);
              setStSelectedPrompt(null);
              if (backFromPathQuestions()) return;
              if (wasSeedless) {
                // Return to StTalkHome. Voice off + null prompt already clears the seedless voice
                // state; we do NOT null activePackId (leaving it 'easy-start' is inert on the doorway,
                // and '' would break a subsequent "Pick a topic" pack lookup). (A topic-picked entry
                // instead falls through here: voice off + null prompt -> the prompt cards, unchanged.)
                setActiveTab('talk');
              }
            }}
            onCancel={handleVoiceCancel}
            onSwitchToText={handleSwitchToText}
            isGiftBook={isGiftBook}
            recipientName={(activeBook?.recipientName || '').trim()}
            // F-58: thread the occasion so a memorial (IN_MEMORY) book greets "in memory of"
            // instead of "something special for". Same derivation the question-picker uses
            // for occasionId further down this file.
            occasionId={isGiftBook ? (activeBook?.occasion || '') : ''}
            // TB-134 pack-wiring fix: the chosen topic pack, threaded through to the
            // ElevenLabs session so the voice adapter's engine-driven tone/move selection
            // matches the actual topic instead of always defaulting to the romance pack.
            // Mirrors the same `activePackId || ''` the text-interview path already uses
            // for its own pack-aware tone lookup (see "Pack-aware tone lookup" above).
            pack={activePackId || ''}
            savedStoryCount={stStoryList?.length || 0}
          />
        </div>
      );
    }

    // Gateway "Add a Story" 3-option chooser removed 2026-05-15. Direct routing now:
    //   - Book detail "Add a Story" button → straight into Tell-My-Own-Story flow (below)
    //   - Book detail "Interview a Loved One" button → routes to memory tab (MemoryInterviewPage)
    // stShowGateway state is preserved (always false) to avoid breaking older code paths
    // that read it; can be removed in a future cleanup pass.

    // ── AI INTERVIEW MODE ──
    if (stInterviewMode) {
      // ── TB-104 Stage 2b: HOT SEAT RAPID-FIRE SCREEN ──
      // Presented one prompt at a time. No AI round-trips between prompts.
      // Answers are stored in rapidAnswers (keyed by prompt index) and assembled
      // as a batch when the user taps "Done". Does NOT touch the chat step.
      if (stInterviewStep === 'rapid-fire') {
        const rfPack = PROMPT_PACKS.find(p => p.id === 'hot-seat') || PROMPT_PACKS[0]; // TB-104 Stage 2b
        const rfRecipientName = (activeBook?.recipientName || '').trim(); // TB-104 Stage 2b
        // Relationship-aware vantage for Rapid Fire.
        // Only 'self' vantage (recipient writing their own book) uses first-person prompts.
        // Partner contributors on a Celebration book write ABOUT the recipient (third person). TB-108.
        const rfRawRelationship = activeBook?.authorRelationship || '';
        const rfAuthorVantage = isGiftBook ? getAuthorVantage(rfRawRelationship) : 'self';
        const rfUseFirstPerson = !isGiftBook || rfAuthorVantage === 'self';
        // Build the ordered prompt list honoring gift/celebration framing
        const rfPrompts = (isGiftBook && !rfUseFirstPerson ? rfPack.prompts.filter(p => p.celebration) : rfPack.prompts) // TB-104 Stage 2b
          .map(p => ({
            id: p.id,
            text: isGiftBook && !rfUseFirstPerson
              ? (p.celebration || p.self).replace(/\[Name\]/g, rfRecipientName || 'them')
              : p.self,
          }));
        const rfTotal = rfPrompts.length; // TB-104 Stage 2b
        const rfPrompt = rfPrompts[rapidCurrentIdx] || rfPrompts[0]; // TB-104 Stage 2b
        const rfCurrentAnswer = rapidAnswers[rapidCurrentIdx] || ''; // TB-104 Stage 2b
        const rfAnsweredCount = Object.values(rapidAnswers).filter(a => a && a.trim()).length; // TB-104 Stage 2b
        const rfIsLast = rapidCurrentIdx >= rfTotal - 1; // TB-104 Stage 2b

        // Advance to next prompt (or finish if at last)
        const rfHandleNext = () => { // TB-104 Stage 2b
          if (rfIsLast) {
            rfHandleDone();
          } else {
            setRapidCurrentIdx(idx => idx + 1);
          }
        };

        // Skip — advance without saving the current blank
        const rfHandleSkip = () => { // TB-104 Stage 2b
          if (rfIsLast) {
            rfHandleDone();
          } else {
            setRapidCurrentIdx(idx => idx + 1);
          }
        };

        // Collect answered pairs and kick off assembly
        const rfHandleDone = () => { // TB-104 Stage 2b
          const qaPairs = rfPrompts
            .map((p, i) => ({ question: p.text, answer: (rapidAnswers[i] || '').trim() }))
            .filter(pair => pair.answer.length > 0);
          if (qaPairs.length === 0) return; // guard: done button is disabled when 0 answers
          generateHotSeatStory(qaPairs);
        };

        const rfProgress = rfTotal > 0 ? ((rapidCurrentIdx + 1) / rfTotal) * 100 : 0; // TB-104 Stage 3 (FIX 8): use +1 so bar reflects current question not previous

        return (
          <div className="storyteller-content">
            {/* Back affordance — returns to prompt pack question view */}
            <button
              onClick={() => {
                setStInterviewMode(false);
                setStInterviewStep('pick');
                setRapidAnswers({});
                setRapidCurrentIdx(0);
                backFromPathQuestions();
              }}
              className="st-back"
            >
              Back to topics
            </button>

            {/* Context banner */}
            {activeBook && (
              <div style={{ padding: '10px 16px', background: isGiftBook ? 'rgba(201,168,76,0.12)' : 'rgba(15,23,42,0.04)', border: `1px solid ${isGiftBook ? 'rgba(201,168,76,0.25)' : 'rgba(15,23,42,0.1)'}`, borderRadius: 10, marginBottom: 16, fontSize: '0.92rem', color: '#3c4653' }}>
                Adding a story to: <strong style={{ color: '#0f172a' }}>{activeBook.title || 'your book'}</strong>
              </div>
            )}

            {/* Header + counter */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.3rem' }}>⚡</span>
                <span style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}>Hot Seat</span>
              </div>
              <span style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653', fontWeight: 600 }}>
                {rapidCurrentIdx + 1} of {rfTotal}
              </span>
            </div>

            {/* Progress bar */}
            <div style={{ height: 6, background: 'rgba(15,23,42,0.08)', borderRadius: 8, marginBottom: 28, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${rfProgress}%`, background: '#0f172a', borderRadius: 8, transition: 'width 0.25s ease' }} />
            </div>

            {/* Prompt text */}
            <div style={{ textAlign: 'center', marginBottom: 24, padding: '24px 20px', background: 'rgba(154,52,18,0.05)', border: '1.5px solid rgba(154,52,18,0.14)', borderRadius: 16 }}>
              <div style={{ fontSize: '1.45rem', color: '#0f172a', lineHeight: 1.45, fontFamily: "'Lora', serif", fontStyle: 'italic', fontWeight: 600 }}>
                {rfPrompt.text}
              </div>
            </div>

            {/* Answer textarea — compact, no AI between prompts */}
            <textarea
              key={rapidCurrentIdx}
              className="st-input st-textarea"
              rows={4}
              placeholder="Your answer..."
              value={rfCurrentAnswer}
              onChange={e => setRapidAnswers(prev => ({ ...prev, [rapidCurrentIdx]: e.target.value }))}
              style={{ marginBottom: 8 }}
              autoFocus
            />

            {/* Next / Skip row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <button
                onClick={rfHandleNext}
                disabled={!rfCurrentAnswer.trim()}
                className="st-btn-primary"
                style={{ flex: 1, opacity: rfCurrentAnswer.trim() ? 1 : 0.45 }}
              >
                {rfIsLast ? 'Finish' : 'Next'}
              </button>
              <button
                onClick={rfHandleSkip}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', color: '#3c4653', fontFamily: "'Lora', serif", padding: '8px 4px', whiteSpace: 'nowrap' }}
              >
                Skip
              </button>
            </div>

            {/* Done early — visible once at least 1 answer exists */}
            {rfAnsweredCount > 0 && (
              <div style={{ textAlign: 'center', borderTop: '1px solid rgba(15,23,42,0.07)', paddingTop: 20 }}>
                <button
                  onClick={rfHandleDone}
                  className="st-btn-secondary"
                  style={{ fontSize: '1rem', padding: '12px 28px' }}
                >
                  Done, write it up ({rfAnsweredCount} answered)
                </button>
                <p style={{ fontSize: '0.87rem', color: '#3c4653', marginTop: 8, fontFamily: "'Lora', serif" }}>
                  You can stop here and we will turn what you have into a chapter.
                </p>
              </div>
            )}
          </div>
        );
      }

      // Generating final story — two honest phases (F-415-STREAM).
      // Phase 1 (no streamed text yet — the assembler is running, no streamable token exists):
      // the original Pip + spinner screen, byte-identical copy.
      // Phase 2 (the Whole's text is arriving): the story renders as it is written — REAL
      // accumulated model output per poll, paragraphs appearing, nothing simulated and nothing
      // smoothed (STREAM-PREVIEW-NEVER-A-STORY-1). All wording on both phases is the existing
      // word-gated copy; the phase-2 wording candidates are the founder's call, not shipped here.
      if (stInterviewStep === 'generating') {
        const preview = formatStreamPreview(stStreamPreview);
        // Founder walk feedback 2026-08-10 (both rounds): the strip is PINNED so the state never
        // scrolls away, and its word count is REAL data — phase 1 counts the draft being assembled
        // (the 18s that used to sit silent), phase 2 counts the story streaming in. A percent bar
        // is deliberately absent: the final length isn't known mid-stream, so any bar would be the
        // F-334 fake-progress shape. The count restarting at the phase switch is honest (a new
        // text begins); its labeling is the founder's wording call (DECISION 1).
        const streaming = preview.paragraphs.length > 0;
        const stripCount = streaming
          ? stStreamPreview.trim().split(/\s+/).filter(Boolean).length
          : (stDraftWords || 0);
        // FOUNDER WALK 2026-08-10, round 3, verbatim: *"having 2 pips here is redundant. I would
        // just have the word counter top center, larger, counting up."*
        //
        // PHASE 1 therefore shows THE COUNT ALONE. The 132px Pip, the "Writing your story..."
        // heading and the spinner all live in the body directly below, so the strip was repeating
        // every one of them — that triple is the redundancy he saw, not the Pip by itself.
        //
        // PHASE 2 IS DELIBERATELY UNTOUCHED. Its body is the streaming card, which carries no Pip,
        // no heading and no spinner of its own, so nothing in the strip is a duplicate there and
        // stripping it would leave the teller watching text arrive with no sign of who is writing
        // it. He walked phase 1; this applies his ruling exactly where it was made.
        const strip = stripCount > 0 && (
          <div data-testid="stream-strip" style={{ position: 'sticky', top: 0, zIndex: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: streaming ? 14 : 9, padding: streaming ? '12px 16px' : '15px 16px', margin: '0 -8px', background: 'rgba(251,246,236,0.96)', backdropFilter: 'blur(3px)', borderBottom: '1px solid #eadfc7' }}>
            {streaming && (
              <>
                <img src={pipPoised} alt="" style={{ width: 56, height: 56, objectFit: 'contain', animation: 'pipWriteBob 2.4s ease-in-out infinite' }} />
                <span style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: '1.25rem', color: '#2a2520' }}>Writing your story...</span>
              </>
            )}
            {/* The number is REAL DATA on every tick and is never tweened between polls: an
                in-between figure would be a count no draft ever had, which is the F-334
                fake-progress shape this screen exists to avoid. §3 — it carries no status hue,
                so it reads with colour removed. */}
            <span data-testid="stream-wordcount" style={{ fontFamily: "'Lora', serif", color: streaming ? '#7a6b5a' : '#2a2520', fontSize: streaming ? '1rem' : '1.05rem', display: 'inline-flex', alignItems: 'baseline', gap: 7 }}>
              <span style={{ fontWeight: 700, fontSize: streaming ? '1rem' : '2.1rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{stripCount}</span>
              {stripCount === 1 ? 'word' : 'words'}
            </span>
            {streaming && <div style={{ width: 20, height: 20, border: '2.5px solid rgba(15,23,42,0.18)', borderTopColor: 'rgba(15,23,42,0.45)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />}
          </div>
        );
        if (streaming) {
          return (
            <div className="storyteller-content" style={{ paddingTop: 0 }}>
              <style>{`@keyframes pipWriteBob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }`}</style>
              {strip}
              <StreamCard preview={preview} />
            </div>
          );
        }
        return (
          <div className="storyteller-content" style={{ paddingTop: 0 }}>
            <style>{`@keyframes pipWriteBob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }`}</style>
            {strip}
            <div style={{ textAlign: 'center', paddingTop: strip ? 36 : 60 }}>
              <img src={pipPoised} alt="Pip" style={{ width: 132, height: 132, objectFit: 'contain', filter: 'drop-shadow(0 10px 16px rgba(120,70,20,0.2))', animation: 'pipWriteBob 2.4s ease-in-out infinite', marginBottom: 8 }} />
              <h2 className="st-h2" style={{ fontSize: '1.8rem' }}>Writing your story...</h2>
              {/* F-334 word-gated. Was "Turning your answer into a chapter. This takes about 15 seconds."
                  CUST-05's own blocking path was 33.3s and the assembler leg alone exceeds 15s on ~20% of
                  generations, so the old promise was untrue on the day it was measured. "Under a minute"
                  is floor-based (F6, accepted): worst observed leg sum ~55s, wall clock can exceed it. */}
              <p className="st-sub" style={{ marginTop: 12 }}>Turning your answers into a chapter. This usually takes under a minute.</p>
              <div style={{ width: 48, height: 48, border: '3px solid rgba(15,23,42,0.25)', borderTopColor: 'rgba(15,23,42,0.3)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '40px auto' }} />
            </div>
          </div>
        );
      }

      // Review generated story
      if (stInterviewStep === 'review' && stProcessed.content) {
        // PIPE-SAVE-1 F-3: after an auto-save we stay HERE (not a generic celebration) in a saved
        // state — badge instead of the "not saved" banner + Save button, plus a low-prominence Remove.
        // Everything else on this screen is unchanged. Decision lives in getReviewMode (unit-tested).
        const reviewMode = getReviewMode(stProcessed);

        // ── REVEAL-001 C4 · State 3 (kept) ─────────────────────────────────
        // "Keep it" lands here: a small ceremony (the chapter count grows, the book appears) and three
        // safe exits — so the session has an ending and nobody has to invent their own way out.
        if (reviewMode.saved && revealKept) {
          const keptIndex = (stBookPages || []).findIndex(p => p.storyId === stProcessed.savedStoryId);
          const keptNumber = keptIndex >= 0 ? keptIndex + 1 : (stBookPages?.length || 1);
          const chapterCount = stBookPages?.length || 1;
          const started = activeBook?.createdAt ? new Date(activeBook.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '';
          const nextQuestion = (stAiQuestion && stAiQuestion.text) || 'What is another moment you would want remembered?';
          const keptGhost = { fontFamily: "'Lora', serif", fontSize: 14.5, background: 'transparent', color: '#A85A0F', border: '1px solid #B8813E', padding: '12px 26px', borderRadius: 10, minHeight: 44, cursor: 'pointer' };
          const tellAnother = () => {
            // F-73: land on the book-aware QUESTION PICKER (the prompts that spark the next story), never a
            // half-initialized answer screen. Exiting interview mode is the same canonical clean-exit F-67
            // used for Discard: own book → My Book's picker with Pip; Celebration Book → that book's picker.
            // (setStInterviewMode(true) + step 'pick' + a null prompt fell through to the answer/record
            // screen — "A question about {recipient}:" with no question on a Celebration Book.)
            setRevealKept(false); setStProcessed({ title: '', content: '' }); setStSelectedPrompt(null);
            setStInterviewInput('');
            setStInterviewMode(false); setStInterviewStep('pick');
          };
          return (
            <div className="storyteller-content" data-testid="reveal-kept-root">
              <div data-testid="reveal-kept-ribbon" role="status" style={{ display: 'block', textAlign: 'center', padding: '11px', background: '#e8f3ec', border: '1px solid #d5e6da', borderRadius: 12, marginBottom: 14, fontSize: '0.92rem', color: '#217a3e', fontFamily: "'Lora', serif" }}>
                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#217a3e', marginRight: 7, verticalAlign: 1 }} />
                Saved to <strong style={{ fontWeight: 600 }}>{activeBook?.title || 'your book'}</strong>
              </div>

              <div style={{ textAlign: 'center', padding: '30px 0 8px' }}>
                <img src={pipHoldingBook} alt="" style={{ width: 96, height: 96, objectFit: 'contain' }} />
                <h2 data-testid="reveal-kept-heading" style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: '1.5rem', marginTop: 12, color: '#2a2520' }}>
                  Chapter {String(chapterWord(keptNumber)).toLowerCase()}, kept.
                </h2>
                <p style={{ color: '#7a6b5a', fontSize: '1rem', marginTop: 8, fontFamily: "'Lora', serif", maxWidth: '40ch', marginLeft: 'auto', marginRight: 'auto' }}>
                  "{stProcessed.title}" is now part of your book. You can edit it there anytime.
                </p>
              </div>

              {/* the book — every save visibly grows an object worth returning to; the REAL chosen cover */}
              <div data-testid="reveal-book-card" style={{ margin: '24px auto 0', maxWidth: 420, background: '#FFFDF8', border: '1px solid #eadfc7', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 4px 14px rgba(60,47,30,.07)' }}>
                <CoverThumbnail coverDesign={activeBook?.coverDesign} alt="" />
                <div>
                  <strong style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: '1rem', display: 'block', color: '#2a2520' }}>{activeBook?.title || 'My Stories'}</strong>
                  <small style={{ fontSize: '0.85rem', color: '#6b6154' }}>{chapterCount} {chapterCount === 1 ? 'chapter' : 'chapters'}{started ? ` · started ${started}` : ''}</small>
                </div>
              </div>

              {/* ONE primary: Tell another story, staged with Pip's actual next question */}
              <div style={{ marginTop: 30, textAlign: 'center' }}>
                <button data-testid="reveal-tell-another" onClick={tellAnother} style={{ fontFamily: "'Lora', serif", fontSize: 16, background: 'linear-gradient(180deg,#AE6015,#A85A0F)', color: '#fff', padding: '15px 42px', borderRadius: 12, border: '1px solid transparent', boxShadow: '0 3px 10px rgba(168,90,15,.28)', minHeight: 48, cursor: 'pointer' }}>Tell another story</button>
                <div data-testid="reveal-next-question" style={{ marginTop: 10, fontSize: 13.5, color: '#6b6154', fontFamily: "'Lora', serif", maxWidth: '46ch', marginLeft: 'auto', marginRight: 'auto' }}>
                  Pip's next question is ready: "{nextQuestion}"
                </div>
              </div>

              <div style={{ marginTop: 8, textAlign: 'center' }}>
                {/* F-236 hygiene: these two exits leave the celebration WITHOUT clearing stProcessed, so the
                    next story used to be generated on top of this chapter's saved state and overwrite it
                    (F-234). SAVE-IDENTITY-1 in reviewState.js is the actual guard — these resets make the
                    intent readable at the call site and keep the object clean. Never rely on them alone:
                    the whole lesson of F-234 is that a rule guarding a PROCESS STEP is forgotten by the
                    next path someone adds, exactly as these two forgot it. */}
                <button data-testid="reveal-see-book" onClick={() => { setRevealKept(false); setStProcessed({ title: '', content: '' }); setStSelectedPrompt(null); setActiveTab('st-book'); }} style={keptGhost}>See your book</button>
              </div>
              <div style={{ marginTop: 10, textAlign: 'center' }}>
                <button data-testid="reveal-done" onClick={() => { setRevealKept(false); setStProcessed({ title: '', content: '' }); setStSelectedPrompt(null); setActiveTab('talk'); }} style={{ background: 'none', border: 'none', color: '#6d5f4e', fontFamily: "'Lora', serif", fontSize: 13, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer', padding: '14px 12px' }}>Done for today. Everything is saved.</button>
              </div>
              {/* F-13: milestone whisper — the gift ask lives at pride peaks ONLY (story_milestone counts 2/5/10, §6b). */}
              {isGiftWhisperMilestone(chapterCount) && openGiftSheet && (
                <div data-testid="milestone-gift-whisper" style={{ marginTop: 12, textAlign: 'center' }}>
                  <span style={{ fontSize: 13, color: '#9a8f7e', fontFamily: "'Lora', serif" }}>
                    Know someone with stories to tell?{' '}
                    <button onClick={() => openGiftSheet('milestone')} style={{ background: 'none', border: 'none', color: '#a06a2c', fontFamily: "'Lora', serif", fontSize: 13, textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer', padding: 0 }}>Give them Talebound</button>
                  </span>
                </div>
              )}
            </div>
          );
        }
        // ── end State 3 ────────────────────────────────────────────────────

        // ── REVEAL-001 C2 · State 1 (arrival) ──────────────────────────────
        // After a save (voice auto-save OR typed manual save), the payoff screen:
        // the chapter is the hero, rendered through the REAL RemnantBookPDF path.
        // Editing swaps to the form below (revealEditing → C3 turns it into State 2).
        if (reviewMode.saved && !revealEditing && !revealKept) {
          const versions = stProcessed.versions;
          const hasBothVersions = !!(versions?.flick && versions?.whole && !stProcessed.isDegradedGeneration);
          const activeVersion = stProcessed.activeVersion || 'whole';
          const hasVoice = !!stProcessed.audioBlob;
          const bookIndex = (stBookPages || []).findIndex(p => p.storyId === stProcessed.savedStoryId);
          const chapterNumber = bookIndex >= 0 ? bookIndex + 1 : undefined;
          const attribution = stProcessed.subtitle || '';
          const qrDataUrl = (hasVoice && revealQrOn && stProcessed.savedStoryId)
            ? (chapterQrCodes && chapterQrCodes[stProcessed.savedStoryId]) || null
            : null;
          const chapter = {
            title: stProcessed.title,
            content: stProcessed.content,
            layout: 'classic',
            qrDataUrl,
            mediaType: stProcessed.videoBlob ? 'video' : 'audio',
          };
          const bookKey = `${stProcessed.savedStoryId}|${activeVersion}|${qrDataUrl ? 'q' : 'n'}`;
          const ghostBtn = {
            fontFamily: "'Lora', serif", fontSize: 14.5, background: 'transparent', color: '#A85A0F',
            border: '1px solid #B8813E', padding: '12px 26px', borderRadius: 10, minHeight: 44, cursor: 'pointer',
          };
          const switchVersion = (key) => {
            if (key === activeVersion || stSaving) return;
            const v = versions && versions[key];
            if (!v) return;
            const next = { ...stProcessed, title: v.title, content: v.content, activeVersion: key };
            setStProcessed(next);
            // Q2: which version PRINTS updates immediately + SILENTLY (no toast/ceremony) — both
            // versions stay saved, no dirty state / savebar. Reading-level choice.
            if (updateSavedInterviewStory) updateSavedInterviewStory(next, { silent: true });
          };
          const handleKeepIt = () => {
            // C4: acceptance → the completion beat (State 3). "Keep it" never locks anything; the chapter
            // stays editable in My Book. Acceptance, not commitment.
            setRevealKept(true);
          };
          const handleTellPipMore = () => {
            // REVEAL-001 C5: reopen the interview for THIS saved chapter with the polished chapter as
            // context. Forward-only — additions are stitched onto the same entry (no duplicate). The
            // reopen fn lives in App.js (it re-primes the interview state the engine consumes).
            if (reopenStoryForMore) reopenStoryForMore();
          };
          return (
            <div className="storyteller-content" data-testid="reveal-root">
              {/* ribbon — STATUS, never clickable */}
              <div data-testid="reveal-ribbon" role="status" style={{ display: 'block', textAlign: 'center', padding: '11px', background: '#e8f3ec', border: '1px solid #d5e6da', borderRadius: 12, marginBottom: 14, fontSize: '0.92rem', color: '#217a3e', fontFamily: "'Lora', serif" }}>
                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#217a3e', marginRight: 7, verticalAlign: 1 }} />
                Saved to <strong style={{ fontWeight: 600 }}>{activeBook?.title || 'your book'}</strong> · already safe in your book
              </div>

              {/* celebration beat — Pip + one line (per the lock; no gold eyebrow) */}
              <div style={{ textAlign: 'center', padding: '18px 0 6px' }}>
                <img src={pipHoldingBook} alt="" style={{ width: 92, height: 92, objectFit: 'contain' }} />
                <p style={{ color: '#7a6b5a', fontSize: '0.98rem', marginTop: 10, fontFamily: "'Lora', serif" }}>Here's your story, just the way you told it.</p>
              </div>

              {/* version switcher — one decision, immediate silent persist */}
              {hasBothVersions && (
                <div role="group" aria-label="Choose which version you are reading" style={{ display: 'flex', width: '100%', maxWidth: 420, margin: '8px auto 0', border: '1.5px solid #B8813E', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                  {[
                    { key: 'whole', label: 'The Whole Tale', sub: 'beautifully written' },
                    { key: 'flick', label: 'A Flick of the Tail', sub: 'in your own words' },
                  ].map(({ key, label, sub }) => {
                    const on = activeVersion === key;
                    return (
                      <button key={key} data-testid={`reveal-version-${key}`} onClick={() => switchVersion(key)} aria-pressed={on} disabled={stSaving}
                        style={{ flex: 1, padding: '9px 8px', minHeight: 48, background: on ? '#A85A0F' : '#fff', border: 'none', cursor: stSaving ? 'default' : 'pointer', fontFamily: "'Lora', serif" }}>
                        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: on ? '#fff' : '#A85A0F' }}>{label}</span>
                        <span style={{ display: 'block', fontSize: 13, color: on ? '#FBE9D7' : '#6b6154', marginTop: 1 }}>{sub}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* the chapter — the REAL printed page(s) */}
              <div style={{ marginTop: 22 }}>
                <RevealPages
                  chapter={chapter}
                  revealChapterNumber={chapterNumber}
                  chapterTitle={stProcessed.title}
                  attribution={attribution}
                  bookTitle={activeBook?.title || 'My Stories'}
                  author={stBookAuthor || ''}
                  bookKey={bookKey}
                  onPageTurn={() => trackEvent('reveal_page_turned', { channel: hasVoice ? 'voice' : 'typed' })}
                />
              </div>

              {/* REVEAL-002 §6b: NO photo slot, NO QR anywhere on the reveal. Photos + scan-to-play
                  live in the Book Builder (the chapter stays editable there, always). */}

              {/* actions — verbs, visually distinct from status */}
              <div style={{ marginTop: 30, textAlign: 'center' }}>
                <button data-testid="reveal-keep" onClick={handleKeepIt} style={{ fontFamily: "'Lora', serif", fontSize: 16, background: 'linear-gradient(180deg,#AE6015,#A85A0F)', color: '#fff', padding: '15px 42px', borderRadius: 12, border: '1px solid transparent', boxShadow: '0 3px 10px rgba(168,90,15,.28)', minHeight: 48, cursor: 'pointer' }}>Keep it</button>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                  <button data-testid="reveal-edit" onClick={() => { setRevealEditBaseline({ title: stProcessed.title, content: stProcessed.content, activeVersion: stProcessed.activeVersion }); setRevealDraftRestored(false); setRevealEditing(true); }} style={ghostBtn}>Edit</button>
                  {/* REVEAL-001 C5: door hidden behind TELL_PIP_MORE_ENABLED until PIPE-APPEND lands. */}
                  {TELL_PIP_MORE_ENABLED && (
                    <button data-testid="reveal-tellpip" onClick={handleTellPipMore} style={ghostBtn}>Tell Pip more</button>
                  )}
                </div>
                <div data-testid="reveal-microcopy" style={{ marginTop: 10, fontSize: 13, color: '#6b6154', fontFamily: "'Lora', serif" }}>{TELL_PIP_MORE_ENABLED ? 'Edit changes the words or photo. Tell Pip more reopens your interview.' : 'Edit changes the words or photo.'}</div>
              </div>

              {/* Remove — tucked, visible for trust, quiet by hierarchy */}
              <div style={{ marginTop: 26, textAlign: 'center' }}>
                <button data-testid="reveal-remove" onClick={() => removeRevealedStory && removeRevealedStory()} style={{ background: 'none', border: 'none', color: '#9a8570', fontFamily: "'Lora', serif", fontSize: 13, cursor: 'pointer', padding: 14, textDecoration: 'underline', textUnderlineOffset: 3 }}>Remove this story</button>
              </div>
            </div>
          );
        }
        // ── end State 1 ────────────────────────────────────────────────────

        // ── REVEAL-001 C3 · State 2 (edit) ─────────────────────────────────
        // The "Edit" door: Title + Story only; photo + scan-to-play behind ONE "More options" door.
        // Dirty state is loud + honest (amber ribbon + sticky savebar); Discard returns to the saved
        // copy; Save changes → the gated update seam. A pagehide DRAFT (Correction 1) never changes what
        // prints — only Save changes does.
        if (reviewMode.saved && revealEditing) {
          const dirty = !!stProcessed.dirty;
          const hasVoice = !!stProcessed.audioBlob;
          const handleSaveChanges = async () => {
            const ok = await updateSavedInterviewStory();
            if (ok) {
              try { localStorage.removeItem(REVEAL_DRAFT_KEY(stProcessed.savedStoryId)); } catch { /* ignore */ }
              setRevealDraftRestored(false); setRevealEditBaseline(null); setRevealEditing(false);
            }
          };
          const handleDiscard = () => {
            if (revealEditBaseline) {
              setStProcessed(p => ({ ...p, title: revealEditBaseline.title, content: revealEditBaseline.content, activeVersion: revealEditBaseline.activeVersion, dirty: false }));
            }
            try { localStorage.removeItem(REVEAL_DRAFT_KEY(stProcessed.savedStoryId)); } catch { /* ignore */ }
            setRevealDraftRestored(false); setRevealEditBaseline(null); setRevealEditing(false);
          };
          return (
            <div className="storyteller-content" data-testid="reveal-edit-root">
              {/* ribbon — amber + loud when dirty; calm + reassuring otherwise. Never claims a save. */}
              <div data-testid="reveal-edit-ribbon" role="status" style={{ display: 'block', textAlign: 'center', padding: '11px', borderRadius: 12, marginBottom: 14, fontSize: '0.92rem', fontFamily: "'Lora', serif",
                background: dirty ? '#FFF4D6' : '#f7f2e7', border: `1px solid ${dirty ? '#EAD79B' : '#e0d7c4'}`, color: dirty ? '#7A5600' : '#6b6154' }}>
                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: dirty ? '#9A7000' : '#b3a892', marginRight: 7, verticalAlign: 1 }} />
                {dirty ? <>You have <strong style={{ fontWeight: 600 }}>unsaved changes</strong> · your original is still safe</> : <>Editing your chapter · your original is still safe</>}
              </div>

              {revealDraftRestored && (
                <div data-testid="reveal-draft-note" style={{ padding: '10px 14px', background: 'rgba(138,109,58,0.08)', border: '1px solid rgba(138,109,58,0.2)', borderRadius: 10, marginBottom: 14, fontSize: '0.9rem', color: '#6b5a3a', fontFamily: "'Lora', serif" }}>
                  We kept your unsaved edits from last time. Save them to your book, or discard to go back.
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label className="st-label">Title</label>
                <input data-testid="reveal-edit-title" className="st-input" value={stProcessed.title || ''}
                  onChange={e => setStProcessed(p => ({ ...p, title: e.target.value, dirty: true }))} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label className="st-label">Story</label>
                <textarea data-testid="reveal-edit-story" className="st-input st-textarea" rows={14} value={stProcessed.content || ''}
                  onChange={e => setStProcessed(p => ({ ...p, content: e.target.value, dirty: true }))} style={{ minHeight: 300 }} />
              </div>

              {/* REVEAL-002 §6b: reveal edit mode is Title + Story and NOTHING else. Photos + scan-to-play
                  move to the Book Builder (every chapter stays editable there, always). */}

              {/* sticky savebar — appears on any change; the ONE primary action */}
              {dirty && (
                <div data-testid="reveal-savebar" style={{ position: 'sticky', bottom: 0, marginTop: 26, background: '#FFFBEF', border: '1px solid #EAD79B', borderRadius: 12, padding: '13px 16px', paddingBottom: 'calc(13px + env(safe-area-inset-bottom))', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', boxShadow: '0 -6px 24px rgba(60,47,30,.10)' }}>
                  <span style={{ fontSize: '0.9rem', color: '#7A5600', fontFamily: "'Lora', serif" }}><strong>Edited.</strong> Save to make this the version in your book.</span>
                  <span style={{ flex: 1 }} />
                  <button data-testid="reveal-discard" onClick={handleDiscard} disabled={stSaving} style={{ background: 'none', border: 'none', color: '#6d5f4e', textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer', fontSize: '0.88rem', padding: '15px 10px', fontFamily: "'Lora', serif" }}>Discard changes</button>
                  <button data-testid="reveal-save-changes" onClick={handleSaveChanges} disabled={stSaving} className="st-btn-primary" style={{ padding: '11px 26px', fontSize: '0.95rem', whiteSpace: 'nowrap', opacity: stSaving ? 0.6 : 1 }}>{stSaving ? 'Saving…' : 'Save changes'}</button>
                </div>
              )}
              {!dirty && (
                <div style={{ marginTop: 22, textAlign: 'center' }}>
                  <button data-testid="reveal-discard" onClick={handleDiscard} style={{ background: 'none', border: '1px solid #B8813E', color: '#A85A0F', borderRadius: 10, padding: '12px 26px', minHeight: 44, cursor: 'pointer', fontFamily: "'Lora', serif" }}>Back to your story</button>
                </div>
              )}
            </div>
          );
        }
        // ── end State 2 ────────────────────────────────────────────────────

        // ── REVEAL-002 R1 · State 0 (ready, NOT saved) ─────────────────────
        // UNIFIED-FINISH: a finish saves NOTHING. The story lives as a server-side draft; this AMBER
        // landing is where Save / Edit / Discard live. §6b: no photo, no QR. draftId gates this branch
        // (set only for the typed-interview draft flow + resumes). Save → the gated seam → State 3.
        if (!reviewMode.saved && stProcessed.draftId && !revealEditing) {
          const versions = stProcessed.versions;
          const hasBothVersions = !!(versions?.flick && versions?.whole && !stProcessed.isDegradedGeneration);
          const activeVersion = stProcessed.activeVersion || 'whole';
          const resumed = !!stProcessed.resumed;
          const chapter = { title: stProcessed.title, content: stProcessed.content, layout: 'classic', qrDataUrl: null };
          const bookKey = `draft-${stProcessed.draftId}|${activeVersion}`;
          const ghostBtn = { fontFamily: "'Lora', serif", fontSize: 14.5, background: 'transparent', color: '#A85A0F', border: '1px solid #B8813E', padding: '12px 26px', borderRadius: 10, minHeight: 44, cursor: 'pointer' };
          const switchVersion = (key) => {
            if (key === activeVersion || stSaving) return;
            const v = versions && versions[key];
            if (!v) return;
            setStProcessed({ ...stProcessed, title: v.title, content: v.content, activeVersion: key });
            // pre-save: persist the reading choice to the DRAFT row (survives resume); never the book.
            if (updateStoryDraftEdits) updateStoryDraftEdits({ activeVersion: key, title: v.title });
          };
          const handleSaveToBook = async () => {
            // F-72 (flash b): land straight on State 3 (kept) with NO State-1 flash between. Two seams caused
            // the saved-revisit screen ("Keep it / Remove this story / Saved to your book.") to flash: (1)
            // setRevealKept ran only AFTER the await, so the saved flip landed on State 1 for a tick; and (2)
            // the revealStoryRef reset effect zeroes revealKept when the new savedStoryId appears. Set kept
            // synchronously AND mark the keep pending so that reset skips this story. Revert only on failure
            // (on success the reset effect consumes the pending flag; on failure savedStoryId never changes,
            // so clear it here).
            pendingKeepRef.current = true;
            setRevealKept(true);
            const ok = await saveStInterviewStory(stProcessed, { fromReveal: true });
            if (!ok) { pendingKeepRef.current = false; setRevealKept(false); }
          };
          // F-72 (flash a): the veil must exist in the SAME render commit that mounts the arrival — never be
          // added by an effect AFTER the content has already painted. Derive its presence synchronously:
          // veilPhase==='veil' once effect (1) confirms it, OR (undecided first commit, veilPhase===null) a
          // fresh finish-arrival that WILL veil. This closes the one-frame un-veiled State 0 (showing the amber
          // ribbon + "Preparing your pages…") that flashed before effect (1) ran. Resume / already-played /
          // reduced-motion are excluded, so they never flash a veil either.
          const reducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const veiling = veilPhase === 'veil'
            || (veilPhase === null && !stProcessed.resumed && !stProcessed.landingPlayedAt && !reducedMotion);
          return (
            <div className="storyteller-content" data-testid="reveal-ready-root">
              <style>{`
                /* Backdrop is FULLY OPAQUE from the first painted frame (F-62) — nothing beneath is ever
                   visible during arrival. Only .reveal-veil-content fades/rises in. */
                .reveal-veil { position: fixed; inset: 0; z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 32px; text-align: center; cursor: pointer; background: radial-gradient(120% 100% at 50% 32%, #FBF6EC 0%, #F1E4CB 100%); opacity: 1; }
                .reveal-veil-content { display: flex; flex-direction: column; align-items: center; gap: 16px; animation: revealVeilContentIn 0.6s ease-out both; }
                .reveal-veil-pip { width: 120px; height: 120px; object-fit: contain; filter: drop-shadow(0 10px 16px rgba(120,70,20,0.2)); animation: pipWriteBob 2.4s ease-in-out infinite; }
                .reveal-veil-note { font-family: 'Lora', serif; font-size: 15px; color: #8a7b62; margin: 0; }
                .reveal-veil-line { font-family: 'Lora', serif; font-style: italic; font-size: clamp(24px, 6vw, 34px); line-height: 1.35; color: #6d5f4e; max-width: 18ch; margin: 6px 0 0; }
                .reveal-veil-skip { margin-top: 8px; background: none; border: none; color: #9a8570; font-family: 'Lora', serif; font-size: 12.5px; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; min-height: 44px; }
                .reveal-arriving { animation: revealPageSettle 0.8s cubic-bezier(0.2,0.7,0.2,1) both; }
                .reveal-arriving .reveal-chrome { animation: revealChromeRise 0.6s ease-out both; animation-delay: 0.45s; }
                /* F-415-STREAM: the streamed arrival skips the veil; this soft fade replaces the
                   hard cut the founder called a "screen flash" (walk feedback 2026-08-10). */
                .reveal-stream-fadein { animation: revealStreamFade 0.55s ease-out both; }
                /* The pinned Save+pager dock. FIXED via portal (sticky dies under .main-content's
                   overflow-x:hidden), aligned to the CONTENT SPAN at each real layout: mobile spans
                   the viewport above the fixed nav; with the sidebar (200px at 641-1024, 280px
                   above) the span starts past it — mirrors .main-content's own margins, so the dock
                   can never band across the sidebar (the desktop bug). */
                .reveal-dock { position: fixed; left: 0; right: 0; bottom: calc(64px + env(safe-area-inset-bottom, 0px)); z-index: 150; padding: 0; pointer-events: none; }
                .reveal-dock-inner { pointer-events: auto; background: #FBF8F1; border-top: 1px solid #eadfc7; box-shadow: 0 -6px 18px rgba(60,47,30,.10); padding: 10px 16px 8px; }
                @media (min-width: 641px) and (max-width: 1024px) { .reveal-dock { left: 200px; bottom: 0; } }
                @media (min-width: 1025px) { .reveal-dock { left: 280px; bottom: 0; } }
                /* ONE band at every width — the founder-approved mobile shape, now the desktop
                   shape too. The sidebar offsets above keep it inside the content column. */
                @keyframes revealStreamFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
                @keyframes revealVeilContentIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
                @keyframes revealPageSettle { from { opacity: 0; transform: translateY(-10px) scale(0.985); } to { opacity: 1; transform: none; } }
                @keyframes revealChromeRise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
                @media (prefers-reduced-motion: reduce) { .reveal-veil-content, .reveal-arriving, .reveal-arriving .reveal-chrome, .reveal-stream-fadein { animation: none !important; } }
              `}</style>

              {veiling && createPortal(
                // Portal to <body> so the veil escapes every stacking context and truly covers the app
                // chrome (top bar + bottom nav) — otherwise "tap anywhere to skip" would be a lie (a tap on
                // a still-visible nav item would navigate instead of skipping).
                <div data-testid="reveal-landing-veil" className="reveal-veil" role="dialog" aria-modal="true" aria-label="Your story is arriving" tabIndex={-1} ref={veilRef}
                  onClick={skipVeil} onKeyDown={() => skipVeil()}>
                  <div className="reveal-veil-content">
                    <img src={pipPoised} alt="" className="reveal-veil-pip" />
                    <p className="reveal-veil-note" aria-live="polite">Pip is finishing your pages…</p>
                    {stProcessed.tellerFirstLine && <p className="reveal-veil-line" data-testid="reveal-veil-line">{stProcessed.tellerFirstLine}</p>}
                    <button type="button" className="reveal-veil-skip" data-testid="reveal-veil-skip">tap anywhere to skip</button>
                  </div>
                </div>,
                document.body
              )}

              {/* F-415-STREAM walk round 2: NO whole-body fade on a streamed arrival — it read as
                  "another transition". Continuity lives in the story card itself (loadingFallback). */}
              {/* Paged: the stage already ends above the dock, so no clearance padding is owed and
                  nothing scrolls. Scroll-card fallback still needs to clear the fixed dock. */}
              <div className={`reveal-ready-body${veilPhase === 'arriving' ? ' reveal-arriving' : ''}`} aria-hidden={veiling} style={{ paddingBottom: paged ? 8 : 'calc(215px + env(safe-area-inset-bottom))', ...(veiling ? { visibility: 'hidden' } : {}) }}>
              {/* amber ribbon — STATUS lane, never clickable (truth: not saved yet) */}
              <div data-testid="reveal-ready-ribbon" role="status" style={{ display: 'block', textAlign: 'center', padding: '8px 11px', background: '#FFF4D6', border: '1px solid #EAD79B', borderRadius: 12, marginBottom: 8, fontSize: '0.88rem', color: '#7A5600', fontFamily: "'Lora', serif" }}>
                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#9A7000', marginRight: 7, verticalAlign: 1 }} />
                {resumed ? <>Your story is still here · <strong style={{ fontWeight: 600 }}>not saved to your book yet</strong></> : <>Your story is ready · <strong style={{ fontWeight: 600 }}>not saved to your book yet</strong></>}
              </div>

              {/* FOUNDER RULING 2026-08-10: Pip and the "here's your story, just the way you told
                  it." beat are REMOVED from the reveal. The height they held goes to the page.
                  The F-60 protection they carried (never render the login handle at a teller) does
                  NOT lapse — the design lock now asserts the handle appears NOWHERE on this screen,
                  which is stricter than checking one line for it. `beatName` stays resolved above
                  for the other surfaces that address the teller by name. */}

              {hasBothVersions && (
                <div role="group" aria-label="Choose which version you are reading" style={{ display: 'flex', width: '100%', maxWidth: 420, margin: '8px auto 0', border: '1.5px solid #B8813E', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                  {[{ key: 'whole', label: 'The Whole Tale', sub: 'beautifully written' }, { key: 'flick', label: 'A Flick of the Tail', sub: 'in your own words' }].map(({ key, label, sub }) => {
                    const on = activeVersion === key;
                    return (
                      <button key={key} data-testid={`reveal-version-${key}`} onClick={() => switchVersion(key)} aria-pressed={on} disabled={stSaving} style={{ flex: 1, padding: '7px 8px', minHeight: 46, background: on ? '#A85A0F' : '#fff', border: 'none', cursor: stSaving ? 'default' : 'pointer', fontFamily: "'Lora', serif" }}>
                        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: on ? '#fff' : '#A85A0F' }}>{label}</span>
                        <span style={{ display: 'block', fontSize: 13, color: on ? '#FBE9D7' : '#6b6154', marginTop: 1 }}>{sub}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* THE STAGE (founder ruling 2026-08-10, option A). Its height is measured, not
                  guessed: from its own top to the top of the pinned dock. That adapts to whatever
                  chrome exists at any breakpoint — mobile header + bottom nav, desktop sidebar —
                  without this file knowing any of their sizes. RevealPages paginates to exactly
                  that, so the page never scrolls and the dock never covers a word. Too short a
                  stage (a phone in landscape) simply falls back to the scrolling card. */}
              <div ref={stageRef} style={{ marginTop: 12, ...(paged ? { height: stageHeight, minHeight: 0, display: 'flex' } : null) }}>
                <RevealPages chapter={chapter} chapterTitle={stProcessed.title} attribution={stProcessed.subtitle || ''} bookTitle={activeBook?.title || 'My Stories'} author={stBookAuthor || ''} bookKey={bookKey} onReady={() => setPagesReady(true)} onPageTurn={() => trackEvent('reveal_page_turned', { channel: 'typed' })}
                  // F-415-STREAM (walk round 2): on a streamed arrival the SAME story card the teller
                  // was just reading stands in while the typeset pages prepare — no "Preparing your
                  // pages" flash between the stream and the reveal.
                  loadingFallback={stProcessed.streamed && stStreamPreview ? <StreamCard preview={formatStreamPreview(stStreamPreview)} /> : undefined}
                  // Walk round 2, item 7: paging lives in the pinned dock; the inline pager is off.
                  // `expandable`: on a phone the inline page can only hold ~51 words, so tapping it
                  // opens the full-screen reader (founder idea 2026-08-10) where it holds ~165.
                  hidePager onPagerState={setRevealPager} fitHeight={stageHeight} expandable />
              </div>

              {/* Founder walk feedback 2026-08-10 round 2 (items 6+7, the desktop bug, and the
                  option-A ruling): SAVING IS THE CALL TO ACTION, paging lives with it, and in a
                  no-scroll composition every control must be reachable without scrolling — so
                  Edit and Discard come here too, deliberately quieter than Save. Portalled and
                  FIXED: sticky-in-flow was tried and dies under .main-content's overflow-x:hidden.
                  Left-offset per breakpoint mirrors .main-content so it can never band across the
                  desktop sidebar. */}
              {!veiling && createPortal(
              <div data-testid="reveal-save-dock" className="reveal-dock" ref={dockRef}><div className="reveal-dock-inner">
                {(() => { const pgr = revealPager || { page: 1, total: 1, go: () => {} }; const many = pgr.total > 1; return (
                  <div data-testid="reveal-pager" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 6, color: '#6b6154', fontFamily: "'Lora', serif", fontSize: 13, visibility: many ? 'visible' : 'hidden' }}>
                    <button onClick={() => pgr.go(-1)} disabled={pgr.page <= 1} aria-label="Previous page"
                      style={{ border: '1px solid #B8813E', background: '#FFFDF8', width: 44, height: 44, borderRadius: '50%', cursor: pgr.page <= 1 ? 'default' : 'pointer', fontSize: 16, color: '#A85A0F', opacity: pgr.page <= 1 ? 0.35 : 1 }}>‹</button>
                    <span style={{ textAlign: 'center', minWidth: 84 }}>Page {pgr.page} of {pgr.total}</span>
                    <button onClick={() => pgr.go(1)} disabled={pgr.page >= pgr.total} aria-label="Next page"
                      style={{ border: '1px solid #B8813E', background: '#FFFDF8', width: 44, height: 44, borderRadius: '50%', cursor: pgr.page >= pgr.total ? 'default' : 'pointer', fontSize: 16, color: '#A85A0F', opacity: pgr.page >= pgr.total ? 0.35 : 1 }}>›</button>
                  </div>
                ); })()}
                <button data-testid="reveal-save" onClick={handleSaveToBook} disabled={stSaving}
                  style={{ display: 'block', width: '100%', maxWidth: 420, margin: '0 auto', fontFamily: "'Lora', serif", fontSize: 16, background: 'linear-gradient(180deg,#AE6015,#A85A0F)', color: '#fff', padding: '13px 42px', borderRadius: 12, border: '1px solid transparent', boxShadow: '0 3px 10px rgba(168,90,15,.28)', minHeight: 48, cursor: 'pointer', opacity: stSaving ? 0.6 : 1 }}>
                  {stSaving ? 'Saving…' : 'Save to My Book'}
                </button>
                {/* FOUNDER RULING 2026-08-10: the "One tap keeps it in your book…" reassurance line
                    is REMOVED. It was F-334 word-gated copy, so this is a founder word-gate change,
                    not a quiet edit — and the design lock now pins its ABSENCE so it cannot creep
                    back. The ribbon above still carries the load-bearing truth (not saved yet). */}
                {/* The quiet lane: secondary and danger, side by side, never competing with Save.
                    F-70 keeps Discard a plain text button, not a second filled control. */}
                <div className="reveal-chrome" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, marginTop: 0 }}>
                  <button data-testid="reveal-edit" onClick={() => { setRevealEditBaseline({ title: stProcessed.title, content: stProcessed.content, activeVersion: stProcessed.activeVersion }); setRevealEditing(true); }}
                    style={{ background: 'none', border: 'none', color: '#A85A0F', fontFamily: "'Lora', serif", fontSize: 13.5, cursor: 'pointer', padding: '8px 12px', minHeight: 44, textDecoration: 'underline', textUnderlineOffset: 3 }}>Edit</button>
                  <span aria-hidden="true" style={{ color: '#cbbfa8' }}>·</span>
                  <button data-testid="reveal-discard-unsaved" onClick={() => setRevealDiscardConfirm(true)}
                    style={{ background: 'none', border: 'none', color: '#9a8570', fontFamily: "'Lora', serif", fontSize: 13, cursor: 'pointer', padding: '8px 12px', minHeight: 44, textDecoration: 'underline', textUnderlineOffset: 3 }}>Discard this story</button>
                </div>
              </div></div>,
              document.body
              )}

              {/* §6b: NO photo slot, NO QR here. */}
              </div>

              {revealDiscardConfirm && createPortal(
                // F-71: portal to <body> at the app overlay z-convention (2000, same as the veil) so the
                // backdrop owns the ENTIRE screen — bottom nav included. Rendered inside the reveal it sat
                // in a stacking context the nav escaped (nav bright + tappable above the z-200 backdrop).
                <div className="iv-switch-overlay" role="dialog" aria-modal="true" aria-label="Discard this story" style={{ zIndex: 2000 }} onClick={() => setRevealDiscardConfirm(false)}>
                  <div className="iv-switch-card" onClick={e => e.stopPropagation()}>
                    <p className="iv-switch-title" style={{ fontFamily: "'Lora', serif" }}>Discard this story?</p>
                    <p className="iv-switch-sub">This story isn't in your book. If you discard it, it's gone for good.</p>
                    {/* F-70: danger lane (§3) — red, <=3-word verb. "Go back" (ghost), never "Keep it here"
                        (going back keeps nothing anywhere; the copy must not imply saving). */}
                    <button data-testid="reveal-discard-confirm" className="iv-switch-btn" style={{ background: '#b03a2e', color: '#fff', border: '1px solid #b03a2e', borderRadius: 12, fontFamily: "'Lora', serif", cursor: 'pointer' }} onClick={() => { setRevealDiscardConfirm(false); if (discardUnsavedDraft) discardUnsavedDraft(); }}>Discard story</button>
                    <button data-testid="reveal-discard-cancel" className="st-btn-secondary iv-switch-btn" onClick={() => setRevealDiscardConfirm(false)}>Go back</button>
                  </div>
                </div>,
                document.body
              )}
            </div>
          );
        }
        // ── end State 0 ────────────────────────────────────────────────────

        // ── REVEAL-002 R1 · State 0 edit (pre-save) — Title + Story ONLY (§6b) ──
        if (!reviewMode.saved && stProcessed.draftId && revealEditing) {
          const activeVersion = stProcessed.activeVersion || 'whole';
          const handleDoneEditing = async () => {
            const fields = { title: stProcessed.title, activeVersion, [activeVersion === 'flick' ? 'flickContent' : 'wholeContent']: stProcessed.content, [activeVersion === 'flick' ? 'flickTitle' : 'wholeTitle']: stProcessed.title };
            if (updateStoryDraftEdits) await updateStoryDraftEdits(fields);
            setStProcessed(p => ({ ...p, versions: { ...p.versions, [activeVersion]: { title: p.title, content: p.content } } }));
            setRevealEditing(false);
          };
          const handleCancelEdit = () => {
            if (revealEditBaseline) setStProcessed(p => ({ ...p, title: revealEditBaseline.title, content: revealEditBaseline.content, activeVersion: revealEditBaseline.activeVersion }));
            setRevealEditing(false);
          };
          return (
            <div className="storyteller-content" data-testid="reveal-ready-edit-root">
              <div data-testid="reveal-ready-edit-ribbon" role="status" style={{ display: 'block', textAlign: 'center', padding: '11px', background: '#FFF4D6', border: '1px solid #EAD79B', borderRadius: 12, marginBottom: 14, fontSize: '0.92rem', color: '#7A5600', fontFamily: "'Lora', serif" }}>
                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#9A7000', marginRight: 7, verticalAlign: 1 }} />
                Editing your story · <strong style={{ fontWeight: 600 }}>still not saved to your book</strong>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label className="st-label">Title</label>
                <input data-testid="reveal-ready-edit-title" className="st-input" value={stProcessed.title || ''} onChange={e => setStProcessed(p => ({ ...p, title: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label className="st-label">Story</label>
                <textarea data-testid="reveal-ready-edit-story" className="st-input st-textarea" rows={14} value={stProcessed.content || ''} onChange={e => setStProcessed(p => ({ ...p, content: e.target.value }))} style={{ minHeight: 300 }} />
              </div>
              {/* §6b: Title + Story and nothing else — no photo, no QR, no More options. */}
              <div style={{ marginTop: 22, display: 'flex', gap: 12, justifyContent: 'center' }}>
                {/* F-66: "Back" (ghost), not "Cancel" — the word "Cancel" read as "cancel my story" and made
                    the founder hesitate. Back reverts the in-box edits to the baseline and returns to State 0
                    with the draft row untouched; Done editing commits the boxes to the draft. Two real lanes. */}
                <button data-testid="reveal-ready-edit-cancel" onClick={handleCancelEdit} style={{ background: 'none', border: '1px solid #B8813E', color: '#A85A0F', borderRadius: 10, padding: '12px 26px', minHeight: 44, cursor: 'pointer', fontFamily: "'Lora', serif" }}>Back</button>
                {/* F-66 §5: Done editing is the action lane — Pip-orange when enabled (st-btn-primary rendered
                    grey, rgba(15,23,42,0.08); §3 says the committing action speaks the orange lane). */}
                <button data-testid="reveal-ready-edit-done" onClick={handleDoneEditing} style={{ background: 'linear-gradient(180deg,#AE6015,#A85A0F)', color: '#fff', border: '1px solid transparent', borderRadius: 10, padding: '13px 26px', minHeight: 44, fontSize: '0.95rem', fontWeight: 600, boxShadow: '0 3px 10px rgba(168,90,15,.28)', cursor: 'pointer', fontFamily: "'Lora', serif" }}>Done editing</button>
              </div>
              <div data-testid="reveal-ready-edit-microcopy" style={{ marginTop: 12, textAlign: 'center', fontSize: 13, color: '#6b6154', fontFamily: "'Lora', serif", maxWidth: '44ch', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.45 }}>Back returns to your story just as it was. Changes here are kept only when you tap Done editing.</div>
            </div>
          );
        }
        // ── end State 0 edit ────────────────────────────────────────────────

        return (
          <div className="storyteller-content">
            {reviewMode.saved ? (
              /* Saved state (auto-save reveal): truthful confirmation; the chapter below is the hero. */
              <div style={{ padding: '12px 16px', background: 'rgba(34,120,52,0.10)', border: '1.5px solid rgba(34,120,52,0.4)', borderRadius: 12, marginBottom: 14, fontSize: '0.98rem', color: '#1f6b2f', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.15rem', lineHeight: 1.2 }}>✓</span>
                <span>Saved to your book. Here's your chapter.</span>
              </div>
            ) : (
              /* TB-117: persistent "not saved yet" cue. The save is a manual final tap and real
                 users were finishing interviews then leaving without saving. This banner does not
                 rely on beforeunload (which iOS Safari suppresses on backgrounding). */
              <div style={{ padding: '12px 16px', background: 'rgba(210,118,31,0.10)', border: '1.5px solid rgba(210,118,31,0.45)', borderRadius: 12, marginBottom: 14, fontSize: '0.95rem', color: '#7a3d10', fontWeight: 600, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontSize: '1.15rem', lineHeight: 1.2 }}>📖</span>
                <span>One last step: your story is not saved yet. Tap <strong>✓ Save to My Book</strong> at the bottom to keep it in your book.</span>
              </div>
            )}
            {/* Context banner — which book you're adding to */}
            {activeBook && (
              <div style={{ padding: '10px 16px', background: isGiftBook ? 'rgba(201,168,76,0.12)' : 'rgba(15,23,42,0.04)', border: `1px solid ${isGiftBook ? 'rgba(201,168,76,0.25)' : 'rgba(15,23,42,0.1)'}`, borderRadius: 10, marginBottom: 12, fontSize: '0.92rem', color: '#3c4653' }}>
                Adding a story to: <strong style={{ color: '#0f172a' }}>{activeBook.title || 'your book'}</strong>
              </div>
            )}
            {/* TB-104 Hot Seat Q&A: show "Rapid Fire chapter" heading when Q&A sentinel detected */}
            <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 4 }}>
              {isRapidFireContent(stProcessed.content) ? 'Your Rapid Fire Chapter' : (stProcessed.raw ? 'Your Words' : 'Your Story')}
            </h2>
            <p className="st-sub" style={{ marginBottom: 24 }}>
              {isRapidFireContent(stProcessed.content)
                ? 'Your answers will print as a Q&A chapter in your book. Edit the title below, then save.'
                : reviewMode.saved
                  ? 'This is in your book. Tweak anything below and tap Save changes, or you are all set.'
                  : (stProcessed.raw ? 'Here\'s what you said. Edit anything, clean it up, or save as-is.' : 'Pick the version you love, tweak anything, then save it to your book.')}
            </p>

            {/* TB-134 QA fix: degraded (timeout -> Flick-only) result. Do NOT show the two-version
                chooser here -- both slots would just be the same faithful draft, and letting someone
                tap "The Whole Tale" would silently hand them the Flick text mislabeled as polished.
                This is a persistent, non-alarming note (not a toast that can vanish) plus a retry
                that re-attempts the polished generation. On success the flag clears and the normal
                chooser below takes over. */}
            {stProcessed.isDegradedGeneration && (
              <div style={{
                marginBottom: 24, padding: '14px 16px', background: 'rgba(210,118,31,0.08)',
                border: '1.5px solid rgba(210,118,31,0.35)', borderRadius: 12,
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <span style={{ fontSize: '1.1rem', lineHeight: 1.3 }}>🦊</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '0.95rem', color: '#7a3d10', fontWeight: 600, marginBottom: 4 }}>
                    This is your faithful draft, in your own words.
                  </p>
                  <p style={{ fontSize: '0.88rem', color: '#7a3d10', marginBottom: 10 }}>
                    The polished "Whole Tale" version took longer than usual, so we saved you this one instead. It's a complete story and safe to save as-is.
                  </p>
                  <button
                    onClick={retryPolishedGeneration}
                    className="st-btn-secondary"
                    style={{ fontSize: '0.88rem', padding: '8px 16px' }}
                  >
                    Try the polished version again
                  </button>
                </div>
              </div>
            )}

            {/* F-145-FINAL: NO customer-facing fidelity UI. The fidelity gate is flag + metric only (surfaced
                in Morning Review), plus the inline regen that reveals the passing version directly — there is
                no banner and no "use the fuller version" offer in any state. */}

            {/* Version chooser — only when both versions exist (interview path with dual generation)
                AND this is not a degraded (Flick-only) result -- see isDegradedGeneration above. */}
            {!stProcessed.isDegradedGeneration && stProcessed.versions?.flick && stProcessed.versions?.whole && (
              <div style={{ marginBottom: 24 }}>
                <p style={{
                  fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.78rem',
                  letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(15,23,42,0.45)',
                  marginBottom: 10,
                }}>Choose your version</p>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { key: 'flick', emoji: '🦊', label: 'A Flick of the Tail', sub: 'Tidied up, in your own words' },
                    { key: 'whole', emoji: '✨', label: 'The Whole Tale', sub: 'Beautifully written' },
                  ].map(({ key, emoji, label, sub }) => {
                    const isActive = (stProcessed.activeVersion || 'whole') === key;
                    return (
                      <button
                        key={key}
                        onClick={() => {
                          const v = stProcessed.versions[key];
                          setStProcessed(p => ({
                            ...p,
                            title: v.title,
                            content: v.content,
                            activeVersion: key,
                            ...(p.saved ? { dirty: true } : {}), // F-7: a version switch after save is an edit
                          }));
                        }}
                        style={{
                          flex: 1,
                          minHeight: 76,
                          padding: '12px 14px',
                          background: isActive ? 'rgba(210,118,31,0.07)' : '#fffdf8',
                          border: isActive ? '2px solid #D2761F' : '1.5px solid rgba(15,23,42,0.13)',
                          borderRadius: 14,
                          cursor: 'pointer',
                          textAlign: 'left',
                          boxShadow: isActive ? '0 2px 10px rgba(210,118,31,0.15)' : 'none',
                          transition: 'border-color 0.15s, box-shadow 0.15s',
                          position: 'relative',
                          WebkitTapHighlightColor: 'transparent',
                        }}
                        aria-pressed={isActive}
                      >
                        {isActive && (
                          <span style={{
                            position: 'absolute', top: 8, right: 10,
                            fontFamily: "'Nunito', sans-serif", fontWeight: 800,
                            fontSize: '0.78rem', color: '#D2761F',
                          }}>✓</span>
                        )}
                        <div style={{ fontSize: '1.3rem', marginBottom: 4 }}>{emoji}</div>
                        <div style={{
                          fontFamily: "'Nunito', sans-serif", fontWeight: 700,
                          fontSize: '0.88rem', color: '#0f172a', lineHeight: 1.25, marginBottom: 3,
                        }}>{label}</div>
                        <div style={{
                          fontFamily: "'Lora', serif", fontSize: '0.8rem',
                          color: 'rgba(15,23,42,0.5)', lineHeight: 1.35,
                        }}>{sub}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label className="st-label">Title</label>
              <input className="st-input" value={stProcessed.title} onChange={e => setStProcessed(p => ({...p, title: e.target.value, ...(p.saved ? { dirty: true } : {})}))} />
            </div>
            {/* TB-104 Hot Seat Q&A: Q&A content preview in review screen */}
            {isRapidFireContent(stProcessed.content) ? (
              <div style={{ marginBottom: 20, padding: '16px 20px', background: 'rgba(15,23,42,0.03)', borderRadius: 12, border: '1px solid rgba(15,23,42,0.08)' }}>
                <label className="st-label" style={{ marginBottom: 12, display: 'block' }}>Your Rapid Fire answers</label>
                {parseRapidFireContent(stProcessed.content).map((pair, i) => (
                  <div key={i} style={{ marginBottom: 16 }}>
                    <div style={{ fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: '0.92rem', color: '#7a6e64', marginBottom: 4, lineHeight: 1.4 }}>{pair.question}</div>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#2a2520', lineHeight: 1.6 }}>{pair.answer}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ marginBottom: 20 }}>
                <label className="st-label">Story</label>
                <textarea className="st-input st-textarea" rows={14} value={stProcessed.content} onChange={e => setStProcessed(p => ({...p, content: e.target.value, ...(p.saved ? { dirty: true } : {})}))} style={{ minHeight: 300 }} />
              </div>
            )}

            {/* Photo upload */}
            <div style={{ marginBottom: 24, padding: '16px 20px', background: 'rgba(15,23,42,0.03)', borderRadius: 12, border: '1px solid rgba(15,23,42,0.08)' }}>
              <label className="st-label" style={{ marginBottom: 10, display: 'block' }}> Add a photo <span style={{ fontWeight: 400, color: '#3c4653' }}>(optional, you can always add one later)</span></label>
              {stProcessed.pendingPhotoPreview ? (
                <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <img src={stProcessed.pendingPhotoPreview} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(15,23,42,0.1)' }} />
                  <div>
                    <p style={{ fontSize: '1rem', color: '#0f172a', marginBottom: 4 }}>Photo attached ✓</p>
                    <button onClick={() => setStProcessed(p => ({...p, pendingPhotoFile: null, pendingPhotoPreview: null}))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', color: '#c0392b', padding: 0, fontFamily: "'Lora', serif" }}>Remove</button>
                  </div>
                </div>
                <EnhanceNudge />
                </>
              ) : (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 20px', background: 'rgba(15,23,42,0.06)', borderRadius: 8, border: '1px solid rgba(15,23,42,0.12)', fontSize: '1rem', color: '#0f172a', fontFamily: "'Lora', serif" }}>
                   Choose photo
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!isImageFile(file)) { showToast('Please choose an image file.', 'error'); return; }
                    if (file.size > 10 * 1024 * 1024) { showToast('Image must be under 10 MB.', 'error'); return; }
                    try {
                      const normalizedFile = await normalizeImageForBook(file);
                      const preview = URL.createObjectURL(normalizedFile);
                      setStProcessed(p => ({...p, pendingPhotoFile: normalizedFile, pendingPhotoPreview: preview}));
                    } catch (err) { showToast('Could not load photo.', 'error'); }
                  }} />
                </label>
              )}
            </div>

            {/* TB-104 Hot Seat Q&A: hide prose polish tiers for Q&A chapters — they'd destroy the structure */}
            {/* Polish tiers only for a RAW transcription the user is choosing help for. An
                interview chapter is already assembled + written by Pip, so offering "clean up
                vs rewrite beautifully" on it is confusing (clean-up would even make it lighter
                than what Pip already did). For interview chapters the user just edits + saves. */}
            {!isRapidFireContent(stProcessed.content) && !stProcessed.fromInterview && <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: '0.95rem', color: '#3c4653', textAlign: 'center', marginBottom: 2, fontFamily: "'Lora', serif" }}>
                {stInterviewPolishing ? 'Working on it...' : stProcessed.raw ? 'How much help do you want?' : 'Try a different version?'}
              </p>
              {!stInterviewPolishing && <p style={{ fontSize: '0.88rem', color: '#3c4653', textAlign: 'center', marginBottom: 10, fontStyle: 'italic' }}>Light cleanup or a full rewrite — your voice stays either way.</p>}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { tier: 'cleanup', icon: <Sparkle size={16} weight="duotone" />, label: 'Clean this up for me' },
                  { tier: 'chapter', icon: <BookOpenText size={16} weight="duotone" />, label: 'Rewrite this beautifully' },
                ].map(({ tier, icon, label }) => {
                  const isActive = stProcessed.polishTier === tier;
                  const isCached = stProcessed.tierCache?.[tier];
                  return (
                    <button key={tier} onClick={() => handleInterviewPolish(tier)} className="st-btn-secondary" style={{
                      flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.95rem',
                      background: isActive ? 'rgba(15,23,42,0.12)' : 'transparent',
                      borderColor: isActive ? 'rgba(15,23,42,0.35)' : 'rgba(15,23,42,0.12)',
                      fontWeight: isActive ? 600 : 400,
                    }} disabled={stInterviewPolishing}>
                      {stInterviewPolishing && !isCached ? '' : icon} {label}{isCached && !isActive ? ' ✓' : ''}
                    </button>
                  );
                })}
              </div>
              {stProcessed.polishTier && (
                <button onClick={() => {
                  const orig = stProcessed.originalContent || stProcessed.content;
                  const origTitle = stProcessed.originalTitle || stProcessed.title;
                  setStProcessed(p => ({ ...p, title: origTitle, content: orig, raw: true, polishTier: null }));
                }} style={{ display: 'block', margin: '10px auto 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', color: '#3c4653', fontFamily: "'Lora', serif" }}>
                  ↩ Back to my original words
                </button>
              )}
            </div>} {/* TB-104 Hot Seat Q&A: end of prose-only polish section */}

            {/* QR code opt-in for voice recordings */}
            {(stProcessed.audioBlob) && (
              <div style={{ marginBottom: 20, padding: '16px 20px', background: 'rgba(138,109,58,0.06)', borderRadius: 12, border: '1px solid rgba(138,109,58,0.15)' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={stProcessed.includeQr !== false} onChange={e => setStProcessed(p => ({...p, includeQr: e.target.checked}))}
                    style={{ marginTop: 4, width: 18, height: 18, accentColor: '#0f172a' }} />
                  <div>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a', fontWeight: 500 }}>{stProcessed.videoBlob ? '' : ''} Include {stProcessed.videoBlob ? 'video' : 'voice'} recording in printed book</div>
                    <div style={{ fontSize: '0.92rem', color: '#3c4653', marginTop: 4, lineHeight: 1.5 }}>A QR code will appear in the book so readers can scan and {stProcessed.videoBlob ? 'watch' : 'hear'} your original recording. Uncheck to exclude it.</div>
                  </div>
                </label>
              </div>
            )}

            {reviewMode.showSaveButton ? (
              <div className="sticky-save-bar" style={{ display: 'flex', gap: 12 }}>
                {/* REVEAL-001 C2: typed manual save lands on the reveal (State 1), same as voice auto-save. */}
                <button onClick={() => saveStInterviewStory(null, { revealAfterSave: true })} disabled={stSaving} className="st-btn-primary" style={{ flex: 1, opacity: stSaving ? 0.6 : 1 }}>{stSaving ? 'Saving…' : '✓ Save to My Book'}</button>
                <button
                  onClick={() => {
                    // Guard: show a clear three-option dialog instead of window.confirm
                    if (stProcessed.content?.trim()) {
                      setStReviewBackConfirm(true);
                      return;
                    }
                    setStInterviewStep('conversation');
                    setStAutoTranscribeTriggered(false);
                  }}
                  className="st-btn-secondary"
                >← Back</button>{/* TB-104 Stage B: was 'answer' */}
              </div>
            ) : (
              /* PIPE-SAVE-1 F-3 saved state: the Save button becomes a truthful badge; a low-prominence
                 "Remove this story" (first live-tested tonight) fixes Remove discoverability. The user
                 leaves via the persistent bottom nav; the chapter above stays the hero. */
              <div className="sticky-save-bar" style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                {reviewMode.showSaveChanges ? (
                  /* F-7: post-save edits are dirty -> actionable "Save changes" (same shape-guarded
                     seam via updateSavedInterviewStory); on success it returns to the badge below. */
                  <button onClick={() => updateSavedInterviewStory()} disabled={stSaving} className="st-btn-primary" style={{ width: '100%', opacity: stSaving ? 0.6 : 1 }}>{stSaving ? 'Saving…' : 'Save changes'}</button>
                ) : (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', background: 'rgba(34,120,52,0.10)', border: '1.5px solid rgba(34,120,52,0.4)', borderRadius: 24, color: '#1f6b2f', fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: '1.05rem' }}>
                    <span>✓</span> Saved to your book
                  </div>
                )}
                {reviewMode.canRemove && (
                  <button onClick={() => removeRevealedStory && removeRevealedStory()} style={{ background: 'none', border: 'none', color: '#9a8570', fontFamily: "'Lora', serif", fontSize: '0.9rem', cursor: 'pointer', padding: 6, textDecoration: 'underline' }}>
                    Remove this story
                  </button>
                )}
              </div>
            )}

            {/* Review-screen back-guard: three-choice dialog replacing window.confirm */}
            {stReviewBackConfirm && (
              <div className="iv-switch-overlay" role="dialog" aria-modal="true" aria-label="Save before leaving"
                onClick={() => setStReviewBackConfirm(false)}>
                <div className="iv-switch-card" onClick={e => e.stopPropagation()}>
                  <p className="iv-switch-title" style={{ fontFamily: "'Lora', serif" }}>Save this story to your book first?</p>
                  <p className="iv-switch-sub">You haven't saved this story yet. It took real effort to get here.</p>
                  <button
                    className="st-btn-primary iv-switch-btn"
                    disabled={stSaving}
                    onClick={() => {
                      setStReviewBackConfirm(false);
                      saveStInterviewStory(null, { revealAfterSave: true });
                    }}
                  >{stSaving ? 'Saving…' : 'Save to my book'}</button>
                  <button
                    className="st-btn-secondary iv-switch-btn"
                    style={{ color: '#b03a2e' }}
                    onClick={() => {
                      setStReviewBackConfirm(false);
                      setStInterviewStep('conversation');
                      setStAutoTranscribeTriggered(false);
                    }}
                  >Leave without saving</button>
                  <button
                    className="iv-switch-cancel"
                    onClick={() => setStReviewBackConfirm(false)}
                  >Keep editing</button>
                </div>
              </div>
            )}
          </div>
        );
      }

      // ── TB-104 Stage B: CONVERSATION STEP — unified surface (replaces 'answer' + 'chat') ──
      // Single scrolling thread: opener seeded locally, voice/text capture inline,
      // "Turn this into a chapter" moment driven by interviewIsWrapped boolean.
      if (stInterviewStep === 'conversation') {
        // ── handlers ──
        const ivHasUserAnswer = interviewConversation.some(t => t.role === 'user');
        // Quiet header Finish — visible once they have answered so a first-time teller
        // can see how to end. Not the old orange dock gold door (FINISH-CTA-FLOOR-1).
        const ivFinishVisible = ivHasUserAnswer;

        const handleConvSend = async (textToSend) => {
          const text = (textToSend || stInterviewInput).trim();
          if (!text || stInterviewLoading || ivRecording || ivTranscribing) return;
          setStInterviewInput('');
          // TB-104 keep-going / TB-134 fix 2: typing after a wrap re-engages the interview.
          // The explicit "Keep going" button (handleInterviewKeepGoing) extends the turn budget
          // by 4 before continuing. Until this fix, a storyteller who instead just typed a new
          // answer past a wrap offer got NO budget extension at all, so the same CAP_REACHED /
          // DIMINISHING_RETURNS condition could re-fire on the very next turn -- looping the
          // storyteller into repeat wrap offers. Mirror the same +4 extension here, gated the
          // same way the Keep going button itself is gated below (never for DONE-SIGNAL -- that
          // wrap reflects the storyteller's own explicit "I'm done"; typing more afterward is a
          // fresh addition, not a continuation past a soft checkpoint).
          //
          // Compute the extended value synchronously and pass it as an explicit override into
          // sendStInterviewMsg rather than relying on the setInterviewBudgetBonus state update
          // having landed by the time sendStInterviewMsg runs -- it has not, since React state
          // updates are not synchronous and sendStInterviewMsg is called in the same tick. This
          // is the same stale-closure hazard TB-104's other *Override params already guard
          // against, and the exact class of silent-no-op bug this TB-134 pass is trying to stop
          // repeating.
          let budgetBonusOverride = null;
          if (interviewIsWrapped) {
            if (interviewWrapReason !== 'DONE-SIGNAL') {
              budgetBonusOverride = interviewBudgetBonus + 4; // TB-134 fix 2
              setInterviewBudgetBonus(budgetBonusOverride);
            }
            setInterviewIsWrapped(false); // TB-104 keep-going: unwrap on new typed answer
          }
          if (!ivHasUserAnswer) {
            setIvTipsOpen(false);
            try { localStorage.setItem('tb_interview_tips_seen', '1'); } catch (e) { /* ignore */ }
            // First answer: goes through submitFirstAnswer (classifies + decides follow-up or wrap).
            // A wrap can never have been offered yet on the first answer, so there is nothing to
            // extend here -- budgetBonusOverride is always null on this branch.
            await submitFirstAnswer(text, null, false);
          } else {
            // Subsequent answers: go through sendStInterviewMsg
            await sendStInterviewMsg(text, budgetBonusOverride !== null ? { budgetBonusOverride } : undefined);
          }
          // Scrolling is handled by the effect watching interviewConversation /
          // stInterviewLoading, so both the user's own message and Pip's reply
          // pin to the bottom (not just the post-reply state).
        };

        // REVEAL-001 C5 Guard 1 (Fable F-41): in a reopened "Tell Pip more" session the SAVED chapter is
        // seeded as the first user turn (so Finish is tappable immediately). Finishing with NO new turn
        // would regenerate the already-saved chapter FROM ITSELF — its words changing with zero new
        // telling, violating "the chapter grows only by the user's own words." So a reopened session needs
        // at least one NEW user turn (userTurns > the single seeded one) before Finish may act.
        const reopenUserTurns = interviewConversation.filter(t => t.role === 'user').length;
        const reopenNeedsAddition = !!stReopenStoryId && reopenUserTurns <= 1;
        const handleWriteItUp = async () => {
          if (stInterviewLoading || ivTranscribing) return;
          if (reopenNeedsAddition) {
            // Leave the chapter untouched; gently invite an addition or a return (the reveal is one tap
            // away via "Back to questions"). No generation, no mutation — the saved prose is unchanged.
            showToast('Add something first, or head back — your chapter is unchanged.');
            return;
          }
          const allAnswers = interviewConversation
            .filter(t => t.role === 'user')
            .map(t => t.content)
            .filter(Boolean)
            .join('\n\n');
          await generateStInterviewStory(allAnswers || stInterviewInput);
        };

        // Voice-to-voice capability check.
        // Requires getUserMedia (mic access) + RTCPeerConnection (WebRTC) — both
        // are needed for the ElevenLabs session. Bias toward dictation when uncertain.
        // Hot Seat is excluded because voice-to-voice is not wired for that pack.
        // Use activePackId directly here; isHotSeatConv is defined later in this block.
        const isVoiceToVoiceCapable = (activePackId || '') !== 'hot-seat'
          && typeof navigator !== 'undefined'
          && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
          && !!(window.RTCPeerConnection || window.webkitRTCPeerConnection || window.mozRTCPeerConnection);

        // "Talk to Pip instead" — switches from the text conversation to voice-to-voice.
        // Gated to isVoiceToVoiceCapable && !ivHasUserAnswer so we never lose typed turns
        // and never offer voice-to-voice on a device that cannot support it.
        // Performs a full engine state reset (same as exitToPicker) so VoiceInterview
        // starts fresh, then re-enters via stVoiceInterviewMode with the same prompt.
        const handleSwitchToVoice = () => {
          // F-118b: no destroy without a warn — switching to voice discards an in-progress typed draft.
          // (Native confirm, mirroring the in-app nav guard. Copy is functional — flagged for founder review.)
          if ((stInterviewInput || '').trim() && typeof window !== 'undefined'
              && !window.confirm('Switch to talking with Pip? The words you typed here will be cleared.')) return;
          setStInterviewMode(false);
          setStInterviewStep('pick');
          setInterviewFollowUpCount(0);
          setInterviewAskedMoves([]);
          setInterviewConversation([]);
          setInterviewLastClassification(null);
          setInterviewEmptyCount(0);
          setStInterviewMessages([]);
          setInterviewIsWrapped(false);
          resetIvState();
          setVoiceAutoStart(true);
          setStVoiceInterviewMode(true);
        };

        // FOUNDER RULING 2026-08-10: the dictation mic and its toggle are GONE from this screen.
        // `showMicButton` and `handleMicToggle` are deleted rather than hidden behind a flag, so
        // nothing can quietly switch them back on. The F-118b merge-and-send behaviour they carried
        // (dictation glued onto the typed draft and sent unread) went with them; it had been flagged
        // for his ratification and never got it, and this ruling settles it by removal.

        // TB-104 steering: does the interview hold a real answer worth protecting?
        // (any storyteller turn of 5+ words) — so even one heartfelt answer isn't lost.
        // F-118b: an unsent typed draft is substance too (Finding 2) — Leave must warn, not silently abandon it.
        const ivHasSubstance = interviewHasSubstance({ draft: stInterviewInput, conversation: interviewConversation });

        // Full reset + return to the question picker.
        const exitToPicker = () => {
          // F-87 (D2): capture seedless BEFORE the reset nulls the prompt. A seedless text interview
          // (reached via "I'd rather type" on a seedless start) leaves to the DOORWAY, not the picker;
          // a topic-picked interview keeps today's return to the questions/prompt cards.
          const wasSeedless = !stSelectedPrompt;
          setIvExitConfirm(false);
          setStInterviewStep('pick');
          setInterviewFollowUpCount(0);
          setInterviewAskedMoves([]);
          setInterviewConversation([]);
          setInterviewLastClassification(null);
          setInterviewEmptyCount(0);
          setStInterviewMessages([]);
          setInterviewIsWrapped(false);
          resetIvState();
          setStInterviewMode(false);
          setStSelectedPrompt(null);
          if (backFromPathQuestions()) return;
          if (wasSeedless) setActiveTab('talk');
        };

        // Back arrow: if there's unsaved substance, confirm first (save / leave / cancel);
        // otherwise just leave. This is where "start a different story" lives.
        const handleBackFromInterview = () => {
          if (ivHasSubstance) { setIvExitConfirm(true); return; }
          exitToPicker();
        };

        // Exit-confirm: "Save what I have" — write it up (review/save), then continue from there.
        const exitSaveFirst = async () => {
          setIvExitConfirm(false);
          await writeUpInterviewSoFar();
        };

        // Pack-aware tone lookup
        const packId = activePackId || '';
        const isHotSeatConv = packId === 'hot-seat';

        // Opener framing line (static, no AI call) — spec §7 state-init
        const openerFraming = isGiftBook
          ? `A question about ${activeBook?.recipientName || 'them'}:`
          : isHotSeatConv ? 'Okay, here\'s one:' : 'Here\'s what I\'d love to know:';

        // Determine whether the thread has been seeded with the opening AI message
        // The first message is always role:'assistant' with seeded:true
        const displayMsgs = stInterviewMessages;
        const laterAssistantIdxs = displayMsgs
          .map((msg, idx) => (msg.role === 'assistant' && !(idx === 0 && msg.seeded) ? idx : -1))
          .filter((idx) => idx >= 0);
        const lastAssistantIdx = laterAssistantIdxs.length ? laterAssistantIdxs[laterAssistantIdxs.length - 1] : -1;
        const openerIsLatest = lastAssistantIdx < 0 && !stInterviewLoading && !ivTranscribing;

        return (
          <div className="iv-chat-shell" ref={ivShellRef}>
            {/* ── TB-104 Stage B: embedded .iv-* styles (design-visual-interaction.md §10.1) ── */}
            <style>{`
              html.iv-chat-lock, body.iv-chat-lock { overflow: hidden; height: 100%; overscroll-behavior: none; }
              .iv-chat-shell {
                display: flex;
                flex-direction: column;
                overflow: hidden;
                background: #fbf8f1;
                max-width: 520px;
                margin: 0 auto;
                height: calc(100svh - 80px);
              }
              @media (max-width: 640px) {
                .iv-chat-shell {
                  position: fixed;
                  top: 48px;
                  left: 0;
                  right: 0;
                  bottom: calc(64px + env(safe-area-inset-bottom, 0px));
                  max-width: none;
                  margin: 0;
                  height: auto;
                  z-index: 90;
                }
              }
              @media (min-width: 641px) {
                .iv-chat-shell {
                  position: fixed;
                  top: 0;
                  left: 280px;
                  right: 0;
                  bottom: 0;
                  max-width: 520px;
                  margin: 0 auto;
                  height: auto;
                  z-index: 90;
                }
              }
              @media (min-width: 641px) and (max-width: 1024px) {
                .iv-chat-shell { left: 200px; }
              }
              .iv-chat-header {
                flex: 0 0 auto;
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                padding: 4px 10px;
                background: #efebe0;
                border-bottom: 1px solid rgba(15,23,42,0.10);
              }
              .iv-header-btn {
                min-height: 44px;
                padding: 8px 12px;
                background: none;
                border: none;
                cursor: pointer;
                font-family: 'Nunito', sans-serif;
                font-size: 0.92rem;
                font-weight: 700;
                color: #5c4f38;
                -webkit-tap-highlight-color: transparent;
              }
              .iv-header-btn:focus-visible { outline: 2px solid #b8944a; outline-offset: 2px; }
              .iv-thread {
                flex: 1 1 auto;
                min-height: 0;
                overflow-y: auto;
                overscroll-behavior: contain;
                -webkit-overflow-scrolling: touch;
                display: flex;
                flex-direction: column;
                gap: 16px;
                padding: 12px 16px 18px;
              }
              .iv-ai-bubble {
                align-self: flex-start;
                max-width: 88%;
                background: rgba(201,168,76,0.08);
                border: 1.5px solid rgba(201,168,76,0.25);
                border-radius: 4px 20px 20px 20px;
                padding: 14px 16px 16px;
                font-family: 'Lora', serif;
                font-size: 1.15rem;
                line-height: 1.65;
                color: #0f172a;
                font-weight: 400;
                font-style: normal;
                box-shadow: 0 2px 12px rgba(15,23,42,0.05);
              }
              .iv-ai-bubble[data-kind="iv-opener"],
              .iv-ai-bubble.iv-opener {
                font-size: clamp(1.15rem, 4vw, 1.35rem);
                font-weight: 500;
              }
              .iv-bubble-avatar,
              .iv-opener-avatar {
                width: 36px;
                height: 36px;
                object-fit: contain;
                display: block;
                margin-bottom: 6px;
                filter: drop-shadow(0 3px 6px rgba(120,70,20,0.18));
              }
              .iv-bubble-name,
              .iv-opener-eyebrow {
                font-size: 0.75rem;
                font-family: 'Nunito', sans-serif;
                font-weight: 800;
                letter-spacing: 0.04em;
                text-transform: uppercase;
                color: #9a7c3a;
                margin-bottom: 6px;
              }
              .iv-opener-eyebrow {
                font-size: 0.82rem;
                font-weight: 700;
                margin-bottom: 10px;
              }
              .iv-user-bubble {
                align-self: flex-end;
                max-width: 88%;
                background: rgba(15,23,42,0.88);
                color: #fff;
                border-radius: 20px 4px 20px 20px;
                padding: 16px 20px;
                font-family: 'Lora', serif;
                font-size: 1.05rem;
                line-height: 1.6;
                font-weight: 400;
                font-style: normal;
              }
              .iv-thinking-bubble,
              .iv-ai-bubble[data-kind="iv-thinking-bubble"] {
                align-self: flex-start;
                max-width: 88%;
                font-family: 'Lora', serif;
                font-size: 1rem;
                font-style: italic;
                color: #7a5a10;
              }
              .iv-transcribing-bubble {
                align-self: flex-end;
                max-width: 78%;
                background: rgba(15,23,42,0.06);
                border: 1.5px dashed rgba(15,23,42,0.2);
                border-radius: 20px 4px 20px 20px;
                padding: 14px 18px;
                color: rgba(15,23,42,0.5);
                font-family: 'Lora', serif;
                font-size: 0.95rem;
                font-style: italic;
              }
              .iv-writeup-nudge {
                margin: 8px 0 4px;
                padding: 20px 24px;
                background: rgba(201,168,76,0.10);
                border: 1.5px solid rgba(201,168,76,0.30);
                border-radius: 16px;
                text-align: center;
              }
              .iv-writeup-nudge p { font-family: 'Lora', serif; font-style: italic; font-size: 1.05rem; color: #7a5a10; margin-bottom: 14px; }
              .iv-qr-opt-in {
                display: flex;
                align-items: flex-start;
                gap: 10px;
                margin-top: 16px;
                padding-top: 14px;
                border-top: 1px solid rgba(201,168,76,0.18);
                text-align: left;
              }
              .iv-qr-opt-in input[type=checkbox] { width: 18px; height: 18px; accent-color: #0f172a; flex-shrink: 0; margin-top: 3px; }
              .iv-qr-label { font-family: 'Lora', serif; font-size: 0.95rem; color: #0f172a; }
              .iv-qr-sub { font-family: 'Lora', serif; font-size: 0.85rem; color: #3c4653; margin-top: 3px; }
              /* Pinned as the shell's bottom region — never in window document flow. */
              .iv-capture-dock {
                flex: 0 0 auto;
                position: relative;
                display: flex;
                flex-direction: column;
                background: #efebe0;
                border-top: 1px solid rgba(15,23,42,0.10);
              }
              .iv-capture-bar {
                background: transparent;
                padding: 8px 14px 10px;
                display: flex;
                align-items: flex-end;
                gap: 8px;
              }
              .iv-capture-bar[data-recording="true"] {
                background: rgba(220,80,60,0.06);
                border-top-color: rgba(220,80,60,0.25);
              }
              .iv-media-toggle { display: flex; flex-direction: column; gap: 4px; flex-shrink: 0; }
              .iv-media-btn {
                width: 44px; height: 44px; border-radius: 50%;
                background: rgba(15,23,42,0.82);
                border: none;
                cursor: pointer; display: flex; align-items: center; justify-content: center;
                transition: background 0.15s, opacity 0.15s;
              }
              .iv-media-btn:disabled { opacity: 0.35; cursor: default; }
              .iv-media-btn:not(:disabled):active { opacity: 0.75; }
              .iv-media-btn:focus-visible { outline: 2px solid #b8944a; outline-offset: 2px; }
              .iv-capture-input {
                flex: 1; min-height: 48px; max-height: 168px;
                padding: 12px 14px; font-size: 1.05rem; font-family: 'Lora', serif;
                line-height: 1.55; background: #fff; border: 1.5px solid #d4c9b8;
                border-radius: 24px; color: #1a1209; resize: none; overflow-y: hidden;
                transition: border-color 0.2s, box-shadow 0.2s;
              }
              .iv-capture-input:focus { border-color: #b8944a; outline: none; box-shadow: 0 0 0 3px rgba(15,23,42,0.07); }
              .iv-capture-input::placeholder { color: #9a8e7e; font-style: italic; }
              .iv-send-btn {
                width: 44px; height: 44px; border-radius: 50%;
                background: rgba(15,23,42,0.85); border: none; cursor: pointer;
                display: flex; align-items: center; justify-content: center; flex-shrink: 0;
                transition: opacity 0.15s, transform 0.1s;
              }
              .iv-send-btn:disabled { background: #ddd4c4; opacity: 1; cursor: default; }
              .iv-send-btn:disabled svg { opacity: 0.35; }
              .iv-send-btn:not(:disabled):active { transform: scale(0.94); }
              .iv-send-btn:focus-visible { outline: 2px solid #b8944a; outline-offset: 2px; }
              .iv-writeup-link {
                font-family: 'Lora', serif; font-size: 0.88rem; color: #7a5a10;
                background: none; border: none; cursor: pointer; padding: 6px 0;
                text-decoration: underline; white-space: nowrap; flex-shrink: 0;
              }
              .iv-writeup-link:disabled { opacity: 0.4; cursor: default; }
              /* Header Finish — findable as a control, not an orange dock shove. */
              .iv-finish-btn {
                display: inline-flex; align-items: center; justify-content: center;
                min-height: 44px;
                margin: 0;
                font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.86rem;
                color: #7a4a12;
                background: #fffdf8;
                border: 1.5px solid rgba(146,64,14,0.38);
                border-radius: 12px;
                padding: 8px 12px;
                cursor: pointer;
                box-shadow: none;
                -webkit-tap-highlight-color: transparent;
                white-space: nowrap;
              }
              .iv-finish-btn:not(:disabled):active { background: rgba(122,74,18,0.08); }
              .iv-finish-btn:disabled { opacity: 1; cursor: default; color: #7a4a12; }
              .iv-finish-btn:focus-visible { outline: 2px solid #b8944a; outline-offset: 2px; }
              /* Leave is an exit — quietly de-emphasized within the secondary row. */
              .iv-steer-chip-leave { color: #9a6a5a; border-color: rgba(180,60,40,0.20); }
              /* TB-104 steering: quiet "you're in charge" control row, sits atop the capture bar.
                 FOUNDER RULING 2026-08-10 (DECISION 1, reply "A"): LEFT-aligned, not centred. He
                 raised it himself — *"we should probably reconsider the location of it, it is right
                 next to the submit button"* — after Leave took one click and ended his interview.
                 Leave is the only chip in this row now, and Send sits at the bottom RIGHT of the
                 capture bar directly below, so centring put the destructive control a thumb-width
                 from the one people press constantly. Left-aligned puts it diagonally opposite Send,
                 the furthest apart the dock allows. This is placement, not protection: the guard is
                 the confirm, which as of F-421 arms on the teller's first answer. */
              .iv-steer-row {
                padding: 8px 14px 4px;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: flex-start;
                gap: 7px;
              }
              .iv-steer-chip {
                flex: 0 0 auto;
                max-width: 100%;
                min-height: 36px;
                font-family: 'Nunito', sans-serif;
                font-size: 0.82rem;
                font-weight: 600;
                color: #756443;
                background: rgba(255,253,248,0.8);
                border: 1px solid rgba(15,23,42,0.10);
                border-radius: 18px;
                padding: 6px 14px;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 5px;
                transition: background 0.15s, color 0.15s, transform 0.1s;
                white-space: nowrap;
                -webkit-tap-highlight-color: transparent;
              }
              .iv-steer-chip:active:not(:disabled) { transform: scale(0.96); background: #f1ebdc; }
              .iv-steer-chip:hover:not(:disabled) { background: #fffdf8; color: #3c2e14; }
              .iv-steer-chip:disabled { opacity: 0.35; cursor: default; }
              .iv-steer-chip:focus-visible { outline: 2px solid #b8944a; outline-offset: 2px; }
              @media (min-width: 641px) {
                /* Chips stay compact, never stretched edge-to-edge. The row's left alignment is now
                   set at every width (founder ruling above), so it is no longer overridden here. */
                .iv-steer-chip { flex: 0 0 auto; padding: 6px 16px; }
              }
              @media (max-width: 374px) {
                /* iPhone SE and similar: shrink type so both chips fit */
                .iv-steer-row { gap: 6px; padding: 8px 10px 4px; }
                .iv-steer-chip { font-size: 0.78rem; padding: 6px 10px; }
              }
              /* TB-104: expandable interview tips */
              .iv-tips {
                margin-bottom: 14px;
                background: rgba(201,168,76,0.07);
                border: 1px solid rgba(201,168,76,0.22);
                border-radius: 12px;
                overflow: hidden;
              }
              .iv-tips-head {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 10px;
                padding: 12px 16px;
                background: none;
                border: none;
                cursor: pointer;
                font-family: 'Lora', serif;
                font-size: 0.95rem;
                font-weight: 600;
                color: #7a5a10;
                text-align: left;
              }
              .iv-tips-head:focus-visible { outline: 2px solid #b8944a; outline-offset: -2px; }
              .iv-tips-caret {
                font-size: 1.1rem;
                line-height: 1;
                transition: transform 0.2s ease;
                flex-shrink: 0;
              }
              .iv-tips-caret[data-open="true"] { transform: rotate(180deg); }
              .iv-tips-body {
                margin: 0;
                padding: 2px 18px 14px 30px;
                list-style: disc;
              }
              .iv-tips-body li {
                font-family: 'Lora', serif;
                font-size: 0.92rem;
                line-height: 1.55;
                color: #5c4f38;
                margin-bottom: 8px;
              }
              .iv-tips-body li:last-child { margin-bottom: 0; }
              .iv-tips-body strong { color: #3c2e14; }
              /* TB-104 steering: switch-question confirm dialog */
              /* .iv-switch-* dialog styles moved to app.css (F-70) so the State-0 reveal Discard dialog
                 is a real modal; this branch used to be their only definition. */
              .iv-video-preview {
                position: fixed;
                bottom: calc(64px + 72px + 10px + env(safe-area-inset-bottom, 0px));
                right: 16px;
                width: 160px; height: 120px;
                border-radius: 12px; background: #1a1612;
                border: 2px solid rgba(220,80,60,0.35);
                object-fit: cover; z-index: 80;
              }
              @media (max-width: 640px) {
                .iv-video-preview { bottom: calc(58px + 72px + 10px + env(safe-area-inset-bottom, 0px)); }
              }
              @media (max-width: 374px) {
                .iv-ai-bubble { font-size: 1.05rem; }
              }
              @media (prefers-reduced-motion: no-preference) {
                @keyframes iv-dot-pulse {
                  0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
                  40% { opacity: 1; transform: translateY(-4px); }
                }
                .iv-dot { animation: iv-dot-pulse 1.2s ease-in-out infinite; }
                .iv-dot:nth-child(2) { animation-delay: 0.2s; }
                .iv-dot:nth-child(3) { animation-delay: 0.4s; }
              }
              @media (prefers-reduced-motion: reduce) {
                .iv-dot { animation: none; opacity: 0.6; }
              }
              /* Hot Seat tonal overrides via data-pack attribute */
              .iv-thread[data-pack="hot-seat"] .iv-ai-bubble {
                font-style: normal; font-weight: 600; font-size: 1.1rem;
                background: rgba(154,52,18,0.05);
                border-color: rgba(154,52,18,0.18);
                border-radius: 4px 14px 14px 14px;
              }
              .iv-thread[data-pack="hot-seat"] .iv-user-bubble { border-radius: 14px 4px 14px 14px; }
              .iv-thread[data-pack="hot-seat"] .iv-writeup-nudge { background: rgba(154,52,18,0.08); border-color: rgba(154,52,18,0.22); }
              .iv-thread[data-pack="hot-seat"] .iv-writeup-nudge p { color: #7c2d12; }
            `}</style>

            <header className="iv-chat-header">
              <div className="iv-steer-row" aria-label="Interview controls" style={{ padding: 0, width: '100%', justifyContent: 'space-between' }}>
                <button
                  className="iv-header-btn iv-steer-chip-leave"
                  onClick={handleBackFromInterview}
                  title="Leave this interview"
                >
                  Leave
                </button>
                {isVoiceToVoiceCapable && !ivHasUserAnswer && (
                  <button
                    onClick={handleSwitchToVoice}
                    className="iv-header-btn"
                    style={{ fontWeight: 600, color: '#7a5a10' }}
                  >
                    Talk to Pip instead
                  </button>
                )}
                {ivFinishVisible && !interviewIsWrapped && (
                  <button
                    className="iv-finish-btn"
                    onClick={handleWriteItUp}
                    aria-label={DONE_BUTTON_LABEL}
                    title={reopenNeedsAddition ? 'Add something new before finishing — your chapter is unchanged' : 'Write up everything so far into your story'}
                  >
                    {DONE_BUTTON_LABEL}
                  </button>
                )}
              </div>
            </header>

            <div className="iv-thread" ref={ivThreadRef} data-pack={packId || undefined}>
            {!isHotSeatConv && (
              <div className="iv-tips">
                <button className="iv-tips-head" onClick={toggleIvTips} aria-expanded={ivTipsOpen}>
                  <span>How this works</span>
                  <span className="iv-tips-caret" data-open={ivTipsOpen ? 'true' : 'false'}>⌄</span>
                </button>
                {ivTipsOpen && (
                  <ul className="iv-tips-body">
                    <li><strong>Just answer naturally.</strong> Pip will ask follow-up questions to help you go deeper. There are no wrong answers.</li>
                    <li><strong>Little details make it vivid.</strong> A name, a smell, something someone said, the year, the car, the street.</li>
                    <li><strong>Rough memory is fine.</strong> "Sometime in the 80s" or "I think I was around ten" is perfect.</li>
                    {/* FOUNDER, 2026-08-10: *"'how this works' section is stale and incorrect and
                        needs updating or removal."* Two bullets were removed here and NOTHING was
                        written to replace them, because replacement copy is his.
                          - "Speak or type... The mic turns your voice into text" named the dictation
                            mic, which HE removed from this screen on 2026-08-10.
                          - "Tap 'Ask me something else' below" named a control deleted in this same
                            change.
                        DESIGN_CONSTITUTION §5 forbids copy naming a control that is not there, so
                        leaving either in place was not an option. OPEN, and his: a teller who does not
                        like the question is now told nothing about what to do. */}
                    <li><strong>When you're done,</strong> tap "{DONE_BUTTON_LABEL}" and Pip will write it up for you.</li>
                  </ul>
                )}
              </div>
            )}

            {/* F-334 GENERATION FAILURE BANNER — persistent, inline, in the thread pane.
                IN FLOW, never fixed/absolute — covering the input at 390px is the defect. */}
            {stGenerationFailure && (
              <div
                data-testid="generation-failure-banner"
                role="alert"
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  background: '#fdf3e7', border: '1px solid #e3c9a4', borderRadius: 12,
                  padding: '13px 14px', margin: 0,
                  fontFamily: "'Lora', serif", fontSize: '0.95rem', lineHeight: 1.5, color: '#6b4a1f',
                }}
              >
                <span style={{ flex: 1 }}>{stGenerationFailure.message}</span>
                <button
                  type="button"
                  aria-label="Dismiss"
                  data-testid="generation-failure-dismiss"
                  onClick={() => setStGenerationFailure(null)}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer', color: '#8a6a3a',
                    fontSize: '1.1rem', lineHeight: 1, padding: '2px 4px', minWidth: 32, minHeight: 32,
                  }}
                >
                  ×
                </button>
              </div>
            )}

              <PipChatBubble extraClass="iv-opener" latest={openerIsLatest}>
                {isGiftBook ? (
                  <>
                    <div className="iv-opener-eyebrow">{openerFraming}</div>
                    <div>{stSelectedPrompt?.text}</div>
                  </>
                ) : (
                  <div>{buildOpenerLine(stSelectedPrompt?.text || '', { savedStoryCount: (stStoryList?.length || 0), variantSeed: (stStoryList?.length || 0) })}</div>
                )}
              </PipChatBubble>

              {displayMsgs.map((msg, idx) => {
                if (idx === 0 && msg.role === 'assistant' && msg.seeded) return null;
                if (msg.role === 'user') {
                  return (
                    <div key={idx} className="iv-user-bubble">{msg.content}</div>
                  );
                }
                return (
                  <PipChatBubble key={idx} latest={idx === lastAssistantIdx && !stInterviewLoading && !ivTranscribing}>
                    {msg.content}
                  </PipChatBubble>
                );
              })}

              {ivTranscribing && (
                <div className="iv-transcribing-bubble">Listening to your recording...</div>
              )}

              {stInterviewLoading && !ivTranscribing && (
                <PipChatBubble extraClass="iv-thinking-bubble" latest thinking>
                  Pip is thinking…
                </PipChatBubble>
              )}

              {/* Write-it-up moment card — shown when engine wraps OR when user has answered (secondary) */}
              {/* TB-104 keep-going: non-DONE-SIGNAL wraps show [Keep going][Turn this into a chapter]; DONE-SIGNAL shows only [Turn this into a chapter] */}
              {interviewIsWrapped && ivHasUserAnswer && (
                <div className="iv-writeup-nudge">
                  {interviewWrapReason !== 'DONE-SIGNAL' ? (
                    /* CAP_REACHED or DIMINISHING_RETURNS: soft checkpoint — user chooses depth */
                    <>
                      <p>{isHotSeatConv ? "Want to keep going, or write it up?" : "Want to keep going, or write it up?"}</p>
                      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button onClick={handleInterviewKeepGoing} disabled={stInterviewLoading} // TB-104 keep-going
                          className="st-btn-secondary" style={{ flex: 1, minWidth: 120, fontSize: '1rem', padding: '14px 20px', opacity: stInterviewLoading ? 0.5 : 1 }}>
                          Keep going
                        </button>
                        <button onClick={handleWriteItUp} disabled={stInterviewLoading}
                          className="st-btn-primary" style={{ flex: 1, minWidth: 120, fontSize: '1rem', padding: '14px 20px', opacity: stInterviewLoading ? 0.5 : 1 }}>
                          {DONE_BUTTON_LABEL}
                        </button>
                      </div>
                    </>
                  ) : (
                    /* DONE-SIGNAL: user explicitly said they're done — only Turn this into a chapter */
                    <>
                      <p>{isHotSeatConv ? "That's the set. Want to see the chapter?" : FINISH_CARD_LINE}</p>
                      <button onClick={handleWriteItUp} disabled={stInterviewLoading}
                        className="st-btn-primary" style={{ width: '100%', fontSize: '1.1rem', padding: '16px 24px', opacity: stInterviewLoading ? 0.5 : 1 }}>
                        {DONE_BUTTON_LABEL}
                      </button>
                    </>
                  )}
                  {/* QR keepsake opt-in — only shown when a recording was captured (spec §6 + red-team P2-4) */}
                  {ivKeepsakeCaptured && (
                    <div className="iv-qr-opt-in">
                      <input type="checkbox" checked={ivIncludeQr} onChange={e => {
                        setIvIncludeQr(e.target.checked);
                        setStProcessed(p => ({ ...p, includeQr: e.target.checked }));
                      }} aria-label="Include voice recording as QR keepsake" />
                      <div>
                        <div className="iv-qr-label">Include my voice recording in the book</div>
                        <div className="iv-qr-sub">Readers will be able to scan a QR code to hear your voice.</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Scroll anchor */}
              <div ref={ivConvEndRef} />
              <div ref={stInterviewEndRef} />
            </div>

            {/* Video live preview — floats above capture bar when recording video */}
            {ivVideoMode && ivRecording && (
              <video ref={ivLiveVideoRef} muted playsInline className="iv-video-preview"
                aria-label="Live camera preview" />
            )}

            <div className="iv-capture-dock" ref={ivDockRef}>
            <div className="iv-capture-bar" data-recording={ivRecording ? 'true' : 'false'}>
              {/* FOUNDER RULING 2026-08-10 — THE DICTATION MIC IS REMOVED FROM THE TYPED SCREEN.
                  Verbatim: *"If you want to talk then you go to the talk with pip section, which is
                  designed for it."* He walked it and named what it actually did: tap, record, tap
                  stop, wait for a transcription, and the result was then SENT to Pip without him
                  ever reading it. A mic beside a text box promises talking and delivered a
                  record-and-wait round trip, so the affordance lied about itself.
                  Do NOT reinstate it here. Voice has one door and it is "Talk to Pip". */}

              {/* Text input or recording indicator */}
              {ivRecording ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 48 }}>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    <span className="iv-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(220,80,60,0.7)', display: 'inline-block' }} />
                    <span className="iv-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(220,80,60,0.7)', display: 'inline-block' }} />
                    <span className="iv-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(220,80,60,0.7)', display: 'inline-block' }} />
                  </div>
                  <span style={{ fontFamily: "'Lora', serif", fontSize: '0.88rem', color: 'rgba(220,80,60,0.85)', fontStyle: 'italic' }}>
                    {ivVideoMode ? 'Recording video...' : 'Listening...'}
                  </span>
                </div>
              ) : (
                <textarea
                  ref={ivInputRef}
                  className="iv-capture-input"
                  rows={1}
                  // "Say or type…" invited a way to SAY that no longer exists on this screen.
                  // Constitution §5 forbids copy naming a control that is not there, so the word
                  // went with the mic. MY wording, not his — one word, trivially changed.
                  placeholder={isHotSeatConv ? 'Quick answer…' : 'Type your answer…'}
                  value={stInterviewInput}
                  onChange={e => {
                    setStInterviewInput(e.target.value);
                    autosizeComposer(e.target);
                  }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleConvSend(); } }}
                  disabled={stInterviewLoading || ivTranscribing}
                  aria-label="Your answer"
                />
              )}

              {/* Write-it-up shortcut moved to the steer row (session 132) — it was crushing
                  the text field when rendered inline here. */}

              {/* Send button */}
              <button
                className="iv-send-btn"
                aria-label="Send"
                onClick={() => ivRecording ? stopIvRecording() : handleConvSend()}
                disabled={ivRecording ? false : composerSendDisabled(stInterviewInput, { loading: stInterviewLoading, transcribing: ivTranscribing })}
              >
                {ivRecording ? (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="4" y="4" width="12" height="12" rx="2" fill="#fff" /></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 10h12M10 4l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                )}
              </button>
            </div>
            </div>

            {/* TB-104 steering: confirm before leaving an in-progress interview with unsaved substance */}
            {/* F-103: universal exit guard (shared copy/styles with the voice screen). Primary FINISHES
                (writes up the story); secondary leaves without keeping; the light backdrop dismisses and stays
                in the interview. Doorway orange primary / white secondary — no gold on interactive. */}
            {ivExitConfirm && (
              <div
                style={EXIT_GUARD_SCRIM}
                onClick={() => setIvExitConfirm(false)}
                role="dialog" aria-modal="true" aria-label="Finish or leave this interview"
              >
                <div style={EXIT_GUARD_CARD} onClick={(e) => e.stopPropagation()}>
                  <p style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 700, color: '#2b2b2b', lineHeight: 1.3, margin: '0 0 8px' }}>{EXIT_GUARD_TITLE}</p>
                  <p style={{ fontFamily: "'Lora', serif", fontSize: 15, color: '#6b5c4e', lineHeight: 1.5, margin: '0 0 18px' }}>{EXIT_GUARD_SUB}</p>
                  <button style={CTA_PRIMARY} onClick={exitSaveFirst}>{DONE_BUTTON_LABEL}</button>
                  <div style={{ height: 10 }} />
                  <button style={CTA_SECONDARY} onClick={exitToPicker}>{EXIT_GUARD_LEAVE}</button>
                </div>
              </div>
            )}
          </div>
        );
      }

      // ── ANSWER STEP: the main new flow ──
      // Transcribing recorded audio
      if (transcribing) {
        return (
          <div className="storyteller-content" style={{ textAlign: 'center', paddingTop: 60 }}>
            <div style={{ fontSize: 56, marginBottom: 20 }}>🎧</div>
            <h2 className="st-h2" style={{ fontSize: '1.8rem' }}>Listening to your recording...</h2>
            <p className="st-sub" style={{ marginTop: 12 }}>Transcribing your words. Just a moment.</p>
            <div style={{ width: 48, height: 48, border: '3px solid rgba(15,23,42,0.25)', borderTopColor: 'rgba(15,23,42,0.3)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '40px auto' }} />
          </div>
        );
      }

      // Got a recording — auto-transcribe immediately
      if (storyAudioBlob && storyAudioUrl) {
        if (!stAutoTranscribeTriggered) {
          setStAutoTranscribeTriggered(true);
          setTimeout(async () => {
            setStInterviewStep('generating');
            try {
              const text = await transcribeAudio(storyAudioBlob);
              if (text?.trim()) {
                setStInterviewInput(text);
                const title = stSelectedPrompt?.text || 'My Story';
                setStProcessed({ title, content: text.trim(), raw: true, pendingPhotoFile: stProcessed.pendingPhotoFile || null, pendingPhotoPreview: stProcessed.pendingPhotoPreview || null, audioBlob: storyAudioBlob, videoBlob: storyVideoBlob || null });
                setStInterviewStep('review');
              } else {
                showToast('Could not hear any words. Try again.', 'error');
                setStInterviewStep('conversation'); // TB-104 Stage B: was 'answer'
                setStAutoTranscribeTriggered(false);
              }
              setStoryAudioBlob(null); setStoryAudioUrl('');
            } catch (err) {
              showToast('Transcription failed.', 'error');
              setStInterviewStep('conversation'); // TB-104 Stage B: was 'answer'
              setStAutoTranscribeTriggered(false);
              setStoryAudioBlob(null); setStoryAudioUrl('');
            }
          }, 0);
        }
        return (
          <div className="storyteller-content" style={{ textAlign: 'center', paddingTop: 60 }}>
            <div style={{ fontSize: 56, marginBottom: 20 }}>🎧</div>
            <h2 className="st-h2" style={{ fontSize: '1.8rem' }}>Transcribing your recording...</h2>
            <p className="st-sub" style={{ marginTop: 12 }}>Turning your words into text. Just a moment.</p>
            <div style={{ width: 48, height: 48, border: '3px solid rgba(15,23,42,0.25)', borderTopColor: 'rgba(15,23,42,0.3)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '40px auto' }} />
          </div>
        );
      }

      // Answer screen + active recording (merged — keeps video element mounted)
      return (
        <div className="storyteller-content" style={storyRecording ? { textAlign: 'center' } : {}}>
          {/* Persistent video element — always in DOM when video mode is on */}
          {storyVideoMode && (
            <video ref={storyLiveVideoRef} muted playsInline
              style={{ width: '100%', maxWidth: 400, borderRadius: 16, background: '#1a1612', display: storyRecording ? 'block' : 'none', border: '3px solid rgba(220,80,60,0.4)', margin: '0 auto 16px' }} />
          )}

          {storyRecording ? (
            <>
              {/* Cancel — stop and discard, return to question picker */}
              <button onClick={() => {
                stopStoryRecording();
                setTimeout(() => { setStoryAudioBlob(null); setStoryAudioUrl(''); }, 100);
                setStInterviewMode(false);
                setStInterviewStep('pick');
                setStSelectedPrompt(null);
              }} className="st-back" style={{ textAlign: 'left', width: '100%' }}>← Cancel</button>

              {/* Question reminder — F-67: only when a prompt is actually selected (never bare "") */}
              {stSelectedPrompt?.text && (
                <div className="st-warm-box" style={{ marginBottom: 32, textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>{stSelectedPrompt?.emoji || ''}</div>
                  <p style={{ fontSize: '1.3rem', color: '#0f172a', fontFamily: "'Lora', serif", fontStyle: 'italic', fontWeight: 500 }}>"{stSelectedPrompt.text}"</p>
                </div>
              )}
              {!storyVideoMode && (
                <div className="st-record-btn st-record-active" style={{ margin: '0 auto 32px' }} aria-label="Recording in progress">
                  <Microphone weight="duotone" size={72} color="#c0392b" />
                </div>
              )}
              <h2 className="st-h2" style={{ fontSize: '1.8rem', color: 'rgba(220,80,60,0.9)' }}>Recording{storyVideoMode ? ' video' : ''}...</h2>
              <p className="st-sub" style={{ marginTop: 8, marginBottom: 40 }}>Take your time. Say as much or as little as you'd like.</p>
              <button onClick={stopStoryRecording} className="st-btn-primary" style={{ background: 'rgba(220,80,60,0.2)', borderColor: 'rgba(220,80,60,0.4)', color: '#c0392b' }}>
                ⏹ Stop Recording
              </button>
            </>
          ) : (
            <>
              <button onClick={() => { setStInterviewMode(false); setStInterviewStep('pick'); setStSelectedPrompt(null); }} className="st-back">← Back</button>

              {/* Context banner — which book you're adding to */}
              {activeBook && (
                <div style={{ padding: '10px 16px', background: isGiftBook ? 'rgba(201,168,76,0.12)' : 'rgba(15,23,42,0.04)', border: `1px solid ${isGiftBook ? 'rgba(201,168,76,0.25)' : 'rgba(15,23,42,0.1)'}`, borderRadius: 10, marginBottom: 8, fontSize: '0.92rem', color: '#3c4653' }}>
                  Adding a story to: <strong style={{ color: '#0f172a' }}>{activeBook.title || 'your book'}</strong>
                </div>
              )}

              {/* TB-104 Stage 4: Conversational entry framing — interviewer asks the question warmly.
                  F-67: render the framing+question ONLY when a prompt is selected — a null prompt must never
                  produce "A question about Mom: \"\"" (the discard residue). This screen is unreachable with a
                  null prompt after the discard routing fix; this guard keeps any future path honest too. */}
              {stSelectedPrompt?.text && (
                <div style={{ marginBottom: 20, padding: '18px 20px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.22)', borderRadius: 14 }}>
                  <p style={{ fontSize: '0.9rem', color: '#7a5a10', fontFamily: "'Lora', serif", marginBottom: 8, fontStyle: 'italic' }}>
                    {isGiftBook ? `A question about ${activeBook?.recipientName || 'them'}:` : "Let's talk this through. Here's your question:"}
                  </p>
                  <div style={{ fontSize: 32, marginBottom: 8, textAlign: 'center' }}>{stSelectedPrompt?.emoji || ''}</div>
                  <p style={{ fontSize: 'clamp(1.2rem, 4vw, 1.6rem)', color: '#0f172a', fontFamily: "'Lora', serif", fontStyle: 'italic', fontWeight: 500, lineHeight: 1.45, textAlign: 'center' }}>
                    "{stSelectedPrompt.text}"
                  </p>
                </div>
              )}

              {/* Record button with integrated voice/video toggle */}
              <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
                  <button onClick={() => setStoryVideoMode(false)}
                    style={{ padding: '8px 20px', fontSize: '0.95rem', fontFamily: "'Lora', serif", fontWeight: 600, background: !storyVideoMode ? 'rgba(15,23,42,0.85)' : 'rgba(15,23,42,0.06)', color: !storyVideoMode ? '#fff' : '#3c4653', border: '1px solid rgba(15,23,42,0.15)', borderRadius: 20, cursor: 'pointer' }}>
                     Voice
                  </button>
                  <button onClick={() => setStoryVideoMode(true)}
                    style={{ padding: '8px 20px', fontSize: '0.95rem', fontFamily: "'Lora', serif", fontWeight: 600, background: storyVideoMode ? 'rgba(15,23,42,0.85)' : 'rgba(15,23,42,0.06)', color: storyVideoMode ? '#fff' : '#3c4653', border: '1px solid rgba(15,23,42,0.15)', borderRadius: 20, cursor: 'pointer' }}>
                     Video
                  </button>
                </div>
                <button onClick={startStoryRecording}
                  className="st-record-btn"
                  aria-label={storyVideoMode ? 'Tap to record video' : 'Tap to record audio'}
                  style={{ margin: '0 auto 12px' }}>
                  {storyVideoMode
                    ? <Camera weight="duotone" size={72} color="#0f172a" />
                    : <Microphone weight="duotone" size={72} color="#0f172a" />}
                </button>
                <p style={{ fontSize: '0.95rem', color: '#3c4653' }}>Just {storyVideoMode ? 'look at the camera and' : ''} talk — say as much or as little as you'd like.</p>
              </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid rgba(15,23,42,0.07)', paddingTop: 28, marginBottom: 8 }}>
            <p style={{ fontSize: '1.05rem', color: '#3c4653', textAlign: 'center', marginBottom: 20 }}>Or type your answer</p>
          </div>

          {/* Text input */}
          <textarea className="st-input st-textarea" rows={6}
            placeholder="Type your answer here... rough notes, full sentences, or anything in between."
            value={stInterviewInput} onChange={e => setStInterviewInput(e.target.value)}
            style={{ minHeight: 160, marginBottom: 8 }} />
          <p style={{ fontSize: '1rem', color: '#7a6b5a', marginBottom: 20 }}>{(stInterviewInput || '').split(/\s+/).filter(Boolean).length} words</p>

          {/* Photo upload */}
          <div style={{ marginBottom: 20, padding: '16px 20px', background: 'rgba(15,23,42,0.03)', borderRadius: 12, border: '1px solid rgba(15,23,42,0.08)' }}>
            <label className="st-label" style={{ marginBottom: 10, display: 'block' }}> Add a photo <span style={{ fontWeight: 400, color: '#3c4653' }}>(optional, you can always add one later)</span></label>
            {stProcessed.pendingPhotoPreview ? (
              <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <img src={stProcessed.pendingPhotoPreview} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(15,23,42,0.1)' }} />
                <div>
                  <p style={{ fontSize: '1rem', color: '#0f172a', marginBottom: 4 }}>Photo attached ✓</p>
                  <button onClick={() => setStProcessed(p => ({...p, pendingPhotoFile: null, pendingPhotoPreview: null}))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', color: '#c0392b', padding: 0, fontFamily: "'Lora', serif" }}>Remove</button>
                </div>
              </div>
              <EnhanceNudge />
              </>
            ) : (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 20px', background: 'rgba(15,23,42,0.06)', borderRadius: 8, border: '1px solid rgba(15,23,42,0.12)', fontSize: '1rem', color: '#0f172a', fontFamily: "'Lora', serif" }}>
                 Choose photo
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (!isImageFile(file)) { showToast('Please choose an image file.', 'error'); return; }
                  if (file.size > 10 * 1024 * 1024) { showToast('Image must be under 10 MB.', 'error'); return; }
                  try {
                    const normalizedFile = await normalizeImageForBook(file);
                    const preview = URL.createObjectURL(normalizedFile);
                    setStProcessed(p => ({...p, pendingPhotoFile: normalizedFile, pendingPhotoPreview: preview}));
                  } catch (err) { showToast('Could not load photo.', 'error'); }
                }} />
              </label>
            )}
          </div>

          {/* QR code opt-in for voice recordings */}
          {stProcessed.audioBlob && (
            <div style={{ marginBottom: 20, padding: '16px 20px', background: 'rgba(138,109,58,0.06)', borderRadius: 12, border: '1px solid rgba(138,109,58,0.15)' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={stProcessed.includeQr || false} onChange={e => setStProcessed(p => ({...p, includeQr: e.target.checked}))}
                  style={{ marginTop: 4, width: 18, height: 18, accentColor: '#0f172a' }} />
                <div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a', fontWeight: 500 }}>{stProcessed.videoBlob ? '' : ''} Include {stProcessed.videoBlob ? 'video' : 'voice'} recording in printed book</div>
                  <div style={{ fontSize: '0.92rem', color: '#3c4653', marginTop: 4, lineHeight: 1.5 }}>A QR code will appear in the book so readers can scan and {stProcessed.videoBlob ? 'watch' : 'hear'} your original recording.</div>
                </div>
              </label>
            </div>
          )}

          {/* TB-104 Stage 4: Primary action — opens the conversation (always enters chat step) */}
          <div style={{ marginBottom: 16 }}>
            <button disabled={!stInterviewInput?.trim()} onClick={() => submitFirstAnswer(stInterviewInput, null, false)}
              className="st-btn-primary" style={{ width: '100%', fontSize: '1.1rem', padding: '16px 24px' }}>
              Keep going with Pip
            </button>
            <p style={{ fontSize: '0.88rem', color: '#3c4653', textAlign: 'center', marginTop: 8, fontFamily: "'Lora', serif", fontStyle: 'italic' }}>
              We'll talk it through together, then write it up.
            </p>
          </div>

          {/* TB-104 Stage 4: Secondary action — skip the conversation, generate directly */}
          <div style={{ marginBottom: 8, padding: '14px 16px', background: 'rgba(15,23,42,0.03)', borderRadius: 10, border: '1px solid rgba(15,23,42,0.07)' }}>
            <p style={{ fontSize: '0.9rem', color: '#3c4653', textAlign: 'center', marginBottom: 10, fontFamily: "'Lora', serif" }}>Or just write it up now:</p>
            {/* TB-104 Stage 4: skipConversation=true bypasses chat, goes straight to generation */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button disabled={!stInterviewInput?.trim()} onClick={() => submitFirstAnswer(stInterviewInput, 'cleanup', true)}
                className="st-btn-secondary" style={{ flex: 1, minWidth: 140, fontSize: '0.88rem', borderColor: 'rgba(15,23,42,0.18)', padding: '10px 14px' }}>
                Clean this up for me
              </button>
              <button disabled={!stInterviewInput?.trim()} onClick={() => submitFirstAnswer(stInterviewInput, 'chapter', true)}
                className="st-btn-secondary" style={{ flex: 1, minWidth: 140, fontSize: '0.88rem', padding: '10px 14px' }}>
                Rewrite this beautifully
              </button>
            </div>
          </div>

          {/* Chapter title input for Save My Own Words */}
          <div style={{ marginBottom: 12 }}>
            <label className="st-label">Chapter title</label>
            <input className="st-input" placeholder="Give this chapter a title..."
              value={stProcessed.ownWordsTitle ?? (promptToChapterTitle(stSelectedPrompt?.text || '') || '')}
              onChange={e => setStProcessed(p => ({ ...p, ownWordsTitle: e.target.value }))} />
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button disabled={!stInterviewInput?.trim()} onClick={async () => {
              ensureBookExists();
              try {
                const title = (stProcessed.ownWordsTitle?.trim()) || promptToChapterTitle(stSelectedPrompt?.text || '') || 'My Story';
                // Save media to S3 — prefer video if present
                const vidBlob = stProcessed.videoBlob;
                const audBlob = stProcessed.audioBlob;
                const isVideo = !!vidBlob;
                const recBlob = isVideo ? vidBlob : audBlob;
                let mediaPath = null;
                if (recBlob) {
                  try {
                    const safeName = title.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '_');
                    const fileName = `${safeName}_${Date.now()}.webm`;
                    const { fetchAuthSession } = await import('aws-amplify/auth');
                    const session = await fetchAuthSession();
                    const identityId = session.identityId;
                    const folder = isVideo ? 'video' : 'audio';
                    mediaPath = `private/${identityId}/${folder}/${fileName}`;
                    await uploadData({ path: mediaPath, data: recBlob, options: { contentType: isVideo ? 'video/webm' : 'audio/webm', metadata: { title } } });
                  } catch (mediaErr) { console.warn('[Media Upload] Failed:', mediaErr.message); mediaPath = null; }
                }
                let contentToSave = stInterviewInput.trim();
                if (mediaPath) { contentToSave += `\n\n[${isVideo ? 'video' : 'audio'}:${mediaPath}]`; }
                const result = await client.graphql({ query: createLegacyEntry, variables: { input: { userId: user?.userId || user?.username, title, content: contentToSave, type: 'story', createdAt: new Date().toISOString() } } });
                const newStoryId = result.data.createLegacyEntry.id;
                if (newStoryId) {
                  let photoPath = null, photoUrl = null, photoBase64 = null;
                  if (stProcessed.pendingPhotoFile) {
                    try {
                      const file = stProcessed.pendingPhotoFile;
                      const ext = file.name.split('.').pop() || 'jpg';
                      const fileName = `book_photo_${Date.now()}.${ext}`;
                      const pathBuilder = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
                      const uploadResult = await (uploadData({ path: pathBuilder, data: file, options: { contentType: file.type } })).result;
                      photoPath = uploadResult?.path || null;
                      if (photoPath) {
                        const urlResult = await getUrl({ path: photoPath });
                        photoUrl = urlResult?.url?.toString() || null;
                        photoBase64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
                      }
                    } catch (photoErr) { console.warn('[Photo Upload] Failed during save:', photoErr.message); }
                  }
                  const newPage = { storyId: newStoryId, photoUrl, photoPath, photoBase64, caption: '', photoSize: 'large', layout: photoPath ? 'photo-spread' : 'classic' };
                  const updatedPages = [...stBookPages, newPage];
                  setStBookPages(updatedPages);
                  const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverPhotoPath: stBookCoverPhotoPath, pages: cleanPagesForSave(updatedPages) };
                  saveBookData(bookData);
                  if (mediaPath) {
                    await autoLinkRecordingToQr(newStoryId, mediaPath, title, updatedPages, isVideo ? 'VIDEO' : 'AUDIO');
                  }
                }
                showToast(`Story saved to ${stBookTitle || 'your book'}!`);
                await refreshStories();
                setStInterviewMode(false); setStInterviewStep('pick'); setStInterviewInput(''); setStSelectedPrompt(null); setStProcessed({ title: '', content: '' });
                setStBookStep(0); setStBookOpen(false); setActiveTabRaw('book'); window.scrollTo(0, 0);
              } catch (e) { showToast('Save failed.', 'error'); }
            }}
              className="st-btn-secondary" style={{ flex: 1, minWidth: 200 }}>
              💾 Save My Own Words
            </button>
          </div>
            </>
          )}
        </div>
      );
    }

    // ── SIMPLE RECORD MODE (original) ──
    // Processing
    if (stPostRecordStep === 'processing') {
      return (
        <div className="storyteller-content" style={{ textAlign: 'center', paddingTop: 60 }}>
          <div style={{ fontSize: 56, marginBottom: 20 }}>{transcribing ? '🎧' : ''}</div>
          <h2 className="st-h2" style={{ fontSize: '1.8rem' }}>{transcribing ? 'Listening...' : 'Creating your story...'}</h2>
          {/* F-334: was "This takes about 15–30 seconds." — an EN-DASH (brand rule) and a second,
              contradictory wait promise on a screen in the same flow. One sentence, one promise. */}
          <p className="st-sub" style={{ marginTop: 12 }}>This usually takes under a minute.</p>
          <div style={{ width: 48, height: 48, border: '3px solid rgba(15,23,42,0.25)', borderTopColor: 'rgba(15,23,42,0.3)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '40px auto' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }

    // Review polished story
    if (stPostRecordStep === 'review' && stProcessed.content) {
      return (
        <div className="storyteller-content">
          <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 4 }}>
            {stProcessed.raw ? 'Your Words' : 'Cleaned Up'}
          </h2>
          <p className="st-sub" style={{ marginBottom: 24 }}>
            {stProcessed.raw ? 'Here\'s what you said. Edit it however you like, or let us clean it up.' : 'Edit anything you\'d like, add a photo, then save it to your book.'}
          </p>
          <div style={{ marginBottom: 16 }}>
            <label className="st-label">Title</label>
            <input className="st-input" value={stProcessed.title} onChange={e => setStProcessed({...stProcessed, title: e.target.value})} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label className="st-label">{stProcessed.raw ? 'Your words (edit freely)' : 'Your story'}</label>
            <textarea className="st-input st-textarea" rows={12} value={stProcessed.content} onChange={e => setStProcessed({...stProcessed, content: e.target.value})} />
          </div>
          {/* 3-tier cleanup options — always visible, active tier highlighted */}
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: '0.95rem', color: '#3c4653', textAlign: 'center', marginBottom: 2, fontFamily: "'Lora', serif" }}>
              {stProcessed.raw ? 'How much help do you want?' : 'Try a different version?'}
            </p>
            <p style={{ fontSize: '0.88rem', color: '#3c4653', textAlign: 'center', marginBottom: 10, fontStyle: 'italic' }}>Light cleanup or a full rewrite — your voice stays either way.</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { tier: 'cleanup', icon: <Sparkle size={16} weight="duotone" />, label: 'Clean this up for me' },
                { tier: 'chapter', icon: <BookOpenText size={16} weight="duotone" />, label: 'Rewrite this beautifully' },
              ].map(({ tier, icon, label }) => {
                const isActive = stProcessed.polishTier === tier;
                const isCached = stProcessed.tierCache?.[tier];
                return (
                  <button key={tier} onClick={() => handleStPolishStory(tier)} className="st-btn-secondary" style={{
                    flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.95rem',
                    background: isActive ? 'rgba(15,23,42,0.12)' : 'transparent',
                    borderColor: isActive ? 'rgba(15,23,42,0.35)' : 'rgba(15,23,42,0.12)',
                    fontWeight: isActive ? 600 : 400,
                  }}>
                    {icon} {label}{isCached && !isActive ? ' ✓' : ''}
                  </button>
                );
              })}
            </div>
            {stProcessed.polishTier && (
              <button onClick={() => {
                const orig = stProcessed.originalContent || stProcessed.content;
                const origTitle = stProcessed.originalTitle || stProcessed.title;
                setStProcessed(p => ({ ...p, title: origTitle, content: orig, raw: true, polishTier: null }));
              }} style={{ display: 'block', margin: '10px auto 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', color: '#3c4653', fontFamily: "'Lora', serif" }}>
                ↩ Back to my original words
              </button>
            )}
          </div>

          {/* Photo upload */}
          <div style={{ marginBottom: 20, padding: '16px 20px', background: 'rgba(15,23,42,0.03)', borderRadius: 12, border: '1px solid rgba(15,23,42,0.08)' }}>
            <label className="st-label" style={{ marginBottom: 10, display: 'block' }}> Add a photo <span style={{ fontWeight: 400, color: '#3c4653' }}>(optional, you can always add one later)</span></label>
            {stProcessed.pendingPhotoPreview ? (
              <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <img src={stProcessed.pendingPhotoPreview} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(15,23,42,0.1)' }} />
                <div>
                  <p style={{ fontSize: '1rem', color: '#0f172a', marginBottom: 4 }}>Photo attached ✓</p>
                  <button onClick={() => setStProcessed(p => ({...p, pendingPhotoFile: null, pendingPhotoPreview: null}))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', color: '#c0392b', padding: 0, fontFamily: "'Lora', serif" }}>Remove</button>
                </div>
              </div>
              <EnhanceNudge />
              </>
            ) : (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 20px', background: 'rgba(15,23,42,0.06)', borderRadius: 8, border: '1px solid rgba(15,23,42,0.12)', fontSize: '1rem', color: '#0f172a', fontFamily: "'Lora', serif" }}>
                 Choose photo
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (!isImageFile(file)) { showToast('Please choose an image file.', 'error'); return; }
                  if (file.size > 10 * 1024 * 1024) { showToast('Image must be under 10 MB.', 'error'); return; }
                  try {
                    const normalizedFile = await normalizeImageForBook(file);
                    const preview = URL.createObjectURL(normalizedFile);
                    setStProcessed(p => ({...p, pendingPhotoFile: normalizedFile, pendingPhotoPreview: preview}));
                  } catch (err) { showToast('Could not load photo.', 'error'); }
                }} />
              </label>
            )}
          </div>

          {/* QR code opt-in for voice recordings */}
          {(stProcessed.audioBlob || storyAudioBlob) && (
            <div style={{ marginBottom: 20, padding: '16px 20px', background: 'rgba(138,109,58,0.06)', borderRadius: 12, border: '1px solid rgba(138,109,58,0.15)' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={stProcessed.includeQr || false} onChange={e => setStProcessed(p => ({...p, includeQr: e.target.checked}))}
                  style={{ marginTop: 4, width: 18, height: 18, accentColor: '#0f172a' }} />
                <div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a', fontWeight: 500 }}>{stProcessed.videoBlob ? '' : ''} Include {stProcessed.videoBlob ? 'video' : 'voice'} recording in printed book</div>
                  <div style={{ fontSize: '0.92rem', color: '#3c4653', marginTop: 4, lineHeight: 1.5 }}>A QR code will appear in the book so readers can scan and {stProcessed.videoBlob ? 'watch' : 'hear'} your original recording.</div>
                </div>
              </label>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={handleStSaveRecorded} disabled={stSaving} className="st-btn-primary" style={{ flex: 1, opacity: stSaving ? 0.6 : 1 }}>{stSaving ? 'Saving…' : '✓ Save & Add to Book'}</button>
            <button onClick={() => { setStPostRecordStep(null); setStProcessed({title:'',content:''}); setStoryAudioBlob(null); setStoryAudioUrl(''); setStoryVideoBlob(null); setStoryVideoUrl(''); }} className="st-btn-secondary">✕ Start Over</button>
          </div>
        </div>
      );
    }

    // Prompt picker — sequential style
    if (stShowPromptPicker) {
      const pickerPrompt = stAiQuestion || ST_PROMPTS[stShuffleIdx % ST_PROMPTS.length];
      return (
        <div className="storyteller-content" style={{ textAlign: 'center' }}>
          <button onClick={() => setStShowPromptPicker(false)} className="st-back" style={{ textAlign: 'left', width: '100%' }}>← Back</button>
          <div style={{ paddingTop: 20, maxWidth: 640, margin: '0 auto' }}>
            <h2 className="st-h2" style={{ fontSize: '1.8rem', marginBottom: 8 }}>Pick a Question</h2>
            <p className="st-sub" style={{ marginBottom: 36 }}>Choose one that sounds interesting.</p>
            <button onClick={() => { setStSelectedPrompt(pickerPrompt); setStShowPromptPicker(false); setStAiQuestion(null); }}
              style={{ display: 'block', width: '100%', background: 'rgba(15,23,42,0.04)', border: '2px solid rgba(15,23,42,0.14)', borderRadius: 16, padding: '36px 28px', cursor: 'pointer', marginBottom: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 14 }}>{pickerPrompt.emoji}</div>
              <div style={{ fontSize: '1.5rem', color: '#0f172a', lineHeight: 1.5, fontFamily: "'Lora', serif", fontStyle: 'italic', fontWeight: 500 }}>"{pickerPrompt.text}"</div>
              <div style={{ marginTop: 16, fontSize: '1.15rem', color: '#1a1612', fontFamily: "'Lora', serif", letterSpacing: 1, fontWeight: 600 }}>Use this question →</div>
            </button>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => { setStAiQuestion(null); setStShuffleIdx(prev => (prev + 1) % activePrompts.length); }}
                style={{ background: 'rgba(15,23,42,0.05)', border: '2px solid rgba(15,23,42,0.14)', borderRadius: 12, padding: '14px 24px', cursor: 'pointer', fontSize: '1.1rem', color: '#1a1612', fontFamily: "'Lora', serif", letterSpacing: 1, fontWeight: 500 }}>
                🔀 Next question
              </button>
              <button onClick={generateAiQuestion} disabled={stAiQuestionLoading}
                style={{ background: 'rgba(15,23,42,0.05)', border: '2px solid rgba(15,23,42,0.14)', borderRadius: 12, padding: '14px 24px', cursor: 'pointer', fontSize: '1.1rem', color: '#1a1612', fontFamily: "'Lora', serif", letterSpacing: 1, fontWeight: 500, opacity: stAiQuestionLoading ? 0.5 : 1 }}>
                {stAiQuestionLoading ? ' Thinking...' : ' Surprise me'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Got a recording ready to process — auto-transcribe immediately
    if (storyAudioBlob && storyAudioUrl && !storyRecording && !stPostRecordStep) {
      // Trigger transcription on next tick to avoid calling during render
      if (!stAutoTranscribeTriggered) {
        setStAutoTranscribeTriggered(true);
        setTimeout(() => handleStPostRecord(), 0);
      }
      return (
        <div className="storyteller-content" style={{ textAlign: 'center', paddingTop: 60 }}>
          <div style={{ fontSize: 56, marginBottom: 20 }}>🎧</div>
          <h2 className="st-h2" style={{ fontSize: '1.8rem' }}>Transcribing your recording...</h2>
          <p className="st-sub" style={{ marginTop: 12 }}>Turning your words into text. Just a moment.</p>
          <div style={{ width: 48, height: 48, border: '3px solid rgba(15,23,42,0.25)', borderTopColor: 'rgba(15,23,42,0.3)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '40px auto' }} />
        </div>
      );
    }

    // Default: curated questions + AI option
    // Both book modes draw from the chosen prompt pack or, for gift books, the third-person themes:
    //  - personal book                    -> the pack's "My Story" (first-person) wording
    //  - gift book + 'self' vantage       -> the pack's first-person wording (recipient writing own)
    //  - gift book + any other vantage    -> THIRD_PERSON_THEMES (TB-108 wiring)
    const needsAudienceChoice = false; // retired: packs use [Name] framing for all books
    const recipientName = (activeBook?.recipientName || '').trim();

    // Resolve interview mode: determines whether to use third-person themes or prompt packs.
    // TB-108: all gift books (including partner vantage) now use third-person themes.
    // The authorRelationship field is set during Celebration book creation.
    const rawRelationship = activeBook?.authorRelationship || '';
    const authorVantage = isGiftBook ? getAuthorVantage(rawRelationship) : 'self';
    const occasionId = isGiftBook ? (activeBook?.occasion || '') : '';
    const interviewMode = resolveInterviewMode({
      isCelebration: isGiftBook,
      authorRelationship: rawRelationship,
      occasionId,
    });
    // isThirdPersonMode: true when the interview should use THIRD_PERSON_THEMES
    const isThirdPersonMode = interviewMode === 'third-person' || interviewMode === 'third-person-memory';
    // useFirstPersonPrompts: only true for personal books and 'self' vantage gift books
    const useFirstPersonPrompts = !isGiftBook || authorVantage === 'self';

    // TB-108: Build theme-based prompts for third-person gift books.
    // Each theme becomes a "topic" in the picker; questions within the theme are the prompt list.
    // The active theme id is tracked via activePackId (theme ids match PACK_CONFIG keys).
    let thirdPersonThemeOrder = null; // ordered array of theme ids for this occasion
    let thirdPersonBranch = null;     // getBranchType result for the author's relationship
    let thirdPersonActiveTheme = null; // the currently selected THIRD_PERSON_THEMES entry
    let thirdPersonActiveQuestions = []; // resolved questions for the active theme
    // Compute pronouns object -- hoisted so heading render sites (activePack.name, theme picker) can use it
    const recipientPronouns = (() => {
      const p = activeBook?.recipientPronouns;
      if (p === 'she') return { subject: 'she', object: 'her', possessive: 'her', objectPoss: 'hers' };
      if (p === 'he')  return { subject: 'he',  object: 'him', possessive: 'his', objectPoss: 'his' };
      return null; // defaults to they/them in resolveThirdPersonPromptText
    })();
    if (isThirdPersonMode) {
      // Rank 1: compute branch FIRST, then pass it into the theme order function
      thirdPersonBranch = getBranchType(rawRelationship);
      thirdPersonThemeOrder = getThirdPersonThemeOrder(occasionId, thirdPersonBranch);
      // Determine active theme: use activePackId if it's a valid theme id, else first in order.
      const validThemeId = thirdPersonThemeOrder.includes(activePackId)
        ? activePackId
        : thirdPersonThemeOrder[0];
      thirdPersonActiveTheme = THIRD_PERSON_THEMES.find(t => t.id === validThemeId) || THIRD_PERSON_THEMES[0];
      // Rank 2: Q0 occasion opener: prepend when we're on the first theme and occasion has an opener
      const rawThemeQuestions = getThirdPersonPrompts(
        thirdPersonActiveTheme.id,
        thirdPersonBranch,
        occasionId
      );
      const isFirstTheme = validThemeId === thirdPersonThemeOrder[0];
      const occasionOpener = (isFirstTheme && occasionId)
        ? getOccasionOpeningQuestion(occasionId, thirdPersonBranch)
        : null;
      // Q0 de-dup: when an occasion opener is prepended, suppress the first theme question
      // if it asks for the same core ask as the opener (same signature-detail or reputation summary).
      // Collision map: opener id -> theme question id(s) to suppress from slot 0-1.
      //
      // TB-108 Final Cleanup: TPOCC-RT-OB, TPOCC-GPD-GC, TPOCC-BD-F, TPOCC-BD-SB openers were
      // re-aimed in thirdPersonThemes.js so they no longer collide with their paired theme questions.
      // Those entries have been removed. TPOCC-FD-C and TPOCC-MD-C remain: both openers touch
      // "early/first memory" territory and the TPMT-1 child variant covers overlapping ground.
      const Q0_COLLISION_SUPPRESSES = {
        'TPOCC-FD-C': ['TPMT-1'], // fathers day child: "early memory" opener vs TPMT-1 child variant
        'TPOCC-MD-C': ['TPMT-1'], // mothers day child: "favorite memory" opener vs TPMT-1 child variant
      };
      let themeQuestionsForAssembly = rawThemeQuestions;
      if (occasionOpener) {
        const suppressIds = Q0_COLLISION_SUPPRESSES[occasionOpener.id] || [];
        if (suppressIds.length > 0) {
          // Only suppress from within the first 3 positions to avoid removing questions entirely
          const firstThreeIds = rawThemeQuestions.slice(0, 3).map(q => q.id);
          const shouldSuppress = suppressIds.some(sid => firstThreeIds.includes(sid));
          if (shouldSuppress) {
            themeQuestionsForAssembly = rawThemeQuestions.filter((q, idx) =>
              idx >= 3 || !suppressIds.includes(q.id)
            );
          }
        }
      }
      const allThemeQuestions = occasionOpener
        ? [occasionOpener, ...themeQuestionsForAssembly]
        : rawThemeQuestions;
      thirdPersonActiveQuestions = allThemeQuestions.map((q) => ({
        id: `${thirdPersonActiveTheme.id}:${q.id}`,
        emoji: '✦',
        // Pass occasionId so [parentRole] in TPMT-7-C resolves correctly (rank 5 fix)
        text: resolveThirdPersonPromptText(q.text, recipientName, recipientPronouns, { occasionId }),
      }));
    }

    // Build a synthetic occasion pack for non-third-person gift books whose occasion has themed questions.
    // This path is only reached for 'self' vantage gift books or personal books.
    // MOTHERS_DAY is audience-aware: partner vantage formerly used MOTHERS_DAY_PARTNER; that path
    // is now unreachable for gift books (partner routes to third-person), so MOTHERS_DAY is used
    // only when the author is writing as the recipient (self vantage) or for a personal book.
    const occasionPromptKey = (() => {
      if (isThirdPersonMode) return null; // third-person books don't use occasion packs
      if (!occasionId || occasionId === 'OTHER') return null;
      if (occasionId === 'MOTHERS_DAY') return 'MOTHERS_DAY';
      return CELEBRATION_PROMPTS[occasionId] ? occasionId : null;
    })();
    const occasionMeta = occasionPromptKey ? OCCASION_TYPES.find(o => o.id === occasionId) : null;
    const occasionPack = occasionMeta && occasionPromptKey ? {
      id: 'occasion',
      name: occasionMeta.label,
      emoji: occasionMeta.emoji,
      // pre-built prompts already in final display shape (no [Name] substitution needed)
      occasionPrompts: CELEBRATION_PROMPTS[occasionPromptKey].map((p, i) => ({
        id: `occasion:${i}`,
        emoji: p.emoji,
        text: p.text,
      })),
    } : null;

    let activePack = null;
    let activePrompts = [];

    if (isThirdPersonMode) {
      // Third-person gift books use theme questions. activePack is used only for the chip label.
      activePack = {
        id: thirdPersonActiveTheme.id,
        name: getThemeDisplayName(thirdPersonActiveTheme.id, thirdPersonBranch, recipientName, recipientPronouns),
        emoji: '✦',
      };
      activePrompts = thirdPersonActiveQuestions;
    } else {
      activePack = activePackId === 'occasion' && occasionPack
        ? occasionPack
        : (PROMPT_PACKS.find(p => p.id === activePackId) || PROMPT_PACKS.find(p => p.isDefault) || PROMPT_PACKS[0]);
      // Observer-vantage gift books only use prompts with celebration wording. If the active pack
      // has none (e.g. a personal-only pack carried over via the saved pack preference), fall back
      // to one that does so activePrompts is never empty (an empty list crashed the page).
      if (activePack !== occasionPack && isGiftBook && !useFirstPersonPrompts && activePack.prompts && !activePack.prompts.some(p => p.celebration)) {
        activePack = PROMPT_PACKS.find(p => p.prompts.some(pr => pr.celebration)) || activePack;
      }
      activePrompts = activePack === occasionPack
        ? occasionPack.occasionPrompts
        : (isGiftBook && !useFirstPersonPrompts ? activePack.prompts.filter(p => p.celebration) : activePack.prompts)
            .map(p => ({
              id: `${activePack.id}:${p.id}`,
              emoji: activePack.emoji,
              text: isGiftBook && !useFirstPersonPrompts
                ? p.celebration.replace(/\[Name\]/g, recipientName || 'them')
                : p.self,
            }));
    }

    const currentPrompt = stAiQuestion || activePrompts[stShuffleIdx % Math.max(activePrompts.length, 1)];
    const isShowingAiQuestion = !!stAiQuestion;

    // Track which prompts have been answered — scoped to active book's pages
    const bookStoryIds = new Set((stBookPages || []).map(p => p.storyId));
    const bookStories = stories.filter(s => bookStoryIds.has(s.id));
    const answeredPromptIds = activePrompts.filter(p =>
      bookStories.some(s => s.title && (s.title === p.text || s.title.toLowerCase().includes(p.text.toLowerCase().slice(0, 20))))
    ).map(p => p.id);

    // Audience selector for Mother's Day
    if (needsAudienceChoice) {
      const goBack = () => { setActiveTab('st-book'); };
      return (
        <div className="storyteller-content" style={{ textAlign: 'center' }}>
          <button onClick={goBack} className="st-back" style={{ textAlign: 'left', width: '100%' }}>← Back</button>
          {activeBook && (
            <div style={{ padding: '10px 16px', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 10, marginBottom: 8, fontSize: '0.92rem', color: '#3c4653' }}>
              Adding a story to: <strong style={{ color: '#0f172a' }}>{activeBook.title || 'your book'}</strong>
            </div>
          )}
          <div style={{ paddingTop: 40, maxWidth: 520, margin: '0 auto' }}>
            <h2 className="st-h2" style={{ fontSize: '1.9rem', marginBottom: 12 }}>Who are you writing about?</h2>
            <p className="st-sub" style={{ fontSize: '1.1rem', marginBottom: 36 }}>We'll tailor the questions to fit.</p>

            <button onClick={() => { setMothersAudience('mom'); setStShuffleIdx(0); }}
              style={{ display: 'block', width: '100%', background: '#fffdf8', border: '2px solid rgba(15,23,42,0.12)', borderRadius: 16, padding: '28px 24px', cursor: 'pointer', marginBottom: 14, transition: 'all 0.15s', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(201,168,76,0.5)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(15,23,42,0.12)'}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontSize: '2.2rem', flexShrink: 0, width: 48, textAlign: 'center' }}>👩</div>
                <div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '1.15rem', fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>My Mom</div>
                  <div style={{ fontSize: '1rem', color: '#3c4653', lineHeight: 1.5 }}>I'm her son or daughter, writing about growing up with her.</div>
                </div>
              </div>
            </button>

            <button onClick={() => { setMothersAudience('partner'); setStShuffleIdx(0); }}
              style={{ display: 'block', width: '100%', background: '#fffdf8', border: '2px solid rgba(15,23,42,0.12)', borderRadius: 16, padding: '28px 24px', cursor: 'pointer', marginBottom: 14, transition: 'all 0.15s', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(201,168,76,0.5)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(15,23,42,0.12)'}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontSize: '2.2rem', flexShrink: 0, width: 48, textAlign: 'center' }}>💑</div>
                <div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '1.15rem', fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>My Wife / Partner</div>
                  <div style={{ fontSize: '1rem', color: '#3c4653', lineHeight: 1.5 }}>I'm her partner, writing about her as a mother.</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      );
    }

    // Path room is opening. Do not paint the pack list or the prompt cards.
    if (pathOpenPackId && pathOpenPackId !== 'hot-seat') {
      return (
        <div className="storyteller-content" data-testid="path-room-opening" style={{ minHeight: '40vh', background: '#FBF8F1' }} />
      );
    }

    // Topic picker: the mobile-first front door.
    // For third-person gift books: shows the 7 THIRD_PERSON_THEMES as topics.
    // For all other books: shows PROMPT_PACKS (existing behavior unchanged).
    if (stShowPackPicker || !stPackChosen) {
      // ── Third-person gift book: theme picker (TB-108) ──
      if (isThirdPersonMode) {
        const selectTheme = (themeId) => {
          setActivePackId(themeId);
          setStShuffleIdx(0);
          setStAiQuestion(null);
          setStPackChosen(true);
          setStShowPackPicker(false);
          try { localStorage.setItem('tb_packChosen', '1'); } catch (e) {}
        };
        const effectiveActiveThemeId = thirdPersonThemeOrder && thirdPersonThemeOrder.includes(activePackId)
          ? activePackId
          : (thirdPersonThemeOrder ? thirdPersonThemeOrder[0] : null);
        return (
          <div className="storyteller-content">
            <button onClick={() => setActiveTab('talk')} className="st-back" style={{ textAlign: 'left', width: '100%' }}>← Back</button>
            <div style={{ paddingTop: 14, maxWidth: 560, margin: '0 auto' }}>
              <div style={{ textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#7a5a10', marginBottom: 8 }}>Topics</div>
              <h2 className="st-h2" style={{ fontSize: '1.7rem', marginBottom: 6, textAlign: 'center' }}>
                What would you like to share about {recipientName || 'them'}?
              </h2>
              <p className="st-sub" style={{ marginBottom: 22, textAlign: 'center' }}>Pick a topic and Pip will ask you a question about it.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(thirdPersonThemeOrder || []).map(themeId => {
                  const theme = THIRD_PERSON_THEMES.find(t => t.id === themeId);
                  if (!theme) return null;
                  const selected = themeId === effectiveActiveThemeId;
                  // Count answered stories whose title matches any question from this theme.
                  const themeQuestions = getThirdPersonPrompts(themeId, thirdPersonBranch, occasionId)
                    .map(q => resolveThirdPersonPromptText(q.text, recipientName));
                  const done = themeQuestions.filter(qt =>
                    bookStories.some(s => s.title && (s.title === qt || s.title.toLowerCase().includes(qt.toLowerCase().slice(0, 20))))
                  ).length;
                  const total = themeQuestions.length;
                  return (
                    <button key={themeId}
                      onClick={() => selectTheme(themeId)}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
                        background: selected ? 'rgba(201,168,76,0.10)' : '#fffdf8',
                        border: `2px solid ${selected ? 'rgba(201,168,76,0.6)' : 'rgba(15,23,42,0.12)'}`,
                        borderRadius: 16, padding: '15px 15px', cursor: 'pointer' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: "'Lora', serif", fontSize: '1.12rem', fontWeight: 700, color: '#0f172a' }}>{getThemeDisplayName(themeId, thirdPersonBranch, recipientName, recipientPronouns)}</div>
                      </div>
                      <div style={{ flexShrink: 0, fontSize: '0.8rem', color: done > 0 ? '#27ae60' : '#9ca3af', fontWeight: 600 }}>{done}/{total}</div>
                    </button>
                  );
                })}
              </div>
              {/* Escape hatch: skip the questions and free-write a story */}
              <button onClick={() => setActiveTab('st-write')}
                style={{ display: 'block', width: '100%', marginTop: 14, background: 'transparent', border: '1.5px dashed rgba(15,23,42,0.28)', borderRadius: 16, padding: '16px 14px', cursor: 'pointer', textAlign: 'center', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653', lineHeight: 1.5 }}>
                <b style={{ color: '#0f172a' }}>Rather just write?</b><br />Skip the questions and write in your own words &rarr;
              </button>
            </div>
          </div>
        );
      }

      // ── Standard pack picker (personal books + self-vantage gift books) ──
      // packModePrompts returns the displayable text strings for a pack (used for answered-count).
      // The occasion pack has pre-built text; standard packs need the usual gift/self mapping.
      const packModePrompts = (pack) => {
        if (pack.id === 'occasion' && pack.occasionPrompts) {
          return pack.occasionPrompts.map(p => p.text);
        }
        return (isGiftBook ? pack.prompts.filter(p => p.celebration) : pack.prompts)
          .map(p => (isGiftBook ? p.celebration.replace(/\[Name\]/g, recipientName || 'them') : p.self));
      };
      const packAnsweredCount = (pack) => packModePrompts(pack).filter(t =>
        bookStories.some(s => s.title && (s.title === t || s.title.toLowerCase().includes(t.toLowerCase().slice(0, 20))))
      ).length;
      // For gift books with a themed occasion pack, default the active selection to the occasion
      // pack so it opens highlighted. Only applies when nothing has been explicitly chosen yet
      // (activePackId is the default or is already 'occasion').
      const effectiveDefaultPackId = occasionPack && (activePackId === (PROMPT_PACKS.find(p => p.isDefault) || PROMPT_PACKS[0]).id || activePackId === 'occasion')
        ? 'occasion'
        : activePackId;
      return (
        <div className="storyteller-content">
          <button onClick={() => setActiveTab('talk')} className="st-back" style={{ textAlign: 'left', width: '100%' }}>← Back</button>
          <div style={{ paddingTop: 14, maxWidth: 560, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#7a5a10', marginBottom: 8 }}>Prompt Packs</div>
            <h2 className="st-h2" style={{ fontSize: '1.7rem', marginBottom: 6, textAlign: 'center' }}>What do you feel like talking about?</h2>
            <p className="st-sub" style={{ marginBottom: 22, textAlign: 'center' }}>Pick a topic and Pip will interview you about it. Switch anytime.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Occasion-themed pack appears first for gift books — it is the recommended starting point */}
              {occasionPack && (() => {
                const total = occasionPack.occasionPrompts.length;
                const done = packAnsweredCount(occasionPack);
                const selected = effectiveDefaultPackId === 'occasion';
                return (
                  <button key="occasion"
                    onClick={() => { setActivePackId('occasion'); setStShuffleIdx(0); setStAiQuestion(null); setStPackChosen(true); setStShowPackPicker(false); try { localStorage.setItem('tb_packChosen', '1'); } catch (e) {} }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
                      background: selected ? 'rgba(201,168,76,0.10)' : '#fffdf8',
                      border: `2px solid ${selected ? 'rgba(201,168,76,0.6)' : 'rgba(15,23,42,0.12)'}`,
                      borderRadius: 16, padding: '15px 15px', cursor: 'pointer' }}>
                    <div style={{ fontSize: '2rem', flexShrink: 0, width: 44, textAlign: 'center' }}>{occasionPack.emoji}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: "'Lora', serif", fontSize: '1.12rem', fontWeight: 700, color: '#0f172a' }}>{occasionPack.name}</span>
                        <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#7a5a10', background: 'rgba(201,168,76,0.16)', padding: '2px 8px', borderRadius: 20 }}>Themed</span>
                      </div>
                      <div style={{ fontSize: '0.92rem', color: '#3c4653', lineHeight: 1.4, marginTop: 2 }}>Questions written just for this occasion.</div>
                    </div>
                    <div style={{ flexShrink: 0, fontSize: '0.8rem', color: done > 0 ? '#27ae60' : '#9ca3af', fontWeight: 600 }}>{done}/{total}</div>
                  </button>
                );
              })()}
              {PROMPT_PACKS.filter(pack => pack.id !== 'hot-seat').map(pack => {
                const total = packModePrompts(pack).length;
                const done = packAnsweredCount(pack);
                // Returning users stored 'hot-seat' as their active pack before the
                // separation. Treat that as no selection so the grid has no orphan highlight.
                // When the occasion pack is selected, no standard pack should appear highlighted.
                const effectivePackId = (activePackId === 'hot-seat' || effectiveDefaultPackId === 'occasion') ? null : activePackId;
                const selected = pack.id === effectivePackId;
                const isBold = pack.register === 'bold';
                return (
                  <button key={pack.id}
                    onClick={() => { setActivePackId(pack.id); setStShuffleIdx(0); setStAiQuestion(null); setStPackChosen(true); setStShowPackPicker(false); try { localStorage.setItem('tb_packChosen', '1'); } catch (e) {} }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
                      background: selected ? 'rgba(201,168,76,0.10)' : '#fffdf8',
                      border: `2px solid ${selected ? 'rgba(201,168,76,0.6)' : 'rgba(15,23,42,0.12)'}`,
                      borderRadius: 16, padding: '15px 15px', cursor: 'pointer' }}>
                    <div style={{ fontSize: '2rem', flexShrink: 0, width: 44, textAlign: 'center' }}>{pack.emoji}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: "'Lora', serif", fontSize: '1.12rem', fontWeight: 700, color: '#0f172a' }}>{pack.name}</span>
                        <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: isBold ? '#9a3412' : '#7a5a10', background: isBold ? 'rgba(154,52,18,0.10)' : 'rgba(201,168,76,0.16)', padding: '2px 8px', borderRadius: 20 }}>{isBold ? 'Bold' : 'Warm'}</span>
                      </div>
                      <div style={{ fontSize: '0.92rem', color: '#3c4653', lineHeight: 1.4, marginTop: 2 }}>{pack.blurb}</div>
                    </div>
                    <div style={{ flexShrink: 0, fontSize: '0.8rem', color: done > 0 ? '#27ae60' : '#9ca3af', fontWeight: 600 }}>{done}/{total}</div>
                  </button>
                );
              })}
            </div>

            {/* Hot Seat — separate entry below a divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 14px' }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(15,23,42,0.12)' }} />
              <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: '0.72rem', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: '#9ca3af', whiteSpace: 'nowrap' }}>Or, for something quick</span>
              <div style={{ flex: 1, height: 1, background: 'rgba(15,23,42,0.12)' }} />
            </div>
            <button
              onClick={() => {
                // Enter rapid-fire directly. Mirrors the existing Hot Seat CTA mechanism
                // in the prompt-picker (setActivePackId + rapid-fire state reset).
                // The rapid-fire step builds its own prompt list from PROMPT_PACKS
                // independently, so no stSelectedPrompt seed is needed here.
                setActivePackId('hot-seat');
                setStShuffleIdx(0);
                setStAiQuestion(null);
                setStPackChosen(true);
                setStShowPackPicker(false);
                try { localStorage.setItem('tb_packChosen', '1'); } catch (e) {}
                setRapidAnswers({});
                setRapidCurrentIdx(0);
                logInterviewStart(); // Hot Seat rapid-fire is a text engagement
                setStInterviewMode(true);
                setStInterviewStep('rapid-fire');
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
                background: 'rgba(192,57,43,0.06)',
                border: '2px solid rgba(192,57,43,0.32)',
                borderRadius: 16, padding: '15px 15px', cursor: 'pointer' }}>
              <div style={{ fontSize: '2rem', flexShrink: 0, width: 44, textAlign: 'center' }}>🌶️</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Lora', serif", fontSize: '1.12rem', fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>Hot Seat</div>
                <div style={{ fontSize: '0.92rem', color: '#7c2d12', lineHeight: 1.4 }}>Quick-fire, cheeky questions. No interview, just snappy takes.</div>
              </div>
              <div style={{ flexShrink: 0, fontSize: '1.1rem', color: '#9a3412', fontWeight: 700 }}>→</div>
            </button>

            {/* Escape hatch: skip the questions and free-write a story */}
            <button onClick={() => setActiveTab('st-write')}
              style={{ display: 'block', width: '100%', marginTop: 14, background: 'transparent', border: '1.5px dashed rgba(15,23,42,0.28)', borderRadius: 16, padding: '16px 14px', cursor: 'pointer', textAlign: 'center', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653', lineHeight: 1.5 }}>
              <b style={{ color: '#0f172a' }}>Rather just write?</b><br />Skip the questions and tell your own story &rarr;
            </button>
          </div>
        </div>
      );
    }

    // Prompt picker redesign: 3-card scannable layout.
    // Three prompts shown at once; stepping by 3 reuses stShuffleIdx.
    // stAiQuestion, if set, surfaces as a fourth card above the pack prompts.
    // Mode selection (talk vs type) is removed from this screen. If the user
    // arrived via StTalkHome "Talk to Pip" (stStartMode === 'voice'), tapping
    // any card still routes directly to VoiceInterview (behavior preserved).
    // Otherwise tapping enters the text interview. Inside the conversation the
    // mic button is the single "talk to Pip" affordance: it routes to
    // voice-to-voice on supported devices before the first answer, and falls
    // back to dictation (Whisper) when voice-to-voice is unavailable or after
    // the first answer has been submitted.
    const pickBaseIdx = isShowingAiQuestion ? stShuffleIdx : stShuffleIdx - (stShuffleIdx % 3);
    const visiblePrompts = isShowingAiQuestion
      ? [stAiQuestion]
      : activePrompts.slice(pickBaseIdx, pickBaseIdx + 3);

    // Voice-to-voice capability check (same logic as the conversation step, hoisted here
    // so the prompt-picker can use it to decide the default entry path).
    // Hot Seat is always text/rapid-fire. Otherwise: prefer voice-to-voice when the device
    // has getUserMedia + RTCPeerConnection. Falls back to the text interview silently.
    const isVoiceToVoiceCapable = activePackId !== 'hot-seat'
      && typeof navigator !== 'undefined'
      && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      && !!(window.RTCPeerConnection || window.webkitRTCPeerConnection || window.mozRTCPeerConnection);

    const enterConversation = (prompt) => {
      // Activation funnel: the interview is starting (voice or text).
      trackEvent('interview_opened', { mode: isVoiceToVoiceCapable ? 'voice' : 'text' });
      setStAiQuestion(null);
      if (isVoiceToVoiceCapable) {
        // Default into voice-to-voice on capable devices (not Hot Seat).
        // "I'd rather type" inside VoiceInterview drops the user back into
        // the text conversation with the same prompt via onSwitchToText.
        setStSelectedPrompt(prompt);
        setStVoiceInterviewMode(true);
      } else {
        // Device does not support WebRTC or getUserMedia: go straight to text.
        // Engagement is logged inside startStInterview (the chokepoint), not here.
        startStInterview(prompt);
      }
    };

    return (
      <div className="storyteller-content" style={{ textAlign: 'center' }}>
        {/* Persistent video element — always in DOM when video mode is on */}
        {storyVideoMode && (
          <video ref={storyLiveVideoRef} muted playsInline
            style={{ width: '100%', maxWidth: 400, borderRadius: 16, background: '#1a1612', display: storyRecording ? 'block' : 'none', border: '3px solid rgba(220,80,60,0.4)', margin: '0 auto 16px' }} />
        )}

        {storyRecording ? (
          <>
            {/* Cancel — stop and discard, return to StWritePage (the typical
                entry point now that "Just record" is removed). */}
            <button onClick={() => {
              stopStoryRecording();
              setTimeout(() => { setStoryAudioBlob(null); setStoryAudioUrl(''); }, 100);
              setActiveTab('st-write');
            }} className="st-back" style={{ textAlign: 'left', width: '100%' }}>← Cancel</button>

            {!storyVideoMode && (
              <div className="st-record-btn st-record-active" style={{ margin: '0 auto 32px' }} aria-label="Recording in progress">
                <Microphone weight="duotone" size={72} color="#c0392b" />
              </div>
            )}
            <h2 className="st-h2" style={{ fontSize: '1.8rem', color: 'rgba(220,80,60,0.9)' }}>Recording{storyVideoMode ? ' video' : ''}...</h2>
            <p className="st-sub" style={{ marginTop: 8, marginBottom: 40 }}>Take your time. Tap stop when you're done.</p>
            {stSelectedPrompt && (
              <div className="st-warm-box" style={{ marginBottom: 40, textAlign: 'left' }}>
                <p style={{ fontSize: '1.05rem', color: '#0f172a', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 600 }}>Your prompt</p>
                <p style={{ fontSize: '1.1rem', color: '#0f172a', fontStyle: 'italic' }}>"{stSelectedPrompt.text}"</p>
              </div>
            )}
            <button onClick={stopStoryRecording} className="st-btn-primary" style={{ background: 'rgba(220,80,60,0.2)', borderColor: 'rgba(220,80,60,0.4)', color: '#fc8181' }}>
              ⏹ Stop Recording
            </button>
          </>
        ) : (
          <>
            {/* ── PROMPT PICKER REDESIGN ── */}
            {/* Back to topics */}
            <button onClick={() => { if (!backFromPathQuestions()) setStShowPackPicker(true); }} className="st-back" style={{ textAlign: 'left', width: '100%' }}>← Back to topics</button>

            {/* Context bar: "Adding a story to: {book title}" */}
            {activeBook && (
              <div style={{ padding: '8px 14px', background: '#f0ebe1', border: '1px solid rgba(15,23,42,0.06)', borderRadius: 10, marginBottom: 14, fontSize: '0.88rem', color: '#6b6152', textAlign: 'center' }}>
                Adding a story to <strong style={{ color: '#0f172a' }}>{activeBook.title || 'your book'}</strong>
              </div>
            )}

            {/* Topic chip: "Topic: {packName} ▾" — opens pack switcher */}
            {activePack && (
              <div style={{ textAlign: 'center', marginBottom: 14 }}>
                <button onClick={() => setStShowPackPicker(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: 999, padding: '5px 14px', cursor: 'pointer', fontFamily: "'Nunito', sans-serif", fontSize: '0.8rem', fontWeight: 700, color: '#7a5a10' }}>
                  Topic: {activePack.emoji} {activePack.name} <span style={{ fontSize: '0.7rem' }}>▾</span>
                </button>
              </div>
            )}

            {/* Pip + heading */}
            <div style={{ textAlign: 'center', margin: '2px 0 16px' }}>
              <img src={pipHoldingBook} alt="Pip" style={{ width: 150, height: 150, objectFit: 'contain', filter: 'drop-shadow(0 4px 8px rgba(120,70,20,.18))' }} />
              <h2 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 'clamp(1.2rem, 5vw, 1.45rem)', color: '#0f172a', margin: '8px 0 2px' }}>
                {isGiftBook ? `What would you like to share about ${activeBook?.recipientName || 'them'}?` : 'What should we talk about?'}
              </h2>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '0.95rem', color: '#6b6152', margin: 0 }}>Pick one that sparks a memory.</p>
            </div>

            <div style={{ maxWidth: 560, margin: '0 auto' }}>
              {/* TB-104 Stage 2b: Hot Seat rapid-fire primary CTA.
                  When the active pack is Hot Seat, the main entry point is the rapid-fire
                  batch screen. The single-question path below remains reachable as a secondary
                  option so users can still answer one question at a time. */}
              {activePackId === 'hot-seat' && (
                <div style={{ marginBottom: 24, textAlign: 'center' }}>
                  <div style={{ display: 'inline-block', background: 'rgba(154,52,18,0.07)', border: '1.5px solid rgba(154,52,18,0.18)', borderRadius: 16, padding: '24px 28px', width: '100%' }}>
                    <div style={{ fontSize: 40, marginBottom: 8 }}>⚡</div>
                    <h3 style={{ fontFamily: "'Lora', serif", fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                      Hot Seat: Rapid Fire
                    </h3>
                    <p style={{ fontSize: '0.95rem', color: '#3c4653', lineHeight: 1.55, marginBottom: 18, fontFamily: "'Lora', serif" }}>
                      {isGiftBook
                        ? `Answer ${activePrompts.length} quick questions about ${recipientName || 'them'}, one at a time. No deep dives, just fast, fun answers.`
                        : `Answer ${activePrompts.length} quick questions about yourself, one at a time. No deep dives, just fast, fun answers.`}
                    </p>
                    <button
                      onClick={() => {
                        // TB-104 Stage 2b: reset rapid-fire state and go to rapid-fire step
                        setRapidAnswers({});
                        setRapidCurrentIdx(0);
                        logInterviewStart(); // Hot Seat rapid-fire is a text engagement
                        setStInterviewMode(true);
                        setStInterviewStep('rapid-fire');
                        setStSelectedPrompt(activePrompts[0] || currentPrompt);
                      }}
                      style={{ display: 'inline-block', background: '#0f172a', color: '#fff', fontFamily: "'Nunito', sans-serif", fontSize: '1.05rem', fontWeight: 800, padding: '14px 36px', borderRadius: 30, border: 'none', cursor: 'pointer' }}
                    >
                      Start Rapid Fire →
                    </button>
                  </div>
                  <p style={{ fontSize: '0.85rem', color: '#7a5a10', marginTop: 12, fontFamily: "'Lora', serif" }}>
                    Or pick one question at a time below.
                  </p>
                </div>
              )}

              {/* THREE prompt cards — scannable, tap to enter conversation.
                  When stAiQuestion is set, only that card is shown. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 4 }}>
                {visiblePrompts.map((prompt, idx) => {
                  const isAnswered = !isShowingAiQuestion && answeredPromptIds.includes(prompt.id);
                  return (
                    <button
                      key={isShowingAiQuestion ? 'ai' : prompt.id}
                      onClick={() => enterConversation(prompt)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 14,
                        width: '100%', textAlign: 'left', cursor: 'pointer',
                        background: isAnswered ? 'rgba(39,174,96,0.04)' : '#fff',
                        border: isAnswered ? '1px solid rgba(39,174,96,0.22)' : '1px solid rgba(15,23,42,0.10)',
                        borderRadius: 14, padding: '15px 16px',
                        transition: 'border-color 0.15s',
                        WebkitTapHighlightColor: 'transparent',
                        position: 'relative',
                      }}
                    >
                      {isAnswered && (
                        <span style={{ position: 'absolute', top: 8, right: 12, fontSize: '0.75rem', color: '#27ae60', fontFamily: "'Nunito', sans-serif", fontWeight: 700 }}>
                          Answered
                        </span>
                      )}
                      <div style={{ flex: 1, fontFamily: "'Lora', serif", fontSize: '0.95rem', color: '#2a2520', lineHeight: 1.5, fontStyle: 'normal', fontWeight: isShowingAiQuestion ? 500 : 400 }}>
                        {prompt.text}
                      </div>
                      <div style={{ color: '#D2761F', fontWeight: 800, fontSize: '1.1rem', flexShrink: 0 }}>→</div>
                    </button>
                  );
                })}
              </div>

              {/* Reshuffle and Surprise me — quiet text links */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, marginTop: 6 }}>
                {!isShowingAiQuestion && (
                  <button
                    onClick={() => { setStAiQuestion(null); setStShuffleIdx(prev => (prev + 3) % Math.max(activePrompts.length, 1)); }}
                    style={{ background: 'transparent', border: 'none', fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', fontWeight: 700, color: '#7a6b54', padding: '12px 16px', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
                    ↻ Show me three different questions
                  </button>
                )}
                <button
                  onClick={generateAiQuestion}
                  disabled={stAiQuestionLoading}
                  style={{ background: 'transparent', border: 'none', fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', fontWeight: 700, color: stAiQuestionLoading ? '#9ca3af' : '#D2761F', padding: '6px 16px', cursor: stAiQuestionLoading ? 'default' : 'pointer', WebkitTapHighlightColor: 'transparent', opacity: stAiQuestionLoading ? 0.7 : 1 }}>
                  {stAiQuestionLoading ? 'Thinking...' : '✨ Surprise me — Pip writes a brand-new question'}
                </button>
                {isShowingAiQuestion && (
                  <button
                    onClick={() => setStAiQuestion(null)}
                    style={{ background: 'transparent', border: 'none', fontFamily: "'Nunito', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: '#9ca3af', padding: '4px 16px', cursor: 'pointer' }}>
                    ← Back to the list
                  </button>
                )}
              </div>

              {/* Quiet secondary controls */}
              <div style={{ borderTop: '1px solid rgba(15,23,42,0.07)', paddingTop: 20, marginTop: 16 }}>
                {/* Kid mode — Mother's Day gift books. HIDDEN behind KID_MODE_ENABLED (legal + product review). */}
                {KID_MODE_ENABLED && isGiftBook && activeBook?.occasion === 'MOTHERS_DAY' && (
                  <div style={{ marginBottom: 16 }}>
                    <button onClick={() => setKidMode(true)}
                      style={{ padding: '12px 24px', fontSize: '0.95rem', fontFamily: "'Nunito', sans-serif", fontWeight: 700, background: 'rgba(201,168,76,0.1)', border: '1.5px solid rgba(201,168,76,0.28)', borderRadius: 30, cursor: 'pointer', color: '#7a5a10' }}>
                      🧒 Add a Kid's Story
                    </button>
                    <p style={{ fontSize: '0.82rem', color: '#3c4653', marginTop: 6 }}>Let a child answer fun questions about Mom in their own words.</p>
                  </div>
                )}

                {/* Skip the prompts — write your own. */}
                <button onClick={() => {
                  // Clear stale write state so a fresh entry never surfaces
                  // "leave without saving?" from a previous session.
                  // localStorage draft is preserved — the draft-restore
                  // banner inside StWritePage will offer to bring it back.
                  setStWriteTitle('');
                  setStWriteBody('');
                  setStWritePhotoFile(null);
                  setStWritePhotoPreview(null);
                  setActiveTab('st-write');
                }} style={{ background: 'transparent', border: 'none', fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', fontWeight: 700, color: '#7a6b54', padding: '8px 16px', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                  Rather write your own story?
                </button>

                {/* "Capturing someone else's story?" crosslink section removed 2026-05-15.
                    Interview a Loved One is now reached from the book detail view's
                    sibling button to "Add a Story," not from inside the writing flow. */}
              </div>
            </div>
          </>
        )}
      </div>
    );

};

export default StRecordPage;
